//sw version 3.9.5.3

import { translations } from "./data/translations.js";
import { CHANGELOG } from "./data/changelog.js";
import {
  META_DATA,
  COMMUNITY_LINKS,
  APP_VERSION,
  WISH_COST,
  PLACEHOLDER_ICON,
  SUPPORTED_GAME_VERSION,
  ELEMENT_ICONS,
  SLIME_ICONS,
  ELEMENT_COLORS,
  THEME_COLORS,
  WEAPON_TYPE_ICONS,
  CURRENCY_ICONS,
  PROFILE_BACKGROUNDS,
  REGION_ICONS,
  ROLE_ICONS,
} from "./data/constants.js";
import { BANNER_ROTATIONS } from "./data/bannerRotations.js";
import { ACHIEVEMENTS } from "./data/achievements.js";

// Diese Version MUSS mit der HTML_VERSION in index.html übereinstimmen.
const SCRIPT_VERSION = "3.9.5";

// Dieser Check läuft, bevor irgendetwas anderes passiert.
if (window.HTML_VERSION && window.HTML_VERSION !== SCRIPT_VERSION) {
  console.warn(
    `Version mismatch detected! HTML is ${window.HTML_VERSION}, Script is ${SCRIPT_VERSION}. Forcing hard reload...`
  );

  // Versuche, den Service Worker zu deregistrieren, um den Cache sicher zu leeren.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      })
      .then(() => {
        // Führe einen Hard-Refresh durch (true = Cache umgehen).
        window.location.reload(true);
      });
  } else {
    // Fallback für Browser ohne Service Worker.
    window.location.reload(true);
  }

  // Verhindere, dass der restliche (veraltete) Code ausgeführt wird und einen Fehler verursacht.
  throw new Error("PWA version mismatch. App is reloading to fix itself.");
}

const CLIENT_ID =
  "545631027710-4sc01oik1kulqeplh6kv8aspfm1hro68.apps.googleusercontent.com";
const SCOPES = "https://www.googleapis.com/auth/drive.file";
const DISCOVERY_DOCS = [
  "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
];
const DATA_FILE_NAME = "genshin-pity-tracker-data.json";
let tokenClient;
let gapiInited = false;
let gisInited = false;
let driveFileId = null;

// Diese Funktionen werden aufgerufen, sobald die Google-Bibliotheken geladen sind.
function gapiLoaded() {
  gapi.load("client", () => {
    gapi.client.init({ discoveryDocs: DISCOVERY_DOCS }).then(() => {
      gapiInited = true;
      // Zeigt die UI basierend auf dem gespeicherten Zustand an, OHNE eine Anfrage zu senden.
      if (state.isDriveConnected) {
        updateDriveUi(true);
      }
    });
  });
}

function gisLoaded() {
  tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: CLIENT_ID,
    scope: SCOPES,
    callback: authCallback, // Dieser Callback ist NUR für den manuellen Login-Flow
    // Diese Zeile teilt Google explizit mit, dass wir einen Popup-Flow verwenden.
    // Das hilft dem Browser, die Cross-Origin-Kommunikation korrekt zu handhaben
    // und kann die COOP-Warnungen in der Konsole reduzieren.
    ux_mode: "popup",
  });
  gisInited = true;
}

// Callback NUR für den manuellen Login (wenn der User auf "Connect" klickt)
async function authCallback(resp) {
  if (resp.error) {
    console.warn("Google Auth Error:", resp.error);
    showToast("Google login failed. Please try again.", "error");
    return;
  }

  // Erfolg nach manuellem Klick
  gapi.client.setToken(resp);
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta")) || {};
  accountsMeta.isDriveConnected = true;

  try {
    const aboutResp = await gapi.client.drive.about.get({ fields: "user(emailAddress)" });
    if (aboutResp.result && aboutResp.result.user && aboutResp.result.user.emailAddress) {
      accountsMeta.driveEmail = aboutResp.result.user.emailAddress;
    }
  } catch (err) {
    console.warn("Could not fetch email for login_hint:", err);
  }

  localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));
  state.isDriveConnected = true; // Aktualisiere auch den laufenden State für die UI

  updateDriveUi(true);
  await findOrCreateDataFile();
}

// Funktion für den manuellen Login-Button
function handleAuthClick() {
  // Zeigt das Popup nur bei expliziter Nutzeraktion an.
  if (gisInited) {
    tokenClient.requestAccessToken({ prompt: "" });
  } else {
    showToast("Google API not ready yet. Please wait a moment.", "error");
  }
}

function ensureValidToken() {
  return new Promise((resolve, reject) => {
    const token = gapi.client.getToken();
    if (token && token.expires_in > 60) {
      resolve();
      return;
    }

    tokenClient.callback = (resp) => {
      // WICHTIG: Setze den Callback für den manuellen Login sofort zurück!
      tokenClient.callback = authCallback;

      if (resp.error) {
        console.error("Silent token refresh failed:", resp.error);
        // Wenn der stille Refresh fehlschlägt, ist der User ausgeloggt.
        state.isDriveConnected = false;
        saveState();
        updateDriveUi(false);
        showToast("Session expired. Please connect again.", "error");
        reject(new Error("Authentication required."));
        return;
      }
      gapi.client.setToken(resp);
      resolve();
    };

    const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta")) || {};
    const requestOptions = { prompt: "none" };
    if (accountsMeta.driveEmail) {
      requestOptions.login_hint = accountsMeta.driveEmail;
    }
    tokenClient.requestAccessToken(requestOptions);
  });
}

// UI-Update-Funktion
function updateDriveUi(isSignedIn) {
  document
    .getElementById("drive-auth-view")
    .classList.toggle("hidden", isSignedIn);
  document
    .getElementById("drive-sync-view")
    .classList.toggle("hidden", !isSignedIn);
}

// Datei-Such-Funktion
async function findOrCreateDataFile() {
  try {
    await ensureValidToken();

    const response = await gapi.client.drive.files.list({
      q: `name='${DATA_FILE_NAME}' and trashed=false`,
      spaces: "drive",
      fields: "files(id, name)",
    });

    if (response.result.files.length > 0) {
      driveFileId = response.result.files[0].id;
    } else {
      driveFileId = null;
    }
  } catch (err) {
    console.error("Error finding data file:", err);
  }
}

async function saveToDrive() {
  showToast("Verifying Google session...", "info", null);
  try {
    await ensureValidToken();
  } catch (error) {
    hideToast();
    return;
  }

  updateToast("Preparing data for Google Drive...", "info");

  // 1. Sammle alle Account-Daten
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  const accountsData = {};

  if (accountsMeta && accountsMeta.accounts) {
    for (const account of accountsMeta.accounts) {
      const accountState = localStorage.getItem(`account_${account.id}`);
      if (accountState) {
        accountsData[account.id] = JSON.parse(accountState);
      }
    }
  }

  // 2. Erstelle das finale Speicherobjekt
  const dataToSave = {
    version: "multi-account-v1",
    accountsMeta: {
      // Speichere eine "saubere" Version der Metadaten
      activeAccountId: accountsMeta.activeAccountId,
      accounts: accountsMeta.accounts,
    },
    accountsData: accountsData,
  };

  const dataString = JSON.stringify(dataToSave);

  updateToast("Saving to Google Drive...", "info");

  try {
    if (driveFileId === null) {
      await findOrCreateDataFile();
    }

    if (driveFileId) {
      const boundary = "-------314159265358979323846";
      const delimiter = "\r\n--" + boundary + "\r\n";
      const close_delim = "\r\n--" + boundary + "--";
      const metadata = { mimeType: "application/json" };
      const multipartRequestBody =
        delimiter +
        "Content-Type: application/json\r\n\r\n" +
        JSON.stringify(metadata) +
        delimiter +
        "Content-Type: application/json\r\n\r\n" +
        dataString +
        close_delim;

      await gapi.client.request({
        path: `/upload/drive/v3/files/${driveFileId}`,
        method: "PATCH",
        params: { uploadType: "multipart" },
        headers: {
          "Content-Type": 'multipart/related; boundary="' + boundary + '"',
        },
        body: multipartRequestBody,
      });
    } else {
      // Datei existiert nicht -> Neu erstellen
      const fileMetadata = {
        name: DATA_FILE_NAME,
        mimeType: "application/json",
      };
      const form = new FormData();
      form.append(
        "metadata",
        new Blob([JSON.stringify(fileMetadata)], { type: "application/json" })
      );
      form.append("file", new Blob([dataString], { type: "application/json" }));
      const res = await fetch(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
        {
          method: "POST",
          headers: new Headers({
            Authorization: "Bearer " + gapi.client.getToken().access_token,
          }),
          body: form,
        }
      );
      const result = await res.json();
      driveFileId = result.id;
    }

    updateToast("All accounts saved successfully!", "success");
    setTimeout(hideToast, 2000);
  } catch (err) {
    console.error("Error saving to Drive:", err);
    updateToast("Failed to save data. Please try again.", "error");
    setTimeout(hideToast, 3000);
  }
}

// Laden-Funktion
async function loadFromDrive() {
  showToast("Verifying Google session...", "info", null);
  try {
    await ensureValidToken();
  } catch (error) {
    hideToast();
    return;
  }

  updateToast("Loading from Google Drive...", "info");

  if (driveFileId === null) {
    await findOrCreateDataFile();
    if (driveFileId === null) {
      updateToast("No data file found in your Drive. Save first!", "warning");
      setTimeout(hideToast, 3000);
      return;
    }
  }

  try {
    const response = await gapi.client.drive.files.get({
      fileId: driveFileId,
      alt: "media",
    });

    const importedData = response.result;

    // 1. Validierung der geladenen Daten
    if (
      !importedData ||
      importedData.version !== "multi-account-v1" ||
      !importedData.accountsMeta ||
      !importedData.accountsData
    ) {
      console.warn(
        "Could not find multi-account data, attempting to load legacy single-account format..."
      );
      if (importedData && (importedData.character1 || importedData.character)) {
        // Dies ist der alte Code zum Laden eines einzelnen Accounts
        const prevMeta = JSON.parse(localStorage.getItem("accounts_meta")) || {};
        const newId = `acc_${Date.now()}`;
        const newMeta = {
          activeAccountId: newId,
          accounts: [{ id: newId, name: "Imported Account" }],
          isDriveConnected: true,
          driveEmail: prevMeta.driveEmail,
        };
        localStorage.setItem("accounts_meta", JSON.stringify(newMeta));
        localStorage.setItem(`account_${newId}`, JSON.stringify(importedData));

        showToast("Legacy data imported. Reloading...", "success");
        setTimeout(() => window.location.reload(), 1500);
        return;
      } else {
        throw new Error("Invalid or corrupted data format in Drive file.");
      }
    }

    // 2. Schreibe die neuen Metadaten in den localStorage
    const prevMeta = JSON.parse(localStorage.getItem("accounts_meta")) || {};
    const newMeta = importedData.accountsMeta;
    newMeta.isDriveConnected = true;
    if (prevMeta.driveEmail) newMeta.driveEmail = prevMeta.driveEmail;
    localStorage.setItem("accounts_meta", JSON.stringify(newMeta));

    // 3. Schreibe die Daten für JEDEN Account in den localStorage
    for (const accountId in importedData.accountsData) {
      localStorage.setItem(
        `account_${accountId}`,
        JSON.stringify(importedData.accountsData[accountId])
      );
    }

    updateToast("All accounts loaded successfully! Reloading...", "success");
    // 4. Lade die Seite neu, damit `loadState` den neuen aktiven Account lädt
    setTimeout(() => window.location.reload(), 1500);
  } catch (err) {
    console.error("Error loading from Drive:", err);
    updateToast(`Failed to load data. ${err.message}`, "error");
    setTimeout(hideToast, 3000);
  }
}

// Ausloggen-Funktion
function handleSignoutClick() {
  const token = gapi.client.getToken();

  // Schritt 1: Widerrufe den Token bei Google, WENN einer vorhanden ist.
  if (token !== null) {
    google.accounts.oauth2.revoke(token.access_token);
  }

  // Schritt 2: Bereinige den Zustand der App SOFORT und IMMER.
  gapi.client.setToken(""); // Leert den Token im Speicher der GAPI-Bibliothek
  driveFileId = null;

  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta")) || {};
  accountsMeta.isDriveConnected = false;
  delete accountsMeta.driveEmail;
  localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));
  state.isDriveConnected = false; // Aktualisiere auch den laufenden State für die UI

  updateDriveUi(false);
  showToast("Disconnected from Google Drive.", "success");
}

// NEUE Funktion zum dynamischen Laden der Skripte
function loadGoogleScripts() {
  // Skript 1: Google Identity Services (GIS)
  const gisScript = document.createElement("script");
  gisScript.src = "https://accounts.google.com/gsi/client";
  gisScript.onload = () => {
    // Der onload-Callback wird hier direkt und sicher aufgerufen
    gisLoaded();
  };
  gisScript.onerror = () => {
    console.error("Failed to load Google Identity Services script.");
    showToast(
      "Could not connect to Google services. Please check your connection.",
      "error"
    );
  };
  document.body.appendChild(gisScript);

  // Skript 2: Google API Client (GAPI)
  const gapiScript = document.createElement("script");
  gapiScript.src = "https://apis.google.com/js/api.js";
  gapiScript.onload = () => {
    // Der onload-Callback wird hier direkt und sicher aufgerufen
    gapiLoaded();
  };
  gapiScript.onerror = () => {
    console.error("Failed to load Google API Client script.");
    showToast(
      "Could not connect to Google services. Please check your connection.",
      "error"
    );
  };
  document.body.appendChild(gapiScript);
}

function updateThemeColorMetaTag(color) {
  const metaTag = document.getElementById("theme-color-meta");
  if (metaTag) {
    metaTag.setAttribute("content", color);
  }
}

async function loadFonts() {
  try {
    // document.fonts.ready wartet, bis alle im CSS deklarierten
    // Schriftarten geladen und einsatzbereit sind.
    await document.fonts.ready;

    console.log("Custom fonts loaded and applied.");
  } catch (error) {
    console.error("Error loading custom fonts:", error);
  } finally {
    document.body.classList.remove("fonts-loading");
  }
}

function getWikiPage(itemType, itemName) {
  if (!itemName) return "";
  if (
    itemType === "character" ||
    itemType === "weapon" ||
    itemType === "material"
  ) {
    return itemName.replace(/ /g, "_");
  }
  return "";
}

function getFandomFileName(itemType, itemName, imageVariant = "icon") {
  if (!itemName) return "";

  if (itemType === "character") {
    const formattedName = itemName.replace(/ /g, "_");
    switch (imageVariant) {
      case "icon":
        return `${formattedName}_Icon.png`;
      case "wish":
        return `${formattedName}_Wish.png`;
      case "item":
        return `${formattedName}_Item.png`;
      case "card":
        return `${itemName} Card.png`;
      case "multi_wish":
        return `${formattedName}_Multi_Wish.png`;
      default:
        return `${formattedName}_Icon.png`;
    }
  } else if (itemType === "weapon") {
    // KORREKTUR: Zuerst die Anführungszeichen entfernen, DANN den Rest formatieren.
    // Dies stellt sicher, dass der Dateiname immer sauber ist.
    const cleanNameForFile = itemName.replace(/"/g, ""); // "\"The Catch\"" -> "The Catch"
    return `Weapon ${cleanNameForFile}.png`; // Ergebnis: "Weapon The Catch.png"
  } else if (itemType === "material") {
    const formattedName = itemName.replace(/ /g, "_");
    return `Item_${formattedName}.png`;
  }
  return "";
}

function getFandomImageUrl(itemType, itemName, imageVariant = "icon") {
  // Überprüft, ob die App auf localhost oder 127.0.0.1 läuft.
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  if (isLocal) {
    // Gib für lokale Tests IMMER den Platzhalter zurück.
    return PLACEHOLDER_ICON;
  }
  if (!itemName) return PLACEHOLDER_ICON;

  // NEU: Spezialbehandlung für Artefakt-Sets
  if (itemType === "artifact_set") {
    const itemDetails = findItem(itemName);
    // Wir prüfen, ob der Eintrag in der itemsDB das neue 'icon'-Feld hat.
    if (itemDetails && itemDetails.icon) {
      // Der 'wikiPage'-Teil der URL ist der Name des Icon-Teils.
      const wikiPage = itemDetails.icon.replace(/ /g, "_");
      // Der Dateiname folgt dem Standard-Item-Format.
      const fileName = `Item ${itemDetails.icon}.png`;

      const encodedWikiPage = encodeURIComponent(wikiPage).replace(/'/g, "%27");
      const encodedFileName = encodeURIComponent(fileName).replace(/'/g, "%27");
      return `/image-proxy/${encodedWikiPage}?file=${encodedFileName}`;
    }
    // Fallback, falls das Icon-Feld fehlt
    return PLACEHOLDER_ICON;
  }

  // Fallback auf die alte Logik für alles andere (Charaktere, Waffen, Materialien)
  const wikiPage = getWikiPage(itemType, itemName);
  const fileName = getFandomFileName(itemType, itemName, imageVariant);

  if (!wikiPage || !fileName) return PLACEHOLDER_ICON;

  const encodedWikiPage = encodeURIComponent(wikiPage).replace(/'/g, "%27");
  const encodedFileName = encodeURIComponent(fileName).replace(/'/g, "%27");

  return `/image-proxy/${encodedWikiPage}?file=${encodedFileName}`;
}

function getRegionIcon(regionName) {
  if (!regionName || !REGION_ICONS[regionName]) return PLACEHOLDER_ICON;
  const { wikiPage, fileName } = REGION_ICONS[regionName];
  const encodedWikiPage = encodeURIComponent(wikiPage).replace(/'/g, "%27");
  const encodedFileName = encodeURIComponent(fileName).replace(/'/g, "%27");
  return `/image-proxy/${encodedWikiPage}?file=${encodedFileName}`;
}

function getRoleIcons(roles) {
  if (!roles || !Array.isArray(roles)) return [];
  return roles.map(role => {
    if (!ROLE_ICONS[role]) return null;
    const { wikiPage, fileName } = ROLE_ICONS[role];
    const encodedWikiPage = encodeURIComponent(wikiPage).replace(/'/g, "%27");
    const encodedFileName = encodeURIComponent(fileName).replace(/'/g, "%27");
    return {
      role: role,
      url: `/image-proxy/${encodedWikiPage}?file=${encodedFileName}`
    };
  }).filter(item => item !== null);
}

function checkAchievements(manualTriggerId = null) {
  const lang = state.language;

  // Manuell ausgelöste Erfolge
  if (
    manualTriggerId &&
    !state.achievements.unlocked.includes(manualTriggerId)
  ) {
    state.achievements.unlocked.push(manualTriggerId);
    showAchievementToast(manualTriggerId);
  }
  const helpers = { calculateComprehensiveStats };
  // Automatisch geprüfte Erfolge
  for (const id in ACHIEVEMENTS) {
    if (ACHIEVEMENTS[id].check && !state.achievements.unlocked.includes(id)) {
      if (ACHIEVEMENTS[id].check(state, helpers)) {
        state.achievements.unlocked.push(id);
        showAchievementToast(id);
      }
    }
  }
}

function showAchievementToast(id) {
  // Verhindere, dass der gleiche Toast mehrmals erscheint
  if (state.achievements.seen.includes(id)) return;
  state.achievements.seen.push(id);

  const achievement = ACHIEVEMENTS[id];
  const title =
    state.language === "de" ? achievement.title_de : achievement.title;
  const toastMessage = `🏆 Achievement Unlocked: ${title}`;
  showToast(toastMessage, "success", 4000);
}

function calculateProfileStats() {
  const allHistory = [
    ...(state.character1?.history || []),
    ...(state.character2?.history || []),
    ...(state.weapon?.history || []),
    ...(state.standard?.history || []),
    ...(state.chronicled?.history || []),
  ];

  const allTotalPulls =
    (state.character1?.totalPulls || 0) +
    (state.character2?.totalPulls || 0) +
    ((state.weapon?.pity || 0) +
      calculateComprehensiveStats(state.weapon?.history || [])
        .totalPullsInHistory) +
    ((state.standard?.pity || 0) +
      calculateComprehensiveStats(state.standard?.history || [])
        .totalPullsInHistory) +
    ((state.chronicled?.pity || 0) +
      calculateComprehensiveStats(state.chronicled?.history || [])
        .totalPullsInHistory);

  const fiveStarPulls = allHistory.filter((p) => p.rarity === 5);
  const fourStarPulls = allHistory.filter((p) => p.rarity === 4);

  if (
    fiveStarPulls.length === 0 &&
    fourStarPulls.length === 0 &&
    allTotalPulls < 50
  ) {
    return null;
  }

  const stats = {
    totalPulls: allTotalPulls,
    avgPity5: null,
    avgPity4: null,
    winRate5050: null,
    winRatePath: null,
    elementalAffinity: { element: "None", icon: "" },
    weaponAffinity: { type: "None", icon: "" },
    luckScore: "C",
    background: PROFILE_BACKGROUNDS.default, // Standardhintergrund
  };

  if (fiveStarPulls.length > 0) {
    const totalPity5 = fiveStarPulls.reduce((sum, p) => sum + p.pity, 0);
    stats.avgPity5 = (totalPity5 / fiveStarPulls.length).toFixed(1);
  }

  if (fourStarPulls.length > 0) {
    const totalPity4 = fourStarPulls.reduce((sum, p) => sum + p.pity, 0);
    stats.avgPity4 = (totalPity4 / fourStarPulls.length).toFixed(1);
  }

  const characterEventHistory = [
    ...(state.character1?.history || []),
    ...(state.character2?.history || []),
  ];
  const charFiftyFiftyPulls = characterEventHistory.filter(
    (p) => p.rarity === 5 && (p.outcome === "win" || p.outcome === "loss")
  );
  const charManualLosses = state.characterShared.manualAdjustments || 0;
  const charTotalEvents = charFiftyFiftyPulls.length + charManualLosses;
  if (charTotalEvents > 0) {
    const wins = charFiftyFiftyPulls.filter((p) => p.outcome === "win").length;
    stats.winRate5050 = ((wins / charTotalEvents) * 100).toFixed(0);
  }

  const pathEventHistory = [
    ...(state.weapon?.history || []),
    ...(state.chronicled?.history || []),
  ];
  const pathFiftyFiftyPulls = pathEventHistory.filter(
    (p) => p.rarity === 5 && (p.outcome === "win" || p.outcome === "loss")
  );
  const weaponManualLosses = state.weapon.manualAdjustments || 0;
  const chronicledManualLosses = state.chronicled.manualAdjustments || 0;
  const pathManualLosses = weaponManualLosses + chronicledManualLosses;
  const pathTotalEvents = pathFiftyFiftyPulls.length + pathManualLosses;
  if (pathTotalEvents > 0) {
    const wins = pathFiftyFiftyPulls.filter((p) => p.outcome === "win").length;
    stats.winRatePath = ((wins / pathTotalEvents) * 100).toFixed(0);
  }

  const allNotablePulls = [...fiveStarPulls, ...fourStarPulls];
  const elementCounts = {};
  const weaponTypeCounts = {};
  allNotablePulls.forEach((pull) => {
    const item = findItem(pull.name);
    if (item) {
      if (item.type === "character" && item.element) {
        elementCounts[item.element] = (elementCounts[item.element] || 0) + 1;
      } else if (item.type === "weapon" && item.element) {
        weaponTypeCounts[item.element] =
          (weaponTypeCounts[item.element] || 0) + 1;
      }
    }
  });

  if (Object.keys(elementCounts).length > 0) {
    const favElement = Object.keys(elementCounts).reduce((a, b) =>
      elementCounts[a] > elementCounts[b] ? a : b
    );
    stats.elementalAffinity = {
      element: favElement,
      icon: ELEMENT_ICONS[favElement],
    };
  }
  if (Object.keys(weaponTypeCounts).length > 0) {
    const favWeaponType = Object.keys(weaponTypeCounts).reduce((a, b) =>
      weaponTypeCounts[a] > weaponTypeCounts[b] ? a : b
    );
    stats.weaponAffinity = {
      type: favWeaponType,
      icon: WEAPON_TYPE_ICONS[favWeaponType],
    };
  }

  let luckPoints = 0;
  if (stats.avgPity5 !== null) {
    luckPoints += Math.max(0, 75 - stats.avgPity5);
  } else {
    luckPoints += 10;
  }
  if (stats.winRate5050 !== null) {
    luckPoints += stats.winRate5050 * 0.4;
  } else {
    luckPoints += 20;
  }
  if (stats.avgPity4 !== null) {
    luckPoints += Math.max(0, 6 - stats.avgPity4) * 3;
  }

  if (luckPoints >= 80) stats.luckScore = "S";
  else if (luckPoints >= 60) stats.luckScore = "A";
  else if (luckPoints >= 40) stats.luckScore = "B";
  else stats.luckScore = "C";

  // --- NEU: Hintergrund basierend auf Priorität auswählen ---
  let backgroundKey = "default";
  if (stats.luckScore === "S") {
    backgroundKey = "luck_s";
  } else if (stats.elementalAffinity.element !== "None") {
    const affinityKey = `affinity_${stats.elementalAffinity.element.toLowerCase()}`;
    // Prüfen, ob für dieses Element ein Hintergrund existiert
    if (PROFILE_BACKGROUNDS[affinityKey]) {
      backgroundKey = affinityKey;
    }
  }
  stats.background = PROFILE_BACKGROUNDS[backgroundKey];

  return stats;
}

function getFortuneCookieMessage(luckScore, lang) {
  const messages = {
    S: {
      en: "The stars themselves must be jealous of your pulls!",
      de: "Die Sterne selbst müssen auf deine Züge neidisch sein!",
    },
    A: {
      en: "You seem to have a special connection to the Ley Lines.",
      de: "Du scheinst eine besondere Verbindung zu den Ley-Linien zu haben.",
    },
    B: {
      en: "A steady hand and a patient heart lead to worthy rewards.",
      de: "Eine ruhige Hand und ein geduldiges Herz führen zu würdigen Belohnungen.",
    },
    C: {
      en: "Qiqi is waving at you from a distance...",
      de: "Qiqi winkt dir aus der Ferne zu...",
    },
  };
  return messages[luckScore][lang];
}

function renderTravelersFortuneProfile() {
  const container = document.getElementById("traveler-profile-card");
  if (!container) return;

  const lang = state.language;
  const stats = calculateProfileStats();

  if (!stats) {
    container.innerHTML = `
      <h3 class="text-2xl font-bold text-center mb-4" data-i18n-key="profileTitle">${translations[lang].profileTitle}</h3>
      <p class="text-center mt-4 text-gray-400" data-i18n-key="profileNoData">${translations[lang].profileNoData}</p>`;
    // Wichtig: Hintergrund entfernen, wenn keine Stats da sind
    container.style.backgroundImage = "none";
    return;
  }

  const bgUrl = `/image-proxy/${stats.background.wikiPage
    }?file=${encodeURIComponent(stats.background.fileName)}`;
  container.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.7), rgba(47, 52, 70, 0.8)), url('${bgUrl}')`;

  const {
    totalPulls,
    avgPity5,
    avgPity4,
    luckScore,
    winRate5050,
    winRatePath,
    elementalAffinity,
    weaponAffinity,
  } = stats;

  const profileHTML = `
    <div>
      <h3 class="text-2xl font-bold text-center mb-6" data-i18n-key="profileTitle">${translations[lang].profileTitle
    }</h3>
      
      <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
          <div class="profile-stat-card col-span-2 md:col-span-1">
              <p class="stat-label" data-i18n-key="profileLuckScore">${translations[lang].profileLuckScore
    }</p>
              <p class="stat-value-large text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.7)]">${luckScore}</p>
          </div>
          <div class="profile-stat-card">
              <p class="stat-label" data-i18n-key="profileAvgPity5">${translations[lang].profileAvgPity5
    }</p>
              <p class="stat-value">${avgPity5 || "N/A"}</p>
          </div>
          <div class="profile-stat-card">
              <p class="stat-label" data-i18n-key="profileAvgPity4">${translations[lang].profileAvgPity4
    }</p>
              <p class="stat-value text-purple-400">${avgPity4 || "N/A"}</p>
          </div>
          <div class="profile-stat-card">
              <p class="stat-label" data-i18n-key="totalPulls">${translations[lang].totalPulls
    }</p>
              <p class="stat-value">${totalPulls}</p>
          </div>
          <div class="profile-stat-card">
              <p class="stat-label" data-i18n-key="winRate5050">${translations[lang].winRate5050
    }</p>
              <p class="stat-value">${winRate5050 ? `${winRate5050}%` : "N/A"
    }</p>
          </div>
          <div class="profile-stat-card">
              <p class="stat-label" data-i18n-key="winRatePath">${translations[lang].winRatePath
    }</p>
              <p class="stat-value">${winRatePath ? `${winRatePath}%` : "N/A"
    }</p>
          </div>
      </div>
      
      <div class="mt-8 pt-6 border-t border-genshin-gold-dark/50 grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
        <div class="profile-affinity-card">
            <p class="stat-label" data-i18n-key="profileElementalAffinity">${translations[lang].profileElementalAffinity
    }</p>
            <p class="affinity-value">
                ${elementalAffinity.icon
      ? `<img src="${elementalAffinity.icon}" class="h-8 w-8 inline-block" alt="${elementalAffinity.element}">`
      : ""
    }
                <span>${elementalAffinity.element}</span>
            </p>
        </div>
        <div class="profile-affinity-card">
            <p class="stat-label" data-i18n-key="profileWeaponAffinity">${translations[lang].profileWeaponAffinity
    }</p>
             <p class="affinity-value">
                ${weaponAffinity.icon
      ? `<img src="${weaponAffinity.icon}" class="h-8 w-8 inline-block" alt="${weaponAffinity.type}">`
      : ""
    }
                <span>${weaponAffinity.type}</span>
            </p>
        </div>
      </div>
      
      <div class="mt-8 text-center">
        <p class="stat-label italic" data-i18n-key="profileFortuneCookie">${translations[lang].profileFortuneCookie
    }</p>
        <p class="text-lg italic text-gold-dark">"${getFortuneCookieMessage(
      luckScore,
      lang
    )}"</p>
      </div>
    </div>
    `;
  container.innerHTML = profileHTML;
}

const initialState = {
  characterShared: {
    pity: 0,
    pity4star: 0,
    isGuaranteed: false,
    is4StarGuaranteed: false,
    cState: 1, // Startwert ist 1, wie in der Analyse beschrieben
    isCStateEstimated: true, // Ein Flag, um zu wissen, ob der Wert nur geschätzt ist
  },
  character1: {
    history: [],
    historyFilter: "all",
    wishPriority: "fates",
    featuredItem: "",
    isQuickstartUsed: false,
    activeChart: "pityHistory",
    totalPulls: 0,
  },
  character2: {
    history: [],
    historyFilter: "all",
    wishPriority: "fates",
    featuredItem: "",
    isQuickstartUsed: false,
    activeChart: "pityHistory",
    totalPulls: 0,
  },
  weapon: {
    pity: 0,
    pity4star: 0,
    isGuaranteed: false,
    is4StarGuaranteed: false,
    history: [],
    historyFilter: "all",
    wishPriority: "fates",
    featuredItems: ["", ""],
    courseSetOn: "",
    isQuickstartUsed: false,
    activeChart: "pityHistory",
  },
  chronicled: {
    pity: 0,
    pity4star: 0,
    isGuaranteed: false,
    history: [],
    historyFilter: "all",
    wishPriority: "fates",
    courseSetOn: "",
    isQuickstartUsed: false,
    activeChart: "pityHistory",
  },
  standard: {
    pity: 0,
    pity4star: 0,
    history: [],
    historyFilter: "all",
    wishPriority: "fates",
    isQuickstartUsed: false,
    activeChart: "pityHistory",
  },
  primogems: 0,
  genesisCrystals: 0,
  starglitter: 0,
  intertwinedFates: 0,
  acquaintFates: 0,
  language: "en",
  theme: "default",
  backgroundStyle: "icons",
  lastSeenVersion: "0.0.0",
  planner: {
    goals: [],
    welkin: false,
    battlepass: false,
    useGenesisCrystals: false,
  },
  profile: {
    name: "", // Hier kommt der Account-Name rein
    traveler: "Aether", // Standard-Auswahl, kann "Aether" oder "Lumine" sein
  },
  achievements: {
    unlocked: [],
    seen: [],
  },
  minigame: {
    isActiveGame: false, // Gibt an, ob ein Spiel läuft
    turnCount: 1,
    currentBalls: 5,
    blocks: [],
    powerUps: [],
    score: 0,
    highScore: 0,
    playerNextX: 0,
    maxCombo: 0,
  },
  inventory: {
    characters: {}, // z.B. { "Bennett": { constellation: 5, pendingConstellations: 1 } }
    weapons: [], // z.B. [ { id: 123, name: "Staff of Homa", refinement: 1, isLocked: false } ]
    sort: "rarity_desc",
    filters: {
      element: null, // z.B. 'Pyro'
      weaponType: null, // z.B. 'Sword'
    },
  },
  teams: [
    { name: "Team 1", members: [null, null, null, null] },
    { name: "Team 2", members: [null, null, null, null] },
    { name: "Team 3", members: [null, null, null, null] },
  ],
  hasVisited: false,
};

let itemsDB = [];
let itemLookup = new Map();
let state = {};
let activeBannerContext = {};
let charts = {};
let lastPullId = 0;
let selectedItemForModal = "";
let activeCharacterBanner = "character1";
let currentBannerData = {};
let brickBreakerAnimationId = null;
let audioContext;
let isAudioUnlocked = false;
let soundQueue = []; //
let audioBuffers = {};
let audioTimestamps = {};
let inventoryModalContext = {};
let editInventoryContext = {};
let currentlySelectedItem = null;
let lastInputMethod = "mouse";
let simulationCurrentPage = 0;
let currentSimulationResults = null;
let goalIdCounter = 0;

const dom = {
  datalist: document.getElementById("item-datalist"),
  langButtons: document.querySelectorAll('[data-action="set-language"]'),
  primogemInput: document.getElementById("primogem-input"),
  genesisInput: document.getElementById("genesis-input"),
  starglitterInput: document.getElementById("starglitter-input"),
  intertwinedInput: document.getElementById("intertwined-input"),
  acquaintInput: document.getElementById("acquaint-input"),
  importInput: document.getElementById("import-input"),
  resolveModalTitle: document.getElementById("resolve-modal-title"),
  confirmationModalText: document.getElementById("confirmation-modal-text"),
  confirmationConfirmBtn: document.getElementById("confirmation-confirm-btn"),
  pullEntriesContainer: document.getElementById("pull-entries-container"),
  goalBannerSelect: document.getElementById("goal-banner-select"),
  goalTargetSelect: document.getElementById("goal-target-select"),
  goalWishesNeeded: document.getElementById("goal-wishes-needed"),
  goalPrimosNeeded: document.getElementById("goal-primos-needed"),
  goalShortfall: document.getElementById("goal-shortfall"),
  goalDaysNeeded: document.getElementById("goal-days-needed"),
  welkinCheckbox: document.getElementById("welkin-checkbox"),
  bpCheckbox: document.getElementById("bp-checkbox"),
  useGenesisCheckbox: document.getElementById("use-genesis-checkbox"),
};

async function loadItemsDatabase() {
  try {
    const response = await fetch("/data/itemsDB.json");
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    itemsDB = await response.json();

    // Populate lookup map for O(1) access
    itemsDB.forEach(item => {
      const normalized = item.name.toLowerCase().replace(/["'\s-:]/g, "");
      itemLookup.set(normalized, item);
    });

    console.log("Items database loaded successfully.");
  } catch (error) {
    console.error("Could not load items database:", error);
    document.body.innerHTML = `<div style="text-align: center; padding: 2rem; color: white; font-family: sans-serif;">
      <h1>Fatal Error</h1>
      <p>Could not load the items database (data/itemsDB.json). The application cannot start.</p>
      <p>Please ensure the file is in the correct directory and there are no network errors.</p>
    </div>`;
    throw new Error("Halting script execution due to missing database.");
  }
}

/**
 * Holt Detaildaten für einen bestimmten Gegenstand aus den /data/ Verzeichnissen.
 * @param {string} type Der Typ des Gegenstands (z.B. 'characters', 'materials').
 * @param {string} id Die ID des Gegenstands (z.B. 'bennett', 'agnidus_agate_chunk').
 * @returns {Promise<object|null>} Die JSON-Daten als Objekt oder null bei einem Fehler.
 */
async function fetchData(type, id) {
  const formattedId = id
    .toLowerCase()
    .replace(/\s/g, "_") // Ersetzt nur Leerzeichen, nicht Bindestriche
    .replace(/[^a-z0-9_-]/g, ""); // Erlaubt Bindestriche im Dateinamen

  const url = `/data/${type}/${formattedId}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.error(
        `Konnte Daten für ${type}/${formattedId} nicht laden: ${response.statusText} (Original ID: ${id})`
      );
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error(`Netzwerkfehler beim Laden von ${url}:`, error);
    return null;
  }
}

/**
 * Zeigt das Charakter-Detail-Modal an und lädt die entsprechenden Daten.
 * @param {string} characterId Die ID des Charakters (z.B. 'bennett').
 */
async function showCharacterDetailModal(characterId) {
  const modal = document.getElementById("character-detail-modal");
  const contentContainer = document.getElementById("character-detail-content");
  if (!modal || !contentContainer) return;

  showModal("character-detail-modal");
  contentContainer.innerHTML =
    '<p class="text-center text-xl p-8">Loading character data...</p>'; // Ladeanzeige

  try {
    const characterData = await fetchData("characters", characterId);
    if (characterData) {
      const modalBox = document.getElementById("character-detail-modal-box");
      const splashArtUrl = characterData.assets.splashArt;
      modalBox.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.91), rgba(47, 52, 70, 0.98)), url('${splashArtUrl}')`;
      modalBox.style.backgroundSize = "cover";
      modalBox.style.backgroundPosition = "center top";

      renderCharacterDetailContent(contentContainer, characterData);
    } else {
      throw new Error("Character data not found.");
    }
  } catch (error) {
    console.error(error);
    contentContainer.innerHTML = `<p class="text-center text-xl p-8 text-red-400">Soon™.</p>`;
  }
}

async function loadAudioAssets() {
  // Erstelle den AudioContext erst bei der ersten Nutzerinteraktion (wichtig für Browser-Policy)
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.error("Web Audio API is not supported in this browser");
      return;
    }
  }

  const soundSources = {
    click: "/sounds/klick.ogg",
    fourStar: "",
    fiveStar: "",
    hit: "/sounds/pop.ogg",
    destroy: "",
    bounce: "/sounds/pop.ogg",
    powerup: "",
    gameover: "",
    split: "",
    swirl: "",
  };

  const loadPromises = Object.entries(soundSources).map(async ([key, url]) => {
    if (!url) return;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      // Dekodiere die Rohdaten in ein abspielbares Format
      const decodedAudio = await audioContext.decodeAudioData(arrayBuffer);
      audioBuffers[key] = decodedAudio; // Speichere den fertigen Sound
    } catch (error) {
      console.error(`Could not load or decode audio for "${key}":`, error);
    }
  });

  await Promise.all(loadPromises);
  console.log("All audio assets decoded and ready.");
}

function processSoundQueue() {
  if (soundQueue.length > 0) {
    // Verhindert doppelte Sounds vom selben Typ im selben Frame (z.B. bei schnellen Kollisionen)
    const uniqueSounds = [...new Set(soundQueue.map((s) => s.type))];
    uniqueSounds.forEach((soundType) => {
      // Finde den ersten Sound dieses Typs, um die korrekte Lautstärke zu bekommen
      const soundData = soundQueue.find((s) => s.type === soundType);
      playSound(soundData.type, soundData.volume);
    });
    soundQueue = []; // Leere die Warteschlange für den nächsten Frame
  }
}

function unlockAudio() {
  if (isAudioUnlocked) return;

  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    // Weckt den Context aus dem Ruhezstand auf
    if (audioContext.state === "suspended") {
      audioContext.resume();
    }

    // Erstelle einen leeren Buffer (Stille)
    const buffer = audioContext.createBuffer(1, 1, 22050);
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    // Verbinde ihn direkt mit dem Ausgang und spiele ihn ab
    source.connect(audioContext.destination);
    source.start(0);

    // Lade die echten Sound-Dateien, NACHDEM der Context bereit ist
    loadAudioAssets();

    isAudioUnlocked = true;
    console.log("Audio Context unlocked and ready.");
  } catch (e) {
    console.error("Could not unlock Audio Context:", e);
  }
}

/**
 * Generiert den HTML-Inhalt für das Charakter-Detail-Modal.
 * @param {HTMLElement} container Das DOM-Element, in das der Inhalt eingefügt wird.
 * @param {object} data Die geladenen JSON-Daten für den Charakter.
 */
function renderCharacterDetailContent(container, data) {
  const lang = state.language;

  const createMaterialList = (title, materials) => {
    if (!materials || Object.keys(materials).length === 0) return "";
    let materialItems = Object.entries(materials)
      .map(([id, amount]) => {
        const itemDetails = findItem(id.replace(/_/g, " "));
        if (!itemDetails) return "";
        const iconUrl = getFandomImageUrl(itemDetails.type, itemDetails.name);
        return `
        <div class="flex items-center gap-2 bg-black/20 p-2 rounded-md cursor-pointer hover:bg-black/40 transition-colors" 
             title="${itemDetails.name}"
             data-action="show-item-detail" 
             data-item-type="material" 
             data-item-id="${id}">
          <img src="${iconUrl}" alt="${itemDetails.name
          }" class="h-8 w-8" loading="lazy" onerror="this.style.display='none'"/>
          <span class="font-semibold text-sm text-white">${amount.toLocaleString()}</span>
        </div>`;
      })
      .join("");
    return `
      <div>
        <h4 class="font-semibold text-gold-accent mb-2">${title}</h4>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">${materialItems}</div>
      </div>`;
  };

  const createWeaponList = () => {
    if (!data.bestWeapons || data.bestWeapons.length === 0) return "";
    let weaponItems = data.bestWeapons
      .map((weaponInfo) => {
        const weaponDetails = findItem(weaponInfo.weaponId.replace(/_/g, " "));
        if (!weaponDetails) return "";
        const iconUrl = getFandomImageUrl("weapon", weaponDetails.name);
        const rarityClass = `rarity-${weaponDetails.rarity}`;
        const rarityTextColorClass =
          weaponDetails.rarity === 5
            ? "text-gold-rarity"
            : "text-purple-rarity";
        return `
        <div class="flex items-center gap-3 bg-black/20 p-2 rounded-md cursor-pointer hover:bg-black/40 transition-colors ${rarityClass}"
             data-action="show-item-detail"
             data-item-type="weapon"
             data-item-id="${weaponInfo.weaponId}">
          <img src="${iconUrl}" alt="${weaponDetails.name}" class="h-10 w-10 flex-shrink-0" loading="lazy" onerror="this.style.display='none'"/>
          <div>
            <p class="font-bold text-sm ${rarityTextColorClass}">${weaponDetails.name}</p>
            <p class="text-xs text-gray-300">${weaponInfo.tag}</p>
          </div>
        </div>`;
      })
      .join("");
    return `
      <div>
        <h4 class="font-semibold text-gold-accent mb-2">Recommended Weapons</h4>
        <div class="space-y-2">${weaponItems}</div>
      </div>`;
  };

  const createArtifactList = () => {
    if (!data.bestArtifacts || data.bestArtifacts.length === 0) return "";
    let artifactItems = data.bestArtifacts
      .map((artifactInfo) => {
        // Prüfen, ob es sich um ein gemischtes Set (z.B. 2+2) handelt
        if (artifactInfo.recommendation.length > 1) {
          // Fall 1: Gemischtes Set. Der äußere Container ist NICHT klickbar.
          const setDisplayHtml = artifactInfo.recommendation
            .map((rec, index) => {
              const currentSetDetails = findItem(
                rec.artifactId.replace(/_/g, " ")
              );
              if (!currentSetDetails) return "";
              const iconUrl = getFandomImageUrl(
                "artifact_set",
                currentSetDetails.name
              );
              const separator =
                index > 0
                  ? `<div class="text-center text-gold-dark font-bold my-2">+</div>`
                  : "";

              // Jedes Set-Element wird zu einem eigenen, klickbaren Block
              return `${separator}
              <div class="flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-black/40 transition-colors"
                   data-action="show-item-detail" 
                   data-item-type="artifact_set" 
                   data-item-id="${rec.artifactId}">
                <img src="${iconUrl}" alt="${currentSetDetails.name}" class="h-10 w-10 flex-shrink-0" loading="lazy" onerror="this.style.display='none'"/>
                <div>
                  <p class="font-bold text-sm text-white">${rec.pieces}-Set Bonus</p>
                  <p class="text-xs text-gray-300">${currentSetDetails.name}</p>
                </div>
              </div>`;
            })
            .join("");

          return `
            <div class="bg-black/20 p-3 rounded-md">
              ${setDisplayHtml}
              <p class="text-xs text-gray-300 mt-3 pt-3 border-t border-gold-dark/20">${artifactInfo.reason}</p>
            </div>`;
        } else {
          // Fall 2: Einzelnes Set (z.B. 4er). Der äußere Container ist klickbar.
          const rec = artifactInfo.recommendation[0];
          const setDetails = findItem(rec.artifactId.replace(/_/g, " "));
          if (!setDetails) return "";
          const iconUrl = getFandomImageUrl("artifact_set", setDetails.name);

          const setDisplayHtml = `
            <div class="flex items-center gap-3">
              <img src="${iconUrl}" alt="${setDetails.name}" class="h-10 w-10 flex-shrink-0" loading="lazy" onerror="this.style.display='none'"/>
              <div>
                <p class="font-bold text-sm text-white">${rec.pieces}-Set Bonus</p>
                <p class="text-xs text-gray-300">${setDetails.name}</p>
              </div>
            </div>`;

          return `
            <div class="bg-black/20 p-3 rounded-md cursor-pointer hover:bg-black/40 transition-colors"
                 data-action="show-item-detail"
                 data-item-type="artifact_set"
                 data-item-id="${rec.artifactId}">
              ${setDisplayHtml}
              <p class="text-xs text-gray-300 mt-3 pt-3 border-t border-gold-dark/20">${artifactInfo.reason}</p>
            </div>`;
        }
      })
      .join("");
    return `
      <div>
        <h4 class="font-semibold text-gold-accent mb-2">Recommended Artifacts</h4>
        <div class="space-y-2">${artifactItems}</div>
      </div>`;
  };

  const html = `
    <header class="mb-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
      <img 
        src="${data.assets.icon}" 
        alt="${data.name} Icon" 
        class="h-24 w-24 sm:h-32 sm:w-32 rounded-lg border-2 border-gold-dark bg-black/20 flex-shrink-0"
        loading="lazy"
        onerror="this.style.display='none'"
      >
      <div class="flex-grow text-center sm:text-left">
        <h2 class="text-4xl font-bold font-headings text-white drop-shadow-lg">${data.name
    }</h2>
        <p class="text-2xl text-yellow-400">${"★".repeat(data.rarity)}</p>
        <div class="flex justify-center sm:justify-start items-center gap-2 mt-1">
          <img src="${ELEMENT_ICONS[data.element]}" class="h-6 w-6" alt="${data.element
    }" title="${data.element}" />
          <img src="${WEAPON_TYPE_ICONS[data.weaponType]
    }" class="h-6 w-6" alt="${data.weaponType}" title="${data.weaponType}" />
        </div>
        
        <!-- Region and Roles -->
        <div class="flex justify-center sm:justify-start items-center gap-2 mt-2">
             ${data.region
      ? `<img src="${getRegionIcon(
        data.region
      )}" class="h-6 w-6" alt="${data.region
      }" title="${data.region}" onerror="this.style.display='none'"/>`
      : ""
    }
             ${data.region && data.roles && data.roles.length > 0 ? '<span class="text-gray-600">|</span>' : ''
    }
             ${data.roles && data.roles.length > 0
      ? getRoleIcons(data.roles)
        .map(
          (r) =>
            `<img src="${r.url}" class="h-6 w-6" alt="${r.role}" title="${r.role}" onerror="this.style.display='none'"/>`
        )
        .join("")
      : ""
    }
        </div>
      </div>
    </header>
    <div class="mb-8">
      <blockquote class="border-l-4 border-gold-dark/50 pl-4 italic text-gray-300 whitespace-pre-line font-body">
        ${data.description}
      </blockquote>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
      <div class="space-y-6">
        ${createWeaponList()}
        ${createArtifactList()}
        <div>
          <h4 class="font-semibold text-gold-accent mb-2">Stat Priority</h4>
          <div class="text-sm space-y-1 bg-black/20 p-3 rounded-md">
            <p><strong>Sands:</strong> ${data.statPriority.sands}</p>
            <p><strong>Goblet:</strong> ${data.statPriority.goblet}</p>
            <p><strong>Circlet:</strong> ${data.statPriority.circlet}</p>
            <p><strong>Substats:</strong> ${data.statPriority.substats}</p>
          </div>
        </div>
         <div>
          <h4 class="font-semibold text-gold-accent mb-2">Recommended Teams</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${data.teamComps
      .map(
        (comp) => `
              <div class="bg-black/20 p-3 rounded-md flex flex-col">
                <p class="font-bold text-base mb-2 min-h-[3rem] text-center">${comp.name
          }</p>
                <div class="grid grid-cols-[auto_auto] justify-center gap-2 mt-auto">
                  ${comp.members
            .map((memberId) => {
              const memberDetails = findItem(
                memberId.replace(/_/g, " ")
              );
              if (!memberDetails) return "";
              const iconUrl = getFandomImageUrl(
                memberDetails.type,
                memberDetails.name
              );
              const characterIdAttr = `data-character-id="${memberDetails.name
                .toLowerCase()
                .replace(/ /g, "_")}" style="cursor: pointer;"`;
              const borderColor =
                ELEMENT_COLORS[memberDetails.element] ||
                "var(--gold-dark)";
              return `<img src="${iconUrl}" alt="${memberId}" class="h-12 w-12 rounded-full border-2 aspect-square" style="border-color: ${borderColor};" title="${memberDetails?.name}" ${characterIdAttr} loading="lazy" onerror="this.style.display='none'"/>`;
            })
            .join("")}
                </div>
              </div>`
      )
      .join("")}
          </div>
        </div>
      </div>
      <div class="space-y-6">
        ${createMaterialList("Ascension Materials", data.totalMaterials)}
        ${createMaterialList(
        "Talent Materials (per Talent)",
        data.totalTalentMaterials
      )}
      </div>
    </div>`;
  container.innerHTML = html;
}

/**
 * Erstellt ein zugängliches, benutzerdefiniertes Dropdown durch Schachtelung in einem Label.
 * @param {string} wrapperId - Die ID des umschließenden <div>.
 * @param {Array} options - Die Liste der Optionen.
 * @param {string} initialValue - Der vorausgewählte Wert.
 * @param {function} onSelect - Die Callback-Funktion.
 */
function createCustomSelect(wrapperId, options, initialValue, onSelect) {
  const wrapper = document.getElementById(wrapperId);
  if (!wrapper) return;

  // Diese Zeile ist jetzt sauber und enthält keine undefinierten Variablen mehr.
  wrapper.innerHTML = `<div class="custom-select-trigger" tabindex="0"><div class="trigger-content"></div><span class="arrow">▼</span></div><div class="custom-options"></div>`;

  const trigger = wrapper.querySelector(".custom-select-trigger");
  const triggerContent = wrapper.querySelector(".trigger-content");
  const optionsContainer = wrapper.querySelector(".custom-options");

  // Hilfsfunktion zum Kodieren von Anführungszeichen für HTML-Attribute
  const escapeQuotes = (str) => str.replace(/"/g, "&quot;");

  const updateTriggerContent = (value) => {
    if (!value) {
      triggerContent.innerHTML = `<span>---</span>`;
      return;
    }
    const itemDetails = findItem(value);
    if (itemDetails) {
      const iconUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );
      triggerContent.innerHTML = `<img src="${iconUrl}" alt="${itemDetails.name}" loading="lazy" onerror="this.style.display='none';"><span>${itemDetails.name}</span>`;
    } else {
      triggerContent.innerHTML = `<span>---</span>`;
    }
  };

  optionsContainer.innerHTML = `<div class="custom-option" data-value="">---</div>`;
  options.forEach((item) => {
    const iconUrl = getFandomImageUrl(item.type, item.name, "icon");
    const escapedName = escapeQuotes(item.name);
    optionsContainer.insertAdjacentHTML(
      "beforeend",
      `<div class="custom-option" data-value="${escapedName}"><img src="${iconUrl}" alt="${item.name}" loading="lazy" onerror="this.style.display='none';"><span>${item.name}</span></div>`
    );
  });

  const allOptions = optionsContainer.querySelectorAll(".custom-option");

  const selectOption = (value) => {
    onSelect(value);
    updateTriggerContent(value);
    allOptions.forEach((opt) =>
      opt.classList.toggle("selected", opt.dataset.value === value)
    );
    wrapper.classList.remove("open");

    const scrollParent = wrapper.closest(".overflow-y-auto");
    if (scrollParent) {
      scrollParent.classList.remove("overflow-visible");
    }
  };

  trigger.addEventListener("click", () => {
    const isOpen = wrapper.classList.contains("open");
    const scrollParent = wrapper.closest(".overflow-y-auto");

    document
      .querySelectorAll(".custom-select-wrapper.open")
      .forEach((openWrapper) => {
        if (openWrapper !== wrapper) {
          openWrapper.classList.remove("open");
          const otherScrollParent = openWrapper.closest(".overflow-y-auto");
          if (otherScrollParent) {
            otherScrollParent.classList.remove("overflow-visible");
          }
        }
      });

    wrapper.classList.toggle("open");

    if (scrollParent) {
      if (wrapper.classList.contains("open")) {
        scrollParent.classList.add("overflow-visible");
      } else {
        scrollParent.classList.remove("overflow-visible");
      }
    }
  });

  allOptions.forEach((option) =>
    option.addEventListener("click", () => selectOption(option.dataset.value))
  );

  updateTriggerContent(initialValue);
  allOptions.forEach((opt) =>
    opt.classList.toggle("selected", opt.dataset.value === initialValue)
  );
}

function showAddInventoryModal(type) {
  inventoryModalContext = { type };

  const modalTitle = document.getElementById("add-inventory-modal-title");
  const levelLabel = document.getElementById("inventory-level-label");
  const levelSelect = document.getElementById("inventory-level-select");
  const pendingContainer = document.getElementById(
    "inventory-pending-container"
  );
  const levelSelectorGrid = document.getElementById("inventory-level-selector");

  modalTitle.textContent = `Add ${type.charAt(0).toUpperCase() + type.slice(1)
    } to Collection`;
  levelSelect.innerHTML = "";
  document.getElementById("inventory-pending-input").value = 0;

  let options = [];
  if (type === "character") {
    levelLabel.textContent = "Constellation";
    pendingContainer.style.display = "block"; // Zeige Pending-Feld an
    levelSelectorGrid.classList.remove("grid-cols-1");
    levelSelectorGrid.classList.add("grid-cols-2"); // Mache das Grid 2-spaltig

    for (let i = 0; i <= 6; i++) {
      levelSelect.innerHTML += `<option value="${i}">C${i}</option>`;
    }
    options = itemsDB.filter(
      (item) => item.type === "character" && item.rarity >= 4
    );
  } else {
    // weapon
    levelLabel.textContent = "Refinement";
    pendingContainer.style.display = "none"; // Verstecke Pending-Feld
    levelSelectorGrid.classList.remove("grid-cols-2");
    levelSelectorGrid.classList.add("grid-cols-1"); // Mache das Grid 1-spaltig

    for (let i = 1; i <= 5; i++) {
      levelSelect.innerHTML += `<option value="${i}">R${i}</option>`;
    }
    options = itemsDB.filter(
      (item) => item.type === "weapon" && item.rarity >= 3
    );
  }

  options.sort((a, b) => a.name.localeCompare(b.name));

  createCustomSelect(
    "inventory-item-select-wrapper",
    options,
    "",
    (newValue) => {
      inventoryModalContext.selectedItem = newValue;
    }
  );

  showModal("add-inventory-modal");
}

function toggleGuarantee(banner) {
  const isCharacterBanner = banner === "character1" || banner === "character2";
  const targetState = isCharacterBanner ? state.characterShared : state[banner];

  // Initialisiere manualAdjustments, falls es nicht existiert
  if (typeof targetState.manualAdjustments === "undefined") {
    targetState.manualAdjustments = 0;
  }

  const wasGuaranteed = targetState.isGuaranteed;
  targetState.isGuaranteed = !wasGuaranteed;

  // Wähle die passende Nachricht basierend auf dem Bannertyp
  const lossMessage =
    banner === "weapon" || banner === "chronicled"
      ? "Manual Path Loss recorded for stats."
      : "Manual 50/50 Loss recorded for stats.";
  const removalMessage =
    banner === "weapon" || banner === "chronicled"
      ? "Manual Path Loss removed from stats."
      : "Manual 50/50 Loss removed from stats.";

  if (!wasGuaranteed && targetState.isGuaranteed) {
    // Wenn die Garantie HINZUGEFÜGT wird, zählt das als manueller Verlust
    targetState.manualAdjustments++;
    showToast(lossMessage, "success");
  } else if (wasGuaranteed && !targetState.isGuaranteed) {
    // Wenn die Garantie ENTFERNT wird, wird ein manueller Verlust rückgängig gemacht
    targetState.manualAdjustments = Math.max(
      0,
      targetState.manualAdjustments - 1
    );
    showToast(removalMessage, "success");
  }

  saveAndRefresh();
}

function handleAddInventoryConfirm() {
  const { type, selectedItem } = inventoryModalContext;
  const level = parseInt(
    document.getElementById("inventory-level-select").value,
    10
  );

  if (!selectedItem) {
    showToast("Please select an item.", "error");
    return;
  }

  if (type === "character") {
    const pending =
      parseInt(document.getElementById("inventory-pending-input").value, 10) ||
      0;
    if (state.inventory.characters[selectedItem]) {
      showToast(
        `${selectedItem} is already in your collection. Use the edit button instead.`,
        "error"
      );
      return;
    }
    state.inventory.characters[selectedItem] = {
      constellation: level,
      pendingConstellations: pending,
    };
  } else {
    // weapon
    const newWeapon = {
      id: Date.now() + Math.random(),
      name: selectedItem,
      refinement: level,
      isLocked: false,
    };
    state.inventory.weapons.push(newWeapon);
  }

  showToast(`${selectedItem} added to your collection!`, "success");
  saveAndRefresh();
  hideModal("add-inventory-modal");
}

function playSound(type, relativeVolume = 1.0) {
  // Stelle sicher, dass der AudioContext existiert und Sounds an sind
  if (
    !audioContext ||
    !state.audio.soundEffects ||
    state.audio.masterVolume === 0
  ) {
    return;
  }

  // Prüfe, ob der vor-dekodierte Sound-Buffer existiert
  const audioBuffer = audioBuffers[type];
  if (!audioBuffer) {
    // console.warn(`Sound buffer for "${type}" not found.`); // Optional: für Debugging
    return;
  }

  // Throttling-Logik bleibt nützlich, um Sound-Spam zu verhindern
  const soundThrottleInfo = { hit: 100, destroy: 75, bounce: 50 };
  const now = Date.now();
  if (soundThrottleInfo[type]) {
    const lastPlayed = audioTimestamps[type] || 0;
    if (now - lastPlayed < soundThrottleInfo[type]) {
      return;
    }
    audioTimestamps[type] = now;
  }

  // Erstelle eine "Source" (eine Abspiel-Instanz des Sounds)
  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;

  // Erstelle einen GainNode, um die Lautstärke zu regeln
  const gainNode = audioContext.createGain();
  gainNode.gain.value = state.audio.masterVolume * relativeVolume;

  // Verbinde die Kette: Source -> Lautstärkeregler -> Lautsprecher
  source.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // Spiele den Sound ab. `start(0)` bedeutet "jetzt sofort".
  source.start(0);
}

function updateInventoryOnPull(itemName) {
  const item = findItem(itemName);
  if (
    !item ||
    item.rarity < 4 ||
    (item.type !== "character" && item.type !== "weapon")
  ) {
    return;
  }

  // Ensure Starglitter state exists
  if (typeof state.starglitter === "undefined") {
    state.starglitter = 0;
  }

  let starglitterEarned = 0;

  if (item.type === "character") {
    const inventory = state.inventory.characters;

    if (inventory[item.name]) {
      // Character exists
      const charData = inventory[item.name];

      // Initialize totalCount if missing (backward compatibility)
      if (typeof charData.totalCount === "undefined") {
        // Estimate based on current state: C0 = 1, C1 = 2, etc.
        // This is a rough guess for existing data, but accurate for new pulls.
        charData.totalCount = charData.constellation + charData.pendingConstellations + 1;
      } else {
        charData.totalCount++;
      }

      const currentEffectiveConstellation = charData.constellation + charData.pendingConstellations;

      if (currentEffectiveConstellation < 6) {
        // Not yet C6: Standard separate & constellation upgrade
        charData.pendingConstellations++;

        starglitterEarned = item.rarity === 5 ? 10 : 2;

        showToast(
          `${item.name} Constellation available! (+${starglitterEarned} Starglitter)`,
          "success"
        );
      } else {
        // Already C6 (or fully pending C6): Limit Reached
        // DO NOT increment pendingConstellations

        starglitterEarned = item.rarity === 5 ? 25 : 5;

        showToast(
          `Max Constellation reached for ${item.name}! (+${starglitterEarned} Starglitter)`,
          "info"
        );
      }

    } else {
      // New Character
      inventory[item.name] = {
        constellation: 0,
        pendingConstellations: 0,
        totalCount: 1
      };
      // New characters usually don't give Starglitter (only 4-star gives 2? No, usually 0 for unlock).
      // Wait, in Genshin:
      // 5-star character duplicate: 10 SG (C0-C5), 25 SG (C6)
      // 4-star character duplicate: 2 SG (C0-C5), 5 SG (C6)
      // New obtain: 0 Starglitter.

      showToast(`${item.name} has been added to your Collection!`, "success");
    }
  } else if (item.type === "weapon") {
    // Weapons always give Starglitter on pull
    starglitterEarned = item.rarity === 5 ? 10 : 2;

    const newWeaponInstance = {
      id: Date.now() + Math.random(), // Unique ID
      name: item.name,
      refinement: 1,
      isLocked: false,
    };

    state.inventory.weapons.push(newWeaponInstance);
    showToast(`New Weapon "${item.name}" (+${starglitterEarned} Starglitter)`, "success");
  }

  // Update State & UI
  if (starglitterEarned > 0) {
    state.starglitter += starglitterEarned;
    // We need to update the UI specifically for Starglitter if it's visible
    if (document.getElementById("starglitter-input")) {
      document.getElementById("starglitter-input").value = state.starglitter;
      updateStarglitterDisplay(); // Ensure this function is available/called
    }
    // Also trigger a general save
  }
}

let activeToast = null;

function showToast(message, type = "info", duration = 3000) {
  // Wenn schon ein Toast da ist, entferne ihn zuerst
  if (activeToast) {
    activeToast.remove();
  }

  activeToast = document.createElement("div");
  activeToast.className = `toast-notification ${type}`;
  activeToast.textContent = message;
  document.body.appendChild(activeToast);

  setTimeout(() => {
    activeToast.classList.add("show");
  }, 10); // Kurze Verzögerung für die CSS-Transition

  if (duration) {
    setTimeout(() => {
      hideToast();
    }, duration);
  }
}

function updateToast(message, type = "info") {
  if (activeToast) {
    activeToast.textContent = message;
    // Entferne alte Typen-Klassen und füge die neue hinzu
    activeToast.className = "toast-notification show"; // reset
    activeToast.classList.add(type);
  }
}

function hideToast() {
  if (activeToast) {
    const toastElement = activeToast;
    toastElement.classList.remove("show");
    toastElement.addEventListener(
      "transitionend",
      () => {
        toastElement.remove();
        if (activeToast === toastElement) {
          activeToast = null;
        }
      },
      { once: true }
    );
  }
}

function setLanguage(lang) {
  state.language = lang;
  document.documentElement.lang = lang;
  const metaDescriptionTag = document.querySelector('meta[name="description"]');
  if (lang === "de") {
    document.title = "Genshin Impact Pity Tracker - Zähle deine Gebete";
    metaDescriptionTag.setAttribute(
      "content",
      "Der ultimative Pity Tracker für Genshin Impact. Zähle ganz einfach deine Gebete für Figuren-, Waffen- und Standardbanner und verpasse nie wieder eine 5-Sterne-Ziehung."
    );
  } else {
    document.title = "Genshin Impact Pity Tracker - Track Your Wishes";
    metaDescriptionTag.setAttribute(
      "content",
      "The ultimate Pity Tracker for Genshin Impact. Easily count your wishes for character, weapon, and standard banners and never miss a 5-star pull again."
    );
  }

  document.querySelectorAll("[data-i18n-key]").forEach((el) => {
    const key = el.dataset.i18nKey;
    if (translations[lang][key]) {
      if (el.tagName === "BUTTON" && key === "buyWithGlitter") {
        el.title = translations[lang][key];
        el.innerHTML = `+ <span class="text-xs ml-1">${translations[lang][key]}</span>`;
      } else {
        el.innerHTML = translations[lang][key];
      }
    }
  });

  const versionInfoEl = document.getElementById("version-info");
  if (versionInfoEl) {
    // Zeigt die App-Version und die unterstützte Spiel-Version an
    const gameVersionText = translations[lang].versionInfo.replace(
      "{version}",
      SUPPORTED_GAME_VERSION
    );
    versionInfoEl.textContent = `App v${APP_VERSION} | ${gameVersionText}`;
  }

  //dom.resolveNameInput.placeholder =
  //  lang === "de" ? "z.B. Diluc, Homa-Stab..." : "e.g. Diluc, Staff of Homa...";
  dom.langButtons.forEach((btn) =>
    btn.classList.toggle("active", btn.dataset.lang === lang)
  );
  updateUI();
  saveState();
}

function updateThemeButtons() {
  const themeButtons = document.querySelectorAll('[data-action="set-theme"]');
  themeButtons.forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.theme === state.theme);
  });
}

function setTheme(theme) {
  document.body.className = document.body.className
    .replace(/theme-\w+/g, "")
    .trim();
  if (theme !== "default") {
    document.body.classList.add(`theme-${theme}`);
  }
  state.theme = theme;

  // NEU: Theme-Farbe für die PWA-Leiste aktualisieren
  const themeColor = THEME_COLORS[theme] || THEME_COLORS.default;
  updateThemeColorMetaTag(themeColor);

  updateThemeButtons();
  saveState();
  Object.keys(charts).forEach((banner) => renderCharts(banner));
}

function showFirstTimeModal() {
  // state.hasVisited wird in der loadState-Funktion initialisiert.
  if (!state.hasVisited) {
    // Bot detection to prevent Soft 404s
    const botPattern = /bot|googlebot|crawler|spider|robot|crawling|lighthouse|pagespeed|inspection|headless/i;
    if (botPattern.test(navigator.userAgent)) {
      console.log("Bot detected, skipping welcome modal.");
      return;
    }

    // Zeige das Modal erst nach einer kurzen Verzögerung (z.B. 1500ms).
    setTimeout(() => {
      showModal("how-to-use-modal");
      state.hasVisited = true;
      saveState(); // Speichere sofort, dass das Modal angezeigt wurde.
    }, 3000); // 3 Sekunden Verzögerung
  }
}

function loadState() {
  console.log("--- [LOAD] Starting loadState() after page reload ---");

  // --- Einmalige Migration zu Multi-Account ---
  function migrateToMultiAccount() {
    const oldStateData = localStorage.getItem("genshinPityTrackerStateV2");
    const accountsMeta = localStorage.getItem("accounts_meta");

    if (oldStateData && !accountsMeta) {
      console.log("Migrating existing data to multi-account structure...");
      try {
        const oldState = JSON.parse(oldStateData);
        const accountId = `acc_${Date.now()}`;

        const newMeta = {
          activeAccountId: accountId,
          accounts: [{ id: accountId, name: "Default Account" }],
          isDriveConnected: oldState.isDriveConnected || false,
        };

        delete oldState.isDriveConnected;

        localStorage.setItem("accounts_meta", JSON.stringify(newMeta));
        localStorage.setItem(`account_${accountId}`, JSON.stringify(oldState));
        localStorage.removeItem("genshinPityTrackerStateV2");

        showToast("App data updated for multi-account support!", "success");
      } catch (e) {
        console.error("Failed to migrate data:", e);
      }
    }
  }

  migrateToMultiAccount();

  // --- Angepasste Lade-Logik ---
  const metaDataString = localStorage.getItem("accounts_meta");

  let accountsMeta = null;
  try {
    accountsMeta = JSON.parse(metaDataString);
  } catch (e) {
    console.error("[LOAD] FEHLER beim Parsen von 'accounts_meta'!", e);
    localStorage.removeItem("accounts_meta");
    accountsMeta = null;
  }

  if (!accountsMeta) {
    const accountId = `acc_${Date.now()}`;
    accountsMeta = {
      activeAccountId: accountId,
      accounts: [{ id: accountId, name: "Default Account" }],
      isDriveConnected: false,
    };
    localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));
  }

  if (!accountsMeta.activeAccountId && accountsMeta.accounts.length > 0) {
    accountsMeta.activeAccountId = accountsMeta.accounts[0].id;
    localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));
  }

  const activeAccountId = accountsMeta.activeAccountId;
  const savedStateData = localStorage.getItem(`account_${activeAccountId}`);

  if (savedStateData && savedStateData !== "undefined") {
    state = deepMerge(
      JSON.parse(JSON.stringify(initialState)),
      JSON.parse(savedStateData)
    );
  } else {
    state = JSON.parse(JSON.stringify(initialState));
  }

  state.isDriveConnected = accountsMeta.isDriveConnected || false;

  let detectedLang = "en";
  if (!savedStateData) {
    const browserLang = navigator.language || navigator.userLanguage || "";
    if (browserLang.toLowerCase().startsWith("de")) {
      detectedLang = "de";
    }
  }

  if (typeof state.backgroundStyle === "undefined") {
    state.backgroundStyle = "icons";
  }
  if (!state.audio) {
    state.audio = {
      masterVolume: 0.5,
      soundEffects: true,
    };
  }

  if (state.character1.pity || state.character2.pity) {
    state.characterShared.pity = Math.max(
      state.character1.pity || 0,
      state.character2.pity || 0
    );
    state.characterShared.pity4star = Math.max(
      state.character1.pity4star || 0,
      state.character2.pity4star || 0
    );
    state.characterShared.isGuaranteed =
      state.character1.isGuaranteed || state.character2.isGuaranteed || false;
    delete state.character1.pity;
    delete state.character1.pity4star;
    delete state.character1.isGuaranteed;
    delete state.character2.pity;
    delete state.character2.pity4star;
    delete state.character2.isGuaranteed;
    saveState();
  }
  if (state.character && !state.character1) {
    state.character1 = state.character;
    delete state.character;
  }
  if (!state.character2) {
    state.character2 = JSON.parse(JSON.stringify(initialState.character2));
  }
  ["character1", "character2", "weapon", "standard", "chronicled"].forEach(
    (banner) => {
      if (state[banner] && !state[banner].wishPriority)
        state[banner].wishPriority = "fates";
      if (typeof state[banner].isQuickstartUsed === "undefined")
        state[banner].isQuickstartUsed = false;
    }
  );
  if (!state.planner)
    state.planner = {
      welkin: false,
      battlepass: false,
      useGenesisCrystals: false,
    };
  if (typeof state.planner.useGenesisCrystals === "undefined")
    state.planner.useGenesisCrystals = false;
  if (typeof state.starglitter === "undefined") state.starglitter = 0;
  if (typeof state.hasVisited === "undefined") state.hasVisited = false;
  if (typeof state.theme === "undefined") state.theme = "default";
  if (
    state.characterShared &&
    typeof state.characterShared.is4StarGuaranteed === "undefined"
  ) {
    state.characterShared.is4StarGuaranteed = false;
  }
  if (state.weapon && typeof state.weapon.is4StarGuaranteed === "undefined") {
    state.weapon.is4StarGuaranteed = false;
  }

  // --- HOTFIX: Eye of Perception 4-Star Guarantee Bug ---
  if (state.weapon && state.weapon.history && state.weapon.history.length > 0) {
    let last4StarPull = null;
    for (let i = 0; i < state.weapon.history.length; i++) {
      if (state.weapon.history[i].rarity === 4) {
        last4StarPull = state.weapon.history[i];
        break;
      }
    }
    if (last4StarPull && (last4StarPull.name === "Eye of Perception" || last4StarPull.name === "Eye of Perception ") && state.weapon.is4StarGuaranteed) {
      console.log("Applying hotfix for Eye of Perception 4-Star Guarantee bug.");
      state.weapon.is4StarGuaranteed = false;
      if (last4StarPull.name === "Eye of Perception ") {
        last4StarPull.name = "Eye of Perception";
      }
    }
  }

  if (!state.teams) {
    console.log("Migrating data: Adding empty teams structure.");
    state.teams = JSON.parse(JSON.stringify(initialState.teams));
  }

  if (state.planner && !Array.isArray(state.planner.goals)) {
    console.log("Migrating old planner state to new multi-goal format...");

    const newPlannerState = JSON.parse(JSON.stringify(initialState.planner));

    newPlannerState.welkin = state.planner.welkin || false;
    newPlannerState.battlepass = state.planner.battlepass || false;
    newPlannerState.useGenesisCrystals =
      state.planner.useGenesisCrystals || false;
    newPlannerState.lastSeenVersion = state.planner.lastSeenVersion || "0.0.0";

    newPlannerState.goals = [];

    state.planner = newPlannerState;

    // Speichere den migrierten Zustand sofort, damit dies nur einmal passiert.
    saveState();
  }

  setActiveBannerRotation();
  setLanguage(state.language || detectedLang);
  setTheme(state.theme);
}

function saveState() {
  try {
    // 1. Finde heraus, welcher Account aktiv ist.
    const metaRaw = localStorage.getItem("accounts_meta");
    if (!metaRaw) {
      console.error("Save failed: 'accounts_meta' missing in localStorage.");
      // Versuche, den Fehler zu beheben, indem wir einen Default-Account annehmen, falls möglich? 
      // Besser: User warnen.
      showToast("Critical Error: Could not save data (Meta-Data missing). Please backup to file!", "error");
      return;
    }

    const accountsMeta = JSON.parse(metaRaw);
    if (!accountsMeta || !accountsMeta.activeAccountId) {
      console.error("Save failed: Could not determine active account.", accountsMeta);
      showToast("Critical Error: Could not save data (No active account).", "error");
      return;
    }
    const activeAccountId = accountsMeta.activeAccountId;

    // 2. Speichere NUR den Account-spezifischen State.
    const stateToSave = { ...state };
    // Entferne temporäre/globale Flags, die nicht in den Account-State gehören
    delete stateToSave.isDriveConnected;

    // Safety Check: Prüfe auf Circular References oder andere Stringify-Probleme
    const serializedState = JSON.stringify(stateToSave);

    localStorage.setItem(
      `account_${activeAccountId}`,
      serializedState
    );
    // console.log("State saved successfully to", `account_${activeAccountId}`);
  } catch (e) {
    console.error("CRITICAL SAVE ERROR:", e);
    showToast(`Save Error: ${e.message}`, "error");
  }
}

function saveAndRefresh() {
  saveState();
  updateUI();
  checkAchievements();
}

function deepMerge(target, source) {
  for (const key in source) {
    if (
      source[key] instanceof Object &&
      !Array.isArray(source[key]) &&
      key in target
    ) {
      deepMerge(target[key], source[key]);
    } else {
      target[key] = source[key];
    }
  }
  return target;
}

function animateCounter(element, start, end, duration = 300) {
  let startTimestamp = null;
  const step = (timestamp) => {
    if (!startTimestamp) startTimestamp = timestamp;
    const progress = Math.min((timestamp - startTimestamp) / duration, 1);
    element.textContent = Math.floor(progress * (end - start) + start);
    if (progress < 1) window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}

function updateAudioUI() {
  const muteBtn = document.getElementById("mute-btn");
  const volumeSlider = document.getElementById("volume-slider");

  if (!muteBtn || !volumeSlider) return;

  volumeSlider.value = state.audio.masterVolume;

  const isSoundOn = state.audio.soundEffects && state.audio.masterVolume > 0;

  if (isSoundOn) {
    // Ton ist AN -> Zeige das "Ton AN"-Icon. Ein Klick darauf soll stummschalten.
    muteBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
</svg>
`;
    muteBtn.title = "Mute";
  } else {
    // Ton ist AUS -> Zeige das "Ton AUS"-Icon (durchgestrichen).
    muteBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
</svg>
`;
    muteBtn.title = "Unmute";
  }
}

function renderTravelerProfileDisplay() {
  const { name, traveler } = state.profile;
  const body = document.body;

  if (name && name.trim() !== "") {
    // 1. Setze die Status-Klasse am Body
    body.classList.add("profile-active");

    // 2. Definiere die Icon-URL
    const iconUrl =
      traveler === "Lumine"
        ? "/image-proxy/Traveler/Gallery?file=Lumine_Icon.png"
        : "/image-proxy/Traveler/Gallery?file=Aether_Icon.png";

    // 3. Befülle BEIDE Elemente mit den Daten. CSS entscheidet, welches sichtbar ist.
    // Desktop-Elemente
    document.getElementById("traveler-profile-icon-desktop").src = iconUrl;
    document.getElementById("traveler-profile-name-desktop").textContent = name;

    // Bestehende Mobile-Elemente
    document.getElementById("traveler-profile-icon-mobile").src = iconUrl;
    document.getElementById("traveler-profile-name-mobile").textContent = name;
  } else {
    // Wenn kein Name da ist, entferne die Status-Klasse
    body.classList.remove("profile-active");
  }
}

// Lädt den gespeicherten Zustand in die Einstellungsfelder
function updateProfileSettingsUI() {
  const nameInput = document.getElementById("profile-name-input");
  const aetherRadio = document.getElementById("traveler-aether-radio");
  const lumineRadio = document.getElementById("traveler-lumine-radio");

  if (nameInput) {
    nameInput.value = state.profile.name || "";
  }
  if (aetherRadio && lumineRadio) {
    if (state.profile.traveler === "Lumine") {
      lumineRadio.checked = true;
    } else {
      aetherRadio.checked = true; // Aether ist der Standard
    }
  }
}

// Speichert die Änderungen aus den Einstellungen
function handleProfileUpdate() {
  const nameInput = document.getElementById("profile-name-input");
  const selectedTraveler = document.querySelector(
    'input[name="traveler-choice"]:checked'
  );

  if (nameInput) {
    state.profile.name = nameInput.value;
  }
  if (selectedTraveler) {
    state.profile.traveler = selectedTraveler.value;
  }

  saveState();
  renderTravelerProfileDisplay(); // Anzeige sofort aktualisieren
}

function updateUI() {
  dom.primogemInput.value = state.primogems;
  dom.genesisInput.value = state.genesisCrystals;
  dom.starglitterInput.value = state.starglitter;
  dom.intertwinedInput.value = state.intertwinedFates;
  dom.acquaintInput.value = state.acquaintFates;
  dom.welkinCheckbox.checked = state.planner.welkin;
  dom.bpCheckbox.checked = state.planner.battlepass;
  dom.useGenesisCheckbox.checked = state.planner.useGenesisCrystals;
  updateStarglitterDisplay();
  updateGenesisCrystalDisplay();
  updatePrimogemDisplay();
  updateAllButtonStates();
  populateAllCourseSelects();
  updateCharacterUI("character1");
  updateCharacterUI("character2");
  renderRateUpFourStars("character1");
  renderRateUpFourStars("character2");
  updateActiveCharacterBannerView();
  updateWeaponUI();
  updateChronicledUI();
  updateStandardUI();
  const phaseType = currentBannerData.phaseType;
  if (phaseType === "lull" || phaseType === "server_down") {
    applySpecialPhaseBackgrounds();
  }
  updateCurrencyIcons();
  updateQuickstartForms();
  updateThemeButtons();
  updateBackgroundButtons();
  renderAchievementsList();
  renderTravelersFortuneProfile();
  renderInventory();
  renderTeamBuilder();
  renderGoalDisplayList();
  updatePlannerCalculations();
  updateAudioUI();
  updateProfileSettingsUI();
  renderTravelerProfileDisplay();
  renderAccountManagerUI();
}

/**
 * Renders the account management UI in the settings modal.
 * Fills the dropdown with all available accounts and selects the active one.
 */
function renderAccountManagerUI() {
  const accountSelect = document.getElementById("account-select");
  if (!accountSelect) return;

  // 1. Lade die Metadaten aus dem localStorage
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  if (!accountsMeta || !accountsMeta.accounts) {
    console.error("Account metadata not found for UI rendering.");
    return;
  }

  const { accounts, activeAccountId } = accountsMeta;

  // 2. Leere das Dropdown, bevor du es neu befüllst
  accountSelect.innerHTML = "";

  // 3. Erstelle für jeden Account eine <option>
  accounts.forEach((account) => {
    const option = document.createElement("option");
    option.value = account.id;
    option.textContent = account.name;
    // 4. Markiere den aktiven Account als ausgewählt
    if (account.id === activeAccountId) {
      option.selected = true;
    }
    accountSelect.appendChild(option);
  });
}

/**
 * Switches to a different account.
 * Saves the current state, updates the active account ID, and reloads the page.
 * @param {string} newAccountId - The ID of the account to switch to.
 */
function switchAccount(newAccountId) {
  console.log(`--- [SWITCH] Starting switch to: ${newAccountId} ---`);

  // 1. Speichere den Fortschritt des aktuellen Accounts
  saveState();

  // 2. Lade die Metadaten, um sie zu ändern
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  console.log(
    "[SWITCH] Metadaten VOR der Änderung:",
    JSON.stringify(accountsMeta)
  );

  if (
    !accountsMeta ||
    !accountsMeta.accounts.some((acc) => acc.id === newAccountId)
  ) {
    console.error(
      "Attempted to switch to a non-existent account:",
      newAccountId
    );
    return;
  }

  // 3. Setze den neuen Account als aktiv
  accountsMeta.activeAccountId = newAccountId;

  // 4. Speichere die Metadaten
  localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));
  console.log(
    "[SWITCH] Metadaten NACH der Änderung (gespeichert):",
    JSON.stringify(accountsMeta)
  );

  // 5. Lade die Seite neu
  showToast(`Switching account...`, "info", 1500);
  setTimeout(() => window.location.reload(), 500);
}

/**
 * Creates a new, empty account and switches to it.
 */
function createNewAccount() {
  let accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  const newName = prompt(
    "Please enter a name for the new account:",
    `Account ${accountsMeta.accounts.length + 1}`
  );

  if (!newName || newName.trim() === "") {
    return; // User hat abgebrochen oder nichts eingegeben
  }

  const newId = `acc_${Date.now()}`;

  // Füge den neuen Account zu den Metadaten hinzu
  accountsMeta.accounts.push({ id: newId, name: newName.trim() });

  // WICHTIG: Speichere die aktualisierten Metadaten SOFORT.
  // Dadurch weiß die `switchAccount`-Funktion, dass der neue Account existiert.
  localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));

  // Erstelle einen leeren State-Eintrag für den neuen Account
  localStorage.setItem(`account_${newId}`, JSON.stringify(initialState));

  // Wechsle zum neuen Account. Der Reload wird den Rest erledigen.
  switchAccount(newId);
}

/**
 * Renames the currently active account.
 */
function renameCurrentAccount() {
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  const activeAccount = accountsMeta.accounts.find(
    (acc) => acc.id === accountsMeta.activeAccountId
  );

  if (!activeAccount) return;

  const newName = prompt(
    "Enter a new name for this account:",
    activeAccount.name
  );

  if (
    !newName ||
    newName.trim() === "" ||
    newName.trim() === activeAccount.name
  ) {
    return; // Abgebrochen, leer oder kein Unterschied
  }

  activeAccount.name = newName.trim();
  localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));

  // UI sofort aktualisieren (kein Reload nötig)
  renderAccountManagerUI();
  showToast("Account renamed successfully!", "success");
}

/**
 * Deletes the currently active account after confirmation.
 */
function deleteCurrentAccount() {
  let accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  const { activeAccountId, accounts } = accountsMeta;

  if (accounts.length <= 1) {
    showToast("You cannot delete the only account.", "error");
    return;
  }

  const activeAccount = accounts.find((acc) => acc.id === activeAccountId);

  if (
    confirm(
      `ARE YOU ABSOLUTELY SURE?\n\nYou are about to permanently delete the account "${activeAccount.name}". This action cannot be undone.`
    )
  ) {
    // Account aus der Liste entfernen
    accountsMeta.accounts = accounts.filter(
      (acc) => acc.id !== activeAccountId
    );

    // WICHTIG: Speichere die Metadaten, BEVOR du den State löschst.
    localStorage.setItem("accounts_meta", JSON.stringify(accountsMeta));

    // Den State-Eintrag löschen
    localStorage.removeItem(`account_${activeAccountId}`);

    // Zum ersten verbleibenden Account wechseln
    const newActiveId = accountsMeta.accounts[0].id;
    switchAccount(newActiveId);
  }
}

function updateAllButtonStates() {
  const primoPool =
    state.primogems +
    (state.planner.useGenesisCrystals ? state.genesisCrystals : 0);
  const primosAsWishes = Math.floor(primoPool / WISH_COST);
  const intertwinedWishes = state.intertwinedFates + primosAsWishes;
  const acquaintWishes = state.acquaintFates + primosAsWishes;

  document.getElementById("total-event-wishes").textContent = intertwinedWishes;
  document.getElementById("event-fates").textContent = state.intertwinedFates;
  document.getElementById("event-primos").textContent = primosAsWishes;
  document.getElementById("total-standard-wishes").textContent = acquaintWishes;
  document.getElementById("standard-fates").textContent = state.acquaintFates;
  document.getElementById("standard-primos").textContent = primosAsWishes;

  for (const banner of ["character1", "character2", "weapon", "chronicled"]) {
    const wishesEl = document.getElementById(`${banner}-available-wishes`);
    // Sicherheitsprüfung, falls ein Element mal nicht da ist
    if (wishesEl) {
      wishesEl.textContent = intertwinedWishes;
      document.getElementById(`${banner}-available-fates`).textContent =
        state.intertwinedFates;
      document.getElementById(`${banner}-available-primos`).textContent =
        primosAsWishes;
      updateBannerButtonState(banner, intertwinedWishes);
    }
  }

  const standardWishesEl = document.getElementById("standard-available-wishes");
  if (standardWishesEl) {
    standardWishesEl.textContent = acquaintWishes;
    document.getElementById("standard-available-fates").textContent =
      state.acquaintFates;
    document.getElementById("standard-available-primos").textContent =
      primosAsWishes;
    updateBannerButtonState("standard", acquaintWishes);
  }
}

function updateBannerButtonState(banner, availableWishes) {
  let bannerState = state[banner];
  let pity4star = bannerState.pity4star;
  let pity = bannerState.pity;
  if (banner === "character1" || banner === "character2") {
    pity4star = state.characterShared.pity4star;
    pity = state.characterShared.pity;
  }
  const hardPity5star = ["weapon"].includes(banner) ? 80 : 90;
  const add1Button = document.querySelector(
    `[data-action="add-wish"][data-banner="${banner}"][data-amount="1"]`
  );
  const isGuaranteed4 = pity4star >= 9;
  const isGuaranteed5 = pity >= hardPity5star - 1;

  if (add1Button) {
    add1Button.disabled = availableWishes < 1 || isGuaranteed4 || isGuaranteed5;
    add1Button.title = isGuaranteed5
      ? translations[state.language].guaranteed5starTooltip
      : isGuaranteed4
        ? translations[state.language].guaranteed4starTooltip
        : "";
  }

  // Die 10-Pull-Buttons werden jetzt nur noch gesperrt, wenn die Wünsche nicht reichen.
  const tenPullButton = document.querySelector(
    `[data-action="show-ten-pull-modal"][data-banner="${banner}"]`
  );
  if (tenPullButton) {
    tenPullButton.disabled = availableWishes < 10;
  }

  const bannerCard = document.getElementById(`${banner}-banner-card`);
  bannerCard
    .querySelectorAll(".btn-info, .btn-success, .btn-secondary")
    .forEach((btn) => {
      if (btn.closest(".space-y-2") || btn.closest(".flex")) {
        let shouldBeDisabled = false;

        // Fix: Nur Log-Buttons sperren, wenn keine Wünsche da sind.
        // Andere Buttons (Save Pity, Next Banner etc.) sollen immer klickbar sein.
        if (btn.dataset.action === "show-resolve-modal") {
          shouldBeDisabled = availableWishes < 1;
        }

        if (
          btn.dataset.action === "show-resolve-modal" &&
          btn.dataset.rarity === "4"
        ) {
          if (isGuaranteed5) {
            shouldBeDisabled = true;
            btn.title = translations[state.language].guaranteed5starTooltip;
          } else {
            btn.title = "";
          }
        }
        btn.disabled = shouldBeDisabled;
      }
    });
}

function updateBannerUI(banner, sharedData = null) {
  const bannerState = sharedData || state[banner];
  const pityEl = document.getElementById(`${banner}-pity`);
  const oldPity = parseInt(pityEl.textContent, 10);
  if (oldPity !== bannerState.pity)
    animateCounter(pityEl, oldPity, bannerState.pity);
  const pity4El = document.getElementById(`${banner}-pity4star`);
  const old4Pity = parseInt(pity4El.textContent, 10);
  if (old4Pity !== bannerState.pity4star)
    animateCounter(pity4El, old4Pity, bannerState.pity4star);
  const softPity5Star = ["weapon"].includes(banner) ? 63 : 74;
  const hardPity5Star = ["weapon"].includes(banner) ? 80 : 90;
  updatePityColor(pityEl, bannerState.pity, softPity5Star, hardPity5Star);
  document.getElementById(`${banner}-chance`).textContent =
    getProbability(bannerState.pity, banner).toFixed(2) + "%";
  renderHistory(banner);
  renderCharts(banner);
  updateFilterButtons(banner);
  updateWishPriorityButtons(banner);
}
function updateCharacterUI(bannerId) {
  const sharedState = state.characterShared;
  const bannerState = state[bannerId];
  const lang = state.language;

  const bannerCard = document.getElementById(`${bannerId}-banner-card`);

  const bannerInfo = currentBannerData[bannerId];
  const featuredItemName = bannerInfo?.fiveStar || "";
  const bannerNameKey = bannerInfo?.bannerNameKey || "";

  const switchButton = document.getElementById(`${bannerId}-switch-btn`);
  if (switchButton && bannerNameKey && translations[lang][bannerNameKey]) {
    switchButton.textContent = translations[lang][bannerNameKey];
  }

  updateBannerUI(bannerId, sharedState);

  const statusEl = document.getElementById(`${bannerId}-status`);
  if (sharedState.isGuaranteed) {
    statusEl.textContent = translations[lang].statusGuaranteed;
    statusEl.className =
      "mt-2 px-3 py-1 text-sm rounded-full inline-block guaranteed";
  } else {
    statusEl.textContent = translations[lang].status5050;
    statusEl.className =
      "mt-2 px-3 py-1 text-sm rounded-full inline-block fifty-fifty";
  }

  const headerEl = document.querySelector(`#${bannerId}-banner-card h2`);
  if (headerEl && featuredItemName) {
    if (bannerId === "character1" && translations[lang].characterBannerPhase1) {
      headerEl.textContent =
        translations[lang].characterBannerPhase1(featuredItemName);
    } else if (
      bannerId === "character2" &&
      translations[lang].characterBannerPhase2
    ) {
      headerEl.textContent =
        translations[lang].characterBannerPhase2(featuredItemName);
    }
  }

  const displayContainer = document.getElementById(
    `${bannerId}-featured-display`
  );
  if (featuredItemName && displayContainer) {
    const itemDetails = findItem(featuredItemName);
    if (itemDetails) {
      const iconUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );
      const characterId = itemDetails.name.toLowerCase().replace(/ /g, "_");
      displayContainer.innerHTML = `
        <div data-character-id="${characterId}" class="flex items-center gap-2 cursor-pointer">
          <img src="${iconUrl}" alt="${itemDetails.name}" class="h-8 w-8 rounded-full border-2 border-gold-dark" onerror="this.style.display='none'">
          <span class="font-bold text-lg text-gold-light">${itemDetails.name}</span>
        </div>
      `;
    }
  }

  if (featuredItemName) {
    const itemDetails = findItem(featuredItemName);
    if (itemDetails) {
      const wishImageUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "wish"
      );
      const cardImageUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "card"
      );
      const gradient = "linear-gradient(rgba(47, 52, 70, 0.85), rgba(47, 52, 70, 0.98))";

      bannerCard.style.backgroundImage = `${gradient}, url('${cardImageUrl}')`;

      const img = new Image();
      img.onload = () => {
        // Ein echtes Wish-Bild hat eine nennenswerte Breite (Platzhalter sind oft winzig)
        if (img.naturalWidth > 50) {
          if (state[bannerId] && state[bannerId].featuredItem === featuredItemName) {
            bannerCard.style.backgroundImage = `${gradient}, url('${wishImageUrl}')`;
          }
        }
      };
      img.src = wishImageUrl;
    }
  } else {
    bannerCard.style.backgroundImage = "none";
  }

  const existingContainer = bannerCard.querySelector(
    ".banner-card-icon-container"
  );
  if (existingContainer) {
    existingContainer.remove();
  }

  // 2. Hole die vollen Details des Charakters, um auf den Waffentyp zugreifen zu können
  const itemDetails = findItem(featuredItemName);

  if (itemDetails) {
    const element = itemDetails.element;
    const weaponType = itemDetails.weaponType;
    const elementIconUrl = ELEMENT_ICONS[element];
    const weaponIconUrl = WEAPON_TYPE_ICONS[weaponType];

    // 3. Erstelle den Container nur, wenn wir beide Icons haben
    if (elementIconUrl && weaponIconUrl) {
      const iconContainer = document.createElement("div");
      iconContainer.className = "banner-card-icon-container";

      // Erstelle das Waffen-Icon
      const weaponIconEl = document.createElement("img");
      weaponIconEl.className = "banner-card-icon";
      weaponIconEl.src = weaponIconUrl;
      weaponIconEl.alt = `${weaponType} Icon`;
      weaponIconEl.onerror = () => {
        weaponIconEl.style.display = "none";
      };

      // Erstelle das Element-Icon
      const elementIconEl = document.createElement("img");
      elementIconEl.className = "banner-card-icon";
      elementIconEl.src = elementIconUrl;
      elementIconEl.alt = `${element} Element`;
      elementIconEl.onerror = () => {
        elementIconEl.style.display = "none";
      };

      // Füge beide Icons zum Container hinzu (Waffe zuerst, damit es links steht)
      iconContainer.appendChild(weaponIconEl);
      iconContainer.appendChild(elementIconEl);

      // Füge den kompletten Container zur Banner-Karte hinzu
      bannerCard.prepend(iconContainer);
    }
  }
  const status4starEl = document.getElementById(`${bannerId}-4star-status`);
  if (status4starEl) {
    if (state.characterShared.is4StarGuaranteed) {
      status4starEl.innerHTML = `Next 4★: <span class="font-bold text-green-400">100% Rate-Up</span>`;
    } else {
      status4starEl.innerHTML = `Next 4★: <span class="font-bold text-yellow-400">50% Rate-Up</span>`;
    }
  }

  // --- Move Button Visibility Logic ---
  const moveBtn = document.getElementById(`move-btn-${bannerId}`);
  if (moveBtn) {
    const history = state[bannerId].history;
    const hasHistory = history && history.length > 0;

    let isValid = false;
    if (hasHistory) {
      const lastItem = history[0];
      // Wir zeigen den Button nur an, wenn der letzte Eintrag ein valides Datum UND einen Banner-Namen hat.
      // Das dient als Sicherheitsnetz gegen alte/Legacy Daten.
      if (lastItem && lastItem.date && lastItem.banner) {
        isValid = true;
      }
    }

    if (isValid) {
      moveBtn.style.display = "block";
    } else {
      moveBtn.style.display = "none";
    }
  }
  renderBannerStats(bannerId);
  renderRateUpFourStars(bannerId);
  handleSpecialBannerPhase(bannerId);
  updateCStateDisplay(bannerId);
}

function updateWeaponUI() {
  updateBannerUI("weapon");

  const bannerCard = document.getElementById("weapon-banner-card");
  const featuredDisplay = document.getElementById("weapon-featured-display");

  // 1. Featured Items anzeigen (unverändert)
  featuredDisplay.innerHTML = state.weapon.featuredItems
    .map((name) => {
      const itemDetails = findItem(name);
      if (!itemDetails) return "";
      const iconUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );
      return `
        <div class="flex items-center gap-2 p-2 rounded bg-black bg-opacity-20">
          <img src="${iconUrl}" alt="${name}" class="h-8 w-8 rounded-full border-2 border-gold-dark" onerror="this.style.display='none'">
          <span class="font-bold text-sm text-gold-light">${name}</span>
        </div>
      `;
    })
    .join("");

  // 2. 4-Sterne-Rate-Up anzeigen (unverändert)
  const fourStarContainer = document.getElementById(
    "weapon-rateup-4star-icons"
  );
  const weaponFourStars = currentBannerData.weapon?.fourStars || [];
  fourStarContainer.innerHTML = weaponFourStars
    .map((name) => {
      const itemDetails = findItem(name);
      if (!itemDetails) return "";
      const iconUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );
      return `
      <div class="relative group h-10 w-10">
        <div class="absolute inset-0 rounded-full border-2 border-purple-400 bg-genshin-bg"></div>
        <img src="${iconUrl}" alt="${name}" class="h-10 w-10 rounded-full border-2 border-purple-400 bg-genshin-bg transition-transform group-hover:scale-110" style="position: relative; z-index: 1;" onerror="this.style.display='none'">
        <span class="absolute bottom-full mb-2 w-max px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -translate-x-1/2 left-1/2">${name}</span>
      </div>
    `;
    })
    .join("");

  // 3. Status-Anzeige (unverändert)
  const statusEl = document.getElementById("weapon-status");
  if (state.weapon.isGuaranteed) {
    statusEl.textContent = translations[state.language].statusGuaranteed;
    statusEl.className =
      "mt-2 px-3 py-1 text-sm rounded-full inline-block guaranteed";
  } else {
    statusEl.textContent = "75/25";
    statusEl.className =
      "mt-2 px-3 py-1 text-sm rounded-full inline-block fifty-fifty";
  }

  // --- ★★★ HIER IST DIE KORREKTUR ★★★ ---

  // 4. Entferne JEDES alte Icon, egal welchen Typs. Das räumt alles auf.
  const existingIconContainer = bannerCard.querySelector(
    ".banner-card-icon-container"
  );
  if (existingIconContainer) existingIconContainer.remove();
  const existingSingleIcon = bannerCard.querySelector(
    ".banner-card-element-icon"
  );
  if (existingSingleIcon) existingSingleIcon.remove();

  // 5. Setze den Hintergrund intelligent
  const courseItemName = state.weapon.courseSetOn;
  // Nimm das Kurs-Item als Hintergrund, WENN es gesetzt ist.
  // Ansonsten nimm das ERSTE featured Item als Standard-Hintergrund.
  const backgroundItemName =
    courseItemName || state.weapon.featuredItems[0] || "";

  if (backgroundItemName) {
    const itemDetails = findItem(backgroundItemName);
    if (itemDetails) {
      // Wichtig: Wir nutzen die "icon"-Variante, die das volle Waffen-Artwork ist.
      const backgroundUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );
      bannerCard.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.8), rgba(47, 52, 70, 0.98)), url('${backgroundUrl}')`;
    }
  } else {
    bannerCard.style.backgroundImage = "none";
  }

  // --- ★★★ ENDE DER KORREKTUR ★★★ ---

  // 6. Restliche UI-Updates (unverändert)
  const status4starEl = document.getElementById("weapon-4star-status");
  if (status4starEl) {
    if (state.weapon.is4StarGuaranteed) {
      status4starEl.innerHTML = `Next 4★: <span class="font-bold text-green-400">100% Rate-Up</span>`;
    } else {
      status4starEl.innerHTML = `Next 4★: <span class="font-bold text-yellow-400">75% Rate-Up</span>`;
    }
  }

  renderBannerStats("weapon");
  handleSpecialBannerPhase("weapon");
}

function updateChronicledUI() {
  const interactiveContent = document.getElementById(
    "chronicled-interactive-content"
  );
  const inactiveInfo = document.getElementById("chronicled-inactive-info");
  const bannerCard = document.getElementById("chronicled-banner-card");

  const isSpecialPhase =
    currentBannerData.phaseType === "lull" ||
    currentBannerData.phaseType === "server_down";
  const isChronicledActive =
    !isSpecialPhase &&
    currentBannerData.chronicled &&
    currentBannerData.chronicled.fiveStars?.characters?.[0] !== "";

  // --- KORREKTUR: Wichtige UI-Updates werden jetzt IMMER ausgeführt ---
  // updateBannerUI ist verantwortlich für Pity-Zahlen, History und Charts.
  // Das wollen wir immer sehen.
  updateBannerUI("chronicled");

  // Schalte die Ansichten für den interaktiven Teil um
  interactiveContent.classList.toggle("hidden", !isChronicledActive);
  inactiveInfo.classList.toggle("hidden", isChronicledActive);

  // --- NEU: Setze den Banner-Namen basierend auf der Region ---
  const headerEl = bannerCard.querySelector("h2");
  if (headerEl) {
    const lang = state.language;
    const region = currentBannerData.chronicled?.region;
    const baseName = translations[lang].chronicledBanner;

    if (region) {
      // Fügt die Region vor dem Basisnamen ein (z.B. "Mondstadt Kronikgebet")
      headerEl.textContent = `${region} ${baseName}`;
    } else {
      headerEl.textContent = baseName;
    }
  }

  // Führe nur noch die Logik aus, die wirklich nur für einen AKTIVEN Banner relevant ist
  if (isChronicledActive) {
    // Selektoren, die nur hier gebraucht werden
    const fiveStarCharContainer = document.getElementById(
      "chronicled-featured-5star-characters"
    );
    const fiveStarWepContainer = document.getElementById(
      "chronicled-featured-5star-weapons"
    );
    const fourStarCharContainer = document.getElementById(
      "chronicled-featured-4star-characters"
    );
    const fourStarWepContainer = document.getElementById(
      "chronicled-featured-4star-weapons"
    );

    const renderItems = (container, itemNames, rarity) => {
      if (!container || !itemNames || itemNames.length === 0) {
        if (container) container.innerHTML = "";
        return;
      }
      container.innerHTML = itemNames
        .map((name) => {
          const itemDetails = findItem(name);
          if (!itemDetails) return "";
          const iconUrl = getFandomImageUrl(
            itemDetails.type,
            itemDetails.name,
            "icon"
          );
          let extraAttributes = "";
          if (itemDetails.type === "character") {
            const characterId = itemDetails.name
              .toLowerCase()
              .replace(/ /g, "_");
            extraAttributes = `data-character-id="${characterId}" style="cursor: pointer;"`;
          }
          return `
            <div class="item rarity-${rarity} group" ${extraAttributes}>
              <img src="${iconUrl}" alt="${name}" class="item-icon" onerror="this.style.display='none'">
              <div class="item-tooltip">${name}</div>
            </div>
          `;
        })
        .join("");
    };

    // Rendere die vorgestellten Items
    renderItems(
      fiveStarCharContainer,
      currentBannerData.chronicled.fiveStars.characters,
      5
    );
    renderItems(
      fiveStarWepContainer,
      currentBannerData.chronicled.fiveStars.weapons,
      5
    );
    renderItems(
      fourStarCharContainer,
      currentBannerData.chronicled.fourStars.characters,
      4
    );
    renderItems(
      fourStarWepContainer,
      currentBannerData.chronicled.fourStars.weapons,
      4
    );

    // Aktualisiere Status und Hintergrundbild
    const statusEl = document.getElementById("chronicled-status");
    if (state.chronicled.isGuaranteed) {
      statusEl.textContent = translations[state.language].statusGuaranteed;
      statusEl.className =
        "mt-2 px-3 py-1 text-sm rounded-full inline-block guaranteed";
    } else {
      statusEl.textContent = "50/50";
      statusEl.className =
        "mt-2 px-3 py-1 text-sm rounded-full inline-block fifty-fifty";
    }

    const courseItemName = state.chronicled.courseSetOn;
    if (courseItemName) {
      const itemDetails = findItem(courseItemName);
      if (itemDetails) {
        if (itemDetails.type === "character") {
          const wishImageUrl = getFandomImageUrl(itemDetails.type, itemDetails.name, "wish");
          const cardImageUrl = getFandomImageUrl(itemDetails.type, itemDetails.name, "card");
          const gradient = "linear-gradient(rgba(47, 52, 70, 0.84), rgba(47, 52, 70, 0.97))";

          bannerCard.style.backgroundImage = `${gradient}, url('${cardImageUrl}')`;

          const img = new Image();
          img.onload = () => {
            if (img.naturalWidth > 50) {
              if (state.chronicled && state.chronicled.courseSetOn === courseItemName) {
                bannerCard.style.backgroundImage = `${gradient}, url('${wishImageUrl}')`;
              }
            }
          };
          img.src = wishImageUrl;
        } else {
          const imageUrl = getFandomImageUrl(itemDetails.type, itemDetails.name, "icon");
          bannerCard.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.84), rgba(47, 52, 70, 0.97)), url('${imageUrl}')`;
        }
      }
    } else {
      bannerCard.style.backgroundImage = "none";
    }
  } else {
    // Wenn der Banner inaktiv ist, setze den Hintergrund explizit zurück
    bannerCard.style.backgroundImage = "none";
  }

  // Diese Funktionen werden immer am Ende ausgeführt
  renderBannerStats("chronicled");
  handleSpecialBannerPhase("chronicled");
}

function updateStandardUI() {
  updateBannerUI("standard");
  renderBannerStats("standard");
  handleSpecialBannerPhase("standard");
  const bannerCard = document.getElementById("standard-banner-card");
  const lastNotablePull = state.standard.history[0];
  if (lastNotablePull) {
    const itemDetails = findItem(lastNotablePull.name);
    if (itemDetails) {
      const imageVariant = itemDetails.type === "character" ? "card" : "icon";
      const imageUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        imageVariant
      );
      bannerCard.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.84), rgba(47, 52, 70, 0.97)), url('${imageUrl}')`;
    } else {
      bannerCard.style.backgroundImage = "none";
    }
  } else {
    bannerCard.style.backgroundImage = "none";
  }
}

function renderRateUpFourStars(bannerId) {
  const container = document.getElementById(`${bannerId}-rateup-4star-icons`);
  if (!container) return;

  const fourStarNames = currentBannerData[bannerId]?.fourStars;
  if (!fourStarNames || fourStarNames.length === 0) {
    container.innerHTML = "";
    return;
  }

  container.innerHTML = fourStarNames
    .map((name) => {
      const itemDetails = findItem(name);
      if (!itemDetails) return "";

      const iconUrl = getFandomImageUrl(
        itemDetails.type,
        itemDetails.name,
        "icon"
      );

      const characterIdAttr =
        itemDetails?.type === "character"
          ? `data-character-id="${itemDetails.name
            .toLowerCase()
            .replace(/ /g, "_")}" style="cursor: pointer;"`
          : "";

      return `
      <div class="relative group h-10 w-10">
        <div class="absolute inset-0 z-0 rounded-full border-2 border-purple-400 bg-genshin-bg"></div>
        <img 
          src="${iconUrl}" 
          alt="${name}" 
          ${characterIdAttr}
          class="h-10 w-10 rounded-full border-2 border-purple-400 bg-genshin-bg transition-transform group-hover:scale-110 relative z-10"
          onerror="this.style.display='none'"
        >
        <span class="absolute bottom-full mb-2 w-max px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none -translate-x-1/2 left-1/2">
          ${name}
        </span>
      </div>
    `;
    })
    .join("");
}
function populateAllCourseSelects() {
  const weaponOptions = itemsDB
    .filter((item) => item.type === "weapon" && item.pool === "limited")
    .sort((a, b) => a.name.localeCompare(b.name));

  const courseOptions = (state.weapon.featuredItems || [])
    .map((name) => findItem(name))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  createCustomSelect(
    "weapon-course-select-wrapper",
    courseOptions,
    state.weapon.courseSetOn,
    (newValue) => {
      if (
        state.weapon.isGuaranteed &&
        state.weapon.courseSetOn &&
        state.weapon.courseSetOn !== newValue
      ) {
        showConfirmationModal("set-course", {
          banner: "weapon",
          itemName: newValue,
        });
      } else {
        state.weapon.courseSetOn = newValue;
        saveAndRefresh();
      }
    }
  );

  const chronicled5StarChars =
    currentBannerData.chronicled?.fiveStars?.characters || [];
  const chronicled5StarWeps =
    currentBannerData.chronicled?.fiveStars?.weapons || [];
  const chronicledCourseOptions = [
    ...chronicled5StarChars,
    ...chronicled5StarWeps,
  ]
    .map((name) => findItem(name))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));

  createCustomSelect(
    "chronicled-course-select-wrapper",
    chronicledCourseOptions,
    state.chronicled.courseSetOn,
    (newValue) => {
      if (
        state.chronicled.isGuaranteed &&
        state.chronicled.courseSetOn &&
        state.chronicled.courseSetOn !== newValue
      ) {
        showConfirmationModal("set-course", {
          banner: "chronicled",
          itemName: newValue,
        });
      } else {
        state.chronicled.courseSetOn = newValue;
        saveAndRefresh();
      }
    }
  );
}

function updateWishPriorityButtons(banner) {
  const priority = state[banner].wishPriority;
  document
    .getElementById(`${banner}-prio-fates`)
    .classList.toggle("active", priority === "fates");
  document
    .getElementById(`${banner}-prio-primos`)
    .classList.toggle("active", priority === "primos");
}

function updatePityColor(element, pity, softPityStart, hardPity) {
  element.classList.remove("soft-pity-active", "hard-pity-active");
  if (pity >= hardPity) element.classList.add("hard-pity-active");
  else if (pity >= softPityStart) element.classList.add("soft-pity-active");
}

function updateFilterButtons(banner) {
  const filter = state[banner].historyFilter;
  ["all", 5, 4].forEach((f) => {
    const btn = document.getElementById(`${banner}-filter-${f}`);
    if (btn) btn.classList.toggle("active", f.toString() === filter.toString());
  });
}

function updateCurrencyIcons() {
  for (const [key, url] of Object.entries(CURRENCY_ICONS)) {
    const imgEl = document.getElementById(`${key}-icon`);
    if (imgEl) {
      imgEl.src = url;
    }
  }
}

function addWishes(banner, amount) {
  if (!deductResources(banner, amount)) return;

  if (banner === "character1" || banner === "character2") {
    state[banner].totalPulls += amount;
  }

  if (banner === "character1" || banner === "character2") {
    state.characterShared.pity += amount;
    state.characterShared.pity4star += amount;
  } else {
    state[banner].pity += amount;
    state[banner].pity4star += amount;
  }
  saveAndRefresh();
}

function updateStarglitterDisplay() {
  const starglitterFates = Math.floor(state.starglitter / 5);
  const starglitterEquivEl = document.getElementById("starglitter-equivalent");
  if (starglitterEquivEl) {
    starglitterEquivEl.textContent = translations[state.language].starglitterEquivalent.replace(
      "{count}",
      starglitterFates
    );
  }
}

function updateGenesisCrystalDisplay() {
  const genesisFates = Math.floor(state.genesisCrystals / 160);
  const genesisEquivEl = document.getElementById("genesis-equivalent");
  if (genesisEquivEl) {
    genesisEquivEl.textContent = translations[state.language].genesisEquivalent.replace(
      "{count}",
      genesisFates
    );
  }
}

function updatePrimogemDisplay() {
  const primogemFates = Math.floor(state.primogems / 160);
  const primogemEquivEl = document.getElementById("primogem-equivalent");
  if (primogemEquivEl) {
    primogemEquivEl.textContent = translations[state.language].primogemEquivalent.replace(
      "{count}",
      primogemFates
    );
  }
}

function updateResources() {
  state.primogems = parseInt(dom.primogemInput.value, 10) || 0;
  state.genesisCrystals = parseInt(dom.genesisInput.value, 10) || 0;
  state.starglitter = parseInt(dom.starglitterInput.value, 10) || 0;
  state.intertwinedFates = parseInt(dom.intertwinedInput.value, 10) || 0;
  state.acquaintFates = parseInt(dom.acquaintInput.value, 10) || 0;
  state.planner.welkin = dom.welkinCheckbox.checked;
  state.planner.battlepass = dom.bpCheckbox.checked;
  state.planner.useGenesisCrystals = dom.useGenesisCheckbox.checked;

  updateStarglitterDisplay();
  updateGenesisCrystalDisplay();
  updatePrimogemDisplay();

  updateAllButtonStates(); // Aktualisiert die verfügbaren Wünsche
  updatePlannerCalculations(); // Berechnet den Planner neu
  saveState(); // Speichert den neuen Zustan
}

/**
 * Füllt die Dropdowns und ehrt gespeicherte Ziele, auch wenn sie nicht mehr aktiv sind.
 * @param {HTMLSelectElement} bannerSelect - Das <select>-Element für den Bannertyp.
 * @param {object|null} goalData - Die gespeicherten Daten zur Wiederherstellung.
 */
function populateGoalItemDropdowns(bannerSelect, goalData = null) {
  const goalItem = bannerSelect.closest(".goal-edit-item");
  const itemSelect = goalItem.querySelector(".goal-target-item");
  const levelSelect = goalItem.querySelector(".goal-target-level");
  const bannerType = bannerSelect.value;

  itemSelect.innerHTML = "";
  levelSelect.innerHTML = "";

  const currentOptions = new Set();
  const upcomingOptions = new Set();

  // --- Sammle aktuelle und zukünftige Ziele (Logik bleibt gleich) ---
  const currentIndex = BANNER_ROTATIONS.findIndex(
    (r) => r.endDate === currentBannerData.endDate
  );
  const upcomingRotation = BANNER_ROTATIONS.find(
    (rotation, index) => index > currentIndex && !rotation.phaseType
  );

  switch (bannerType) {
    case "character1":
    case "character2":
      const currentChar = currentBannerData[bannerType]?.fiveStar;
      if (currentChar) currentOptions.add(currentChar);
      if (upcomingRotation) {
        const upcomingChar1 = upcomingRotation.character1?.fiveStar;
        const upcomingChar2 = upcomingRotation.character2?.fiveStar;
        if (upcomingChar1) upcomingOptions.add(upcomingChar1);
        if (upcomingChar2) upcomingOptions.add(upcomingChar2);
      }
      break;
    case "weapon":
      (currentBannerData.weapon?.fiveStars || []).forEach((item) =>
        currentOptions.add(item)
      );
      if (upcomingRotation) {
        (upcomingRotation.weapon?.fiveStars || []).forEach((item) =>
          upcomingOptions.add(item)
        );
      }
      break;
    case "chronicled":
      const currentChars =
        currentBannerData.chronicled?.fiveStars?.characters || [];
      const currentWeps =
        currentBannerData.chronicled?.fiveStars?.weapons || [];
      [...currentChars, ...currentWeps].forEach((item) =>
        currentOptions.add(item)
      );
      if (upcomingRotation) {
        const upcomingChars =
          upcomingRotation.chronicled?.fiveStars?.characters || [];
        const upcomingWeps =
          upcomingRotation.chronicled?.fiveStars?.weapons || [];
        [...upcomingChars, ...upcomingWeps].forEach((item) =>
          upcomingOptions.add(item)
        );
      }
      break;
  }

  let optionsHTML = "";
  const savedTarget = goalData ? goalData.target : null;

  // Füge das gespeicherte Ziel hinzu, falls es nicht in den aktuellen/kommenden Listen ist.
  if (
    savedTarget &&
    !currentOptions.has(savedTarget) &&
    !upcomingOptions.has(savedTarget)
  ) {
    optionsHTML += `<optgroup label="Unavailable Banner">`;
    optionsHTML += `<option value="${savedTarget}">${savedTarget} (Ended)</option>`;
    optionsHTML += `</optgroup>`;
  }

  // Füge die restlichen Optionen hinzu
  if (currentOptions.size > 0) {
    optionsHTML += `<optgroup label="Current Banner">`;
    currentOptions.forEach((name) => {
      optionsHTML += `<option value="${name}">${name}</option>`;
    });
    optionsHTML += `</optgroup>`;
  }
  if (upcomingOptions.size > 0) {
    optionsHTML += `<optgroup label="Upcoming Banner">`;
    upcomingOptions.forEach((name) => {
      if (!currentOptions.has(name)) {
        optionsHTML += `<option value="${name}">${name}</option>`;
      }
    });
    optionsHTML += `</optgroup>`;
  }

  if (optionsHTML === "") {
    itemSelect.innerHTML = `<option value="">No Target Available</option>`;
  } else {
    itemSelect.innerHTML = optionsHTML;
  }

  const selectedItemName = goalData ? goalData.target : itemSelect.value;
  const itemDetails = findItem(selectedItemName);

  levelSelect.innerHTML = ""; // Wichtig: Zuerst leeren
  if (itemDetails?.type === "character") {
    for (let i = 0; i <= 6; i++) {
      // Der value ist die Gesamtanzahl an Kopien (C0=1, C1=2, ... C6=7)
      levelSelect.innerHTML += `<option value="${i + 1}">C${i}</option>`;
    }
  } else {
    // Waffen oder Fallback
    for (let i = 1; i <= 5; i++) {
      levelSelect.innerHTML += `<option value="${i}">R${i}</option>`;
    }
  }

  if (goalData) {
    itemSelect.value = goalData.target;
    levelSelect.value = goalData.copies;
  }

  itemSelect.addEventListener("change", () => {
    const newItemDetails = findItem(itemSelect.value);
    levelSelect.innerHTML = "";
    if (newItemDetails?.type === "character") {
      for (let i = 0; i <= 6; i++) {
        levelSelect.innerHTML += `<option value="${i + 1}">C${i}</option>`;
      }
    } else {
      for (let i = 1; i <= 5; i++) {
        levelSelect.innerHTML += `<option value="${i}">R${i}</option>`;
      }
    }
  });
}

/**
 * Führt die reine Berechnung für den Goal Planner durch.
 * NEUE VERSION: Berücksichtigt den Besitz des Spielers aus state.inventory.
 * @returns {object} Ein Objekt mit den Berechnungsergebnissen.
 */
function getPlannerCalculation() {
  const goals = state.planner.goals || [];

  const PITY_VALUES = {
    LUCKY: 35,
    AVERAGE: 63,
    HARD_CHAR: 90,
    HARD_WEAPON: 80,
  };

  let results = { lucky: 0, average: 0, worst: 0 };

  const simStates = {
    lucky: {
      character: { ...state.characterShared },
      weapon: { ...state.weapon },
      chronicled: { ...state.chronicled },
    },
    average: {
      character: { ...state.characterShared },
      weapon: { ...state.weapon },
      chronicled: { ...state.chronicled },
    },
    worst: {
      character: { ...state.characterShared },
      weapon: { ...state.weapon },
      chronicled: { ...state.chronicled },
    },
  };

  const getSimState = (scenario, bannerKey) => {
    return bannerKey.startsWith("character")
      ? simStates[scenario].character
      : simStates[scenario][bannerKey];
  };

  const currentCState = state.characterShared.cState || 0;

  for (const goal of goals) {
    if (!goal.target) continue;

    const itemDetails = findItem(goal.target);
    if (!itemDetails) continue;

    // --- ★★★ DIE NEUE KERNLOGIK STARTET HIER ★★★ ---
    let copiesNeeded = 0;
    const targetCopies = parseInt(goal.copies, 10) || 1;

    if (itemDetails.type === "character") {
      const ownedChar = state.inventory.characters[goal.target];
      const currentConstellation = ownedChar ? ownedChar.constellation : -1; // -1 bedeutet, nicht im Besitz
      const targetConstellation = targetCopies - 1;
      copiesNeeded = Math.max(0, targetConstellation - currentConstellation);
    } else if (itemDetails.type === "weapon") {
      // Finde die Waffe mit dem höchsten Refinement im Inventar
      const highestRefinement = state.inventory.weapons
        .filter((w) => w.name === goal.target)
        .reduce((max, w) => Math.max(max, w.refinement), 0);

      const targetRefinement = targetCopies;
      // Um R5 zu erreichen (5 Kopien), von R1 (1 Kopie), braucht man 4 neue.
      copiesNeeded = Math.max(0, targetRefinement - highestRefinement);
    }
    // --- ★★★ ENDE DER NEUEN KERNLOGIK ★★★ ---

    if (copiesNeeded === 0) continue; // Wenn Ziel schon erreicht, überspringe dieses Ziel.

    const bannerKey = goal.banner.startsWith("character")
      ? "character"
      : goal.banner;
    if (!simStates.lucky[bannerKey]) continue;

    const hardPity =
      bannerKey === "weapon" ? PITY_VALUES.HARD_WEAPON : PITY_VALUES.HARD_CHAR;

    for (let i = 0; i < copiesNeeded; i++) {
      // Der Rest der Simulationslogik bleibt exakt gleich...
      // --- WORST CASE ---
      const worstState = getSimState("worst", bannerKey);
      if (!worstState.isGuaranteed) {
        results.worst += hardPity - worstState.pity;
        worstState.pity = 0;
        worstState.isGuaranteed = true;
      }
      results.worst += hardPity - worstState.pity;
      worstState.pity = 0;
      worstState.isGuaranteed = false;

      // --- LUCKY CASE ---
      const luckyState = getSimState("lucky", bannerKey);
      results.lucky += Math.max(0, PITY_VALUES.LUCKY - luckyState.pity);
      luckyState.pity = 0;
      luckyState.isGuaranteed = false;

      // --- AVERAGE CASE ---
      const avgState = getSimState("average", bannerKey);
      let isGuaranteedWin =
        avgState.isGuaranteed ||
        (bannerKey === "character" && avgState.cState === 3);
      if (isGuaranteedWin) {
        results.average += Math.max(0, PITY_VALUES.AVERAGE - avgState.pity);
        avgState.pity = 0;
        avgState.isGuaranteed = false;
        if (bannerKey === "character") avgState.cState = 1;
      } else {
        let lossProbability = 0.5;
        if (bannerKey === "weapon") lossProbability = 0.25;
        results.average += Math.max(0, PITY_VALUES.AVERAGE - avgState.pity);
        results.average += lossProbability * PITY_VALUES.AVERAGE;
        avgState.pity = 0;
      }
    }
  }

  // Der Rest der Funktion bleibt unverändert...
  results.lucky = Math.ceil(results.lucky);
  results.average = Math.ceil(results.average);
  results.worst = Math.ceil(results.worst);

  const primoPool =
    state.primogems +
    (state.planner.useGenesisCrystals ? state.genesisCrystals : 0);
  const currentWishes =
    state.intertwinedFates + Math.floor(primoPool / WISH_COST);
  const shortfall = Math.max(0, results.average - currentWishes);

  let dailyPrimos = 60;
  if (state.planner.welkin) dailyPrimos += 90;
  if (state.planner.battlepass) dailyPrimos += Math.round(1320 / 42);

  const primosShortfall = shortfall * WISH_COST;
  const daysNeeded =
    primosShortfall > 0 && dailyPrimos > 0
      ? Math.ceil(primosShortfall / dailyPrimos)
      : 0;

  return {
    lucky: results.lucky,
    average: results.average,
    worst: results.worst,
    shortfall,
    daysNeeded,
  };
}

/**
 * Aktualisiert die Anzeige der Planner-Ergebnisse im DOM für alle drei Szenarien.
 * @param {object} results - Das Ergebnisobjekt von getPlannerCalculation.
 */
function renderPlannerResults(results) {
  // Die drei Hauptszenarien
  document.getElementById("goal-wishes-lucky").textContent = results.lucky;
  document.getElementById("goal-wishes-average").textContent = results.average;
  document.getElementById("goal-wishes-worst").textContent = results.worst;

  // Die abgeleiteten Berechnungen
  document.getElementById("goal-shortfall").textContent = results.shortfall;
  document.getElementById("goal-days-needed").textContent = results.daysNeeded;
}

function triggerFiveStarAnimation() {
  const body = document.body;
  body.classList.remove("five-star-flash-animation");
  setTimeout(() => {
    body.classList.add("five-star-flash-animation");
  }, 10);
  setTimeout(() => {
    body.classList.remove("five-star-flash-animation");
  }, 1010);
}

async function resolveSinglePull(banner, rarity, name, outcome, wishes) {
  if (!deductResources(banner, wishes)) return;

  if (banner === "character1" || banner === "character2") {
    state[banner].totalPulls += wishes;
  }

  const isCharacterBanner = banner === "character1" || banner === "character2";
  const targetState = isCharacterBanner ? state.characterShared : state[banner];
  const bannerState = state[banner];
  targetState.pity += wishes;
  targetState.pity4star += wishes;

  const pity = rarity === 5 ? targetState.pity : targetState.pity4star;
  const pullId = Date.now();
  const now = new Date();

  let friendlyBannerName = banner;
  const lang = state.language;
  if (banner === "character1" || banner === "character2") {
    const bannerData = currentBannerData[banner];
    if (bannerData) {
      const key = bannerData.bannerNameKey;
      if (key && translations[lang][key]) {
        friendlyBannerName = translations[lang][key];
      } else if (bannerData.fiveStar) {
        friendlyBannerName = `Banner ${bannerData.fiveStar}`;
      }
    }
  } else if (banner === "weapon") {
    friendlyBannerName = translations[lang].weaponBanner;
  } else if (banner === "chronicled") {
    const region = currentBannerData.chronicled?.region;
    const baseName = translations[lang].chronicledBanner;
    friendlyBannerName = region ? `${region} ${baseName}` : baseName;
  } else if (banner === "standard") {
    friendlyBannerName = translations[lang].standardBanner;
  }

  bannerState.history.unshift({
    name,
    pity,
    rarity,
    outcome,
    id: pullId,
    date: now.toLocaleDateString(),
    time: now.toLocaleTimeString(),
    banner: friendlyBannerName
  });

  updateInventoryOnPull(name);

  lastPullId = pullId;
  bannerState.history.sort((a, b) => b.id - a.id);
  if (rarity === 5) {
    playSound("fiveStar");
    triggerFiveStarAnimation();
    targetState.pity = 0;

    if (banner === "weapon") {
      targetState.pity4star = 0;
    }

    if (isCharacterBanner) {
      if (outcome === "loss") {
        targetState.cState = Math.min(3, targetState.cState + 1);
        targetState.isGuaranteed = true;
      } else if (outcome === "win") {
        targetState.isGuaranteed = false;
        const wasRadiance = await showCRConfirmationModal();
        if (wasRadiance) {
          targetState.cState = 1;
          // Update the history entry we just added
          if (bannerState.history.length > 0) {
            bannerState.history[0].outcome = "capturingRadiance";
          }
        } else {
          targetState.cState = Math.max(0, targetState.cState - 1);
        }
        targetState.isCStateEstimated = false;
      } else {
        targetState.isGuaranteed = false;
      }
    } else if (banner === "weapon") {
      if (bannerState.courseSetOn) {
        targetState.isGuaranteed = outcome === "loss";
      }
    } else if (banner === "chronicled") {
      targetState.isGuaranteed = outcome === "loss";
    }
  } else {
    playSound("fourStar");
    const isCharacterBanner =
      banner === "character1" || banner === "character2";

    const has4StarGuaranteeSystem = isCharacterBanner || banner === "weapon";

    if (has4StarGuaranteeSystem) {
      const targetState = isCharacterBanner
        ? state.characterShared
        : state[banner];
      const rateUpItems =
        (isCharacterBanner
          ? currentBannerData[banner]?.fourStars
          : currentBannerData.weapon?.fourStars) || [];
      const isRateUpItem = rateUpItems
        .map((n) => n.toLowerCase())
        .includes(name.toLowerCase());

      if (isRateUpItem) {
        targetState.is4StarGuaranteed = false;
      } else {
        // Nur wenn es sich nicht um einen Rate-Up handelt, wird die Garantie gesetzt
        targetState.is4StarGuaranteed = true;
      }
    }
    targetState.pity4star = 0;
  }

  // Wichtig: saveAndRefresh wird jetzt immer aufgerufen, NACHDEM auf die Modal-Antwort gewartet wurde.
  saveAndRefresh();
}

function updateActiveCharacterBannerView() {
  // Buttons aktualisieren
  document
    .getElementById("character1-switch-btn")
    .classList.toggle("active", activeCharacterBanner === "character1");
  document
    .getElementById("character2-switch-btn")
    .classList.toggle("active", activeCharacterBanner === "character2");

  // Grids ein-/ausblenden
  document
    .getElementById("character1-grid")
    .classList.toggle("hidden", activeCharacterBanner !== "character1");
  document
    .getElementById("character2-grid")
    .classList.toggle("hidden", activeCharacterBanner !== "character2");
}

function handleResetConfirm(banner) {
  if (banner === "character1" || banner === "character2") {
    const originalBannerState = JSON.parse(
      JSON.stringify(initialState[banner])
    );
    state[banner].history = originalBannerState.history;
    state[banner].historyFilter = originalBannerState.historyFilter;
    state[banner].isQuickstartUsed = originalBannerState.isQuickstartUsed;
    state[banner].activeChart = originalBannerState.activeChart;
    state[banner].totalPulls = 0;
    state.characterShared.pity = initialState.characterShared.pity;
    state.characterShared.pity4star = initialState.characterShared.pity4star;
    state.characterShared.isGuaranteed =
      initialState.characterShared.isGuaranteed;
  } else {
    const originalState = JSON.parse(JSON.stringify(initialState[banner]));
    state[banner] = {
      ...originalState,
      wishPriority: state[banner].wishPriority,
      featuredItems: state[banner].featuredItems,
      courseSetOn: state[banner].courseSetOn,
    };
  }
  saveAndRefresh();
  hideModal("confirmation-modal");
}

function handleDeleteConfirm(banner, id) {
  state[banner].history = state[banner].history.filter(
    (item) => item.id !== id
  );
  saveAndRefresh();
  hideModal("confirmation-modal");
}

function handleSetCourseConfirm(banner, itemName) {
  state[banner].courseSetOn = itemName;
  state[banner].isGuaranteed = false;
  saveAndRefresh();
  hideModal("confirmation-modal");
}

function showModal(id) {
  const modal = document.getElementById(id);
  if (modal) {
    // ZUERST 'hidden' entfernen, damit das Element überhaupt im Layout ist
    modal.classList.remove("hidden");
    // DANN 'active' hinzufügen, um die Einblend-Animation auszulösen
    // Ein winziges Timeout stellt sicher, dass der Browser die Klassenänderungen getrennt verarbeitet.
    setTimeout(() => {
      modal.classList.add("active");
    }, 10);
  }
}

function hideModal(id) {
  const modal = document.getElementById(id);
  if (modal) {
    // ZUERST 'active' entfernen, um die Ausblend-Animation zu starten
    modal.classList.remove("active");
    // DANN, NACHDEM die Animation fertig ist, 'hidden' hinzufügen, um es komplett aus dem DOM zu nehmen.
    modal.addEventListener(
      "transitionend",
      () => {
        modal.classList.add("hidden");
      },
      { once: true }
    ); // 'once: true' sorgt dafür, dass der Listener sich selbst entfernt.
  }
}
function showConfirmationModal(action, context) {
  const { banner, id, itemName } = context;
  const lang = state.language;
  dom.confirmationConfirmBtn.dataset.action = action;
  dom.confirmationConfirmBtn.dataset.banner = banner;
  if (id) dom.confirmationConfirmBtn.dataset.id = id;
  else delete dom.confirmationConfirmBtn.dataset.id;
  if (itemName) dom.confirmationConfirmBtn.dataset.itemName = itemName;
  else delete dom.confirmationConfirmBtn.dataset.itemName;
  let text = "";
  switch (action) {
    case "reset":
      let bannerNameKey = banner;
      if (banner === "character1" || banner === "character2") {
        bannerNameKey = "character";
      }
      const translatedBannerName = translations[lang][`${bannerNameKey}Banner`];
      text = translations[lang].confirmResetText.replace(
        "{bannerName}",
        translatedBannerName
      );
      break;
    case "delete":
      text = translations[lang].confirmDeleteText;
      break;
    case "delete-team":
      text = "Are you sure you want to delete this team?";
      if (translations[lang] && translations[lang].confirmDeleteTeam) {
        text = translations[lang].confirmDeleteTeam;
      }
      break;
    case "set-course":
      text = translations[lang].confirmSetCourse;
      break;
    case "reset-highscore":
      text = translations[lang].confirmResetHighscoreText;
      break;
    case "apply-constellation":
      text = `Do you really want to activate a constellation for ${itemName}? This cannot be undone.`;
      break;
    case "confirm-delete-inventory-char":
      text = translations[lang].confirmDeleteInventoryChar(itemName);
      break;
    case "confirm-delete-inventory-weapon":
      text = translations[lang].confirmDeleteInventoryWeapon(itemName);
      break;
  }
  dom.confirmationModalText.textContent = text;
  showModal("confirmation-modal");
}

async function showItemDetailModal(itemType, itemId) {
  const modal = document.getElementById("item-detail-modal");
  const contentContainer = document.getElementById("item-detail-content");
  if (!modal || !contentContainer) return;

  showModal("item-detail-modal");
  contentContainer.innerHTML =
    '<p class="text-center p-8">Loading item data...</p>';

  let fetchType;
  if (itemType === "artifact_set") {
    fetchType = "artifacts";
  } else {
    fetchType = `${itemType}s`;
  }

  try {
    const itemData = await fetchData(fetchType, itemId);
    if (itemData) {
      renderItemDetailContent(contentContainer, itemData, itemType);
    } else {
      throw new Error(`Data not found for ${fetchType}/${itemId}`);
    }
  } catch (error) {
    console.error(error);
    contentContainer.innerHTML = `<p class="text-center p-8 text-red-400">Soon™</p>`;
  }
}

function renderItemDetailContent(container, data, itemType) {
  const iconUrl = getFandomImageUrl(itemType, data.name);
  let detailsHtml = "";

  switch (itemType) {
    case "material":
      detailsHtml = `
        ${data.description
          ? `<p class="text-gray-300 mb-4 whitespace-pre-line">${data.description}</p>`
          : ""
        }
        ${data.source
          ? `<h4 class="font-semibold text-gold-accent mb-1">Source</h4><p class="text-sm text-gray-400">${data.source}</p>`
          : ""
        }
      `;
      break;
    case "weapon":
      detailsHtml = `
        ${data.mainStat
          ? `<p class="text-gray-300 mb-4">${data.mainStat}</p>`
          : ""
        }
        ${data.source
          ? `<h4 class="font-semibold text-gold-accent mb-1">Source</h4><p class="text-sm text-gray-400">${data.source}</p>`
          : ""
        }
      `;
      break;
    case "artifact_set":
      detailsHtml = `
        <div class="space-y-3">
          <div>
            <h4 class="font-semibold text-gold-accent">2-Piece Set Bonus</h4>
            <p class="text-sm text-gray-300">${data.setBonus_2}</p>
          </div>
          <div>
            <h4 class="font-semibold text-gold-accent">4-Piece Set Bonus</h4>
            <p class="text-sm text-gray-300">${data.setBonus_4}</p>
          </div>
          ${data.domainId
          ? `<div><h4 class="font-semibold text-gold-accent">Source</h4><p class="text-sm text-gray-400">${findItem(data.domainId.replace(/_/g, " "))?.name ||
          data.domainId
          }</p></div>`
          : ""
        }
        </div>
      `;
      break;
  }

  const html = `
    <header class="flex items-center gap-4 mb-4">
      <img src="${iconUrl}" alt="${data.name
    }" class="h-16 w-16 rounded-lg border-2 border-gold-dark bg-black/20 flex-shrink-0" loading="lazy" onerror="this.style.display='none'">
      <div>
        <h3 class="text-2xl font-bold font-headings text-white">${data.name
    }</h3>
        <p class="text-xl text-yellow-400">${"★".repeat(data.rarity)}</p>
      </div>
    </header>
    <div class="border-t border-gold-dark/30 pt-4">
      ${detailsHtml}
    </div>
  `;
  container.innerHTML = html;
}

function findItem(name) {
  if (!name) return null;

  // 1. Erstelle eine normalisierte Version des Suchbegriffs.
  const normalizedSearchName = name
    .trim()
    .toLowerCase()
    .replace(/["'\s-:]/g, "");

  // 2. Nutze die Map für O(1) Lookup
  return itemLookup.get(normalizedSearchName) || null;
}

/**
 * Gets available items for a banner, considering the current guarantee status.
 * Can be overridden by a temporary status for multi-pull modals.
 * @param {string} banner - The banner ID.
 * @param {number} rarity - The rarity of items to fetch.
 * @param {object} [overrideStatus={}] - Optional object to override the global guarantee status.
 * @param {boolean} [overrideStatus.is4StarGuaranteed] - A temporary 4-star guarantee status.
 * @returns {Array} - A sorted array of available item objects.
 */
function getAvailableItemsForBanner(banner, rarity, overrideStatus = {}) {
  let options;
  const isCharacterBanner = banner === "character1" || banner === "character2";

  if (rarity === 4) {
    const targetState = isCharacterBanner
      ? state.characterShared
      : state[banner];

    const isGuaranteed =
      overrideStatus.is4StarGuaranteed !== undefined
        ? overrideStatus.is4StarGuaranteed
        : targetState.hasOwnProperty("is4StarGuaranteed") &&
        targetState.is4StarGuaranteed;

    if (isGuaranteed) {
      console.log(
        `4-Star Guarantee is ACTIVE for banner: ${banner}. Filtering options.`
      );
      const rateUpItems =
        (isCharacterBanner
          ? currentBannerData[banner]?.fourStars
          : currentBannerData.weapon?.fourStars) || [];
      options = rateUpItems.map((name) => findItem(name)).filter(Boolean);
      return options.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  switch (banner) {
    case "character1":
    case "character2":
      if (rarity === 5) {
        const featuredItemName = currentBannerData[banner]?.fiveStar;
        const featuredItem = findItem(featuredItemName);
        const standard5StarChars = itemsDB.filter(
          (item) =>
            item.rarity === 5 &&
            item.type === "character" &&
            item.pool === "standard"
        );
        options = [...standard5StarChars];
        if (featuredItem) options.push(featuredItem);
      } else {
        // rarity === 4
        const rateUp4Stars = (currentBannerData[banner]?.fourStars || [])
          .map((name) => findItem(name))
          .filter(Boolean);
        const standard4StarChars = itemsDB.filter(
          (item) =>
            item.rarity === 4 &&
            item.type === "character" &&
            item.pool === "standard"
        );
        const standard4StarWeapons = itemsDB.filter(
          (item) =>
            item.rarity === 4 &&
            item.type === "weapon" &&
            item.pool === "standard"
        );
        options = [
          ...rateUp4Stars,
          ...standard4StarChars,
          ...standard4StarWeapons,
        ];
      }
      break;
    case "weapon":
      if (rarity === 5) {
        const standardWeapons = itemsDB.filter(
          (item) =>
            item.rarity === 5 &&
            item.type === "weapon" &&
            item.pool === "standard"
        );
        const featuredWeapons = (currentBannerData.weapon?.fiveStars || [])
          .map((name) => findItem(name))
          .filter(Boolean);
        options = [...standardWeapons, ...featuredWeapons];
      } else {
        // rarity === 4
        const standard4StarWeapons = itemsDB.filter(
          (item) =>
            item.rarity === 4 &&
            item.type === "weapon" &&
            item.pool === "standard"
        );
        const rateUp4StarWeapons = (currentBannerData.weapon?.fourStars || [])
          .map((name) => findItem(name))
          .filter(Boolean);
        const standard4StarCharacters = itemsDB.filter(
          (item) =>
            item.rarity === 4 &&
            item.type === "character" &&
            item.pool === "standard"
        );
        options = [
          ...standard4StarWeapons,
          ...rateUp4StarWeapons,
          ...standard4StarCharacters,
        ];
      }
      break;

    case "standard":
      options = itemsDB.filter(
        (item) => item.rarity === rarity && item.pool === "standard"
      );
      break;
    case "chronicled":
      if (rarity === 5) {
        // 1. Hole den vom Nutzer festgelegten Kurs aus dem State.
        const courseItemName = state.chronicled.courseSetOn;
        const courseItemDetails = courseItemName
          ? findItem(courseItemName)
          : null;

        const all5StarChars =
          currentBannerData.chronicled?.fiveStars?.characters || [];
        const all5StarWeps =
          currentBannerData.chronicled?.fiveStars?.weapons || [];

        let poolNames = [];

        // 2. Wende die neue Logik an.
        if (courseItemDetails && courseItemDetails.type === "character") {
          // Wenn ein Charakter als Kurs gesetzt ist, besteht der Pool nur aus Charakteren.
          poolNames = all5StarChars;
        } else if (courseItemDetails && courseItemDetails.type === "weapon") {
          // Wenn eine Waffe als Kurs gesetzt ist, besteht der Pool nur aus Waffen.
          poolNames = all5StarWeps;
        } else {
          // Wenn kein Kurs gesetzt ist, besteht der Pool aus allem.
          poolNames = [...all5StarChars, ...all5StarWeps];
        }

        // 3. Wandle die Namen in Item-Objekte um
        options = poolNames.map((name) => findItem(name)).filter(Boolean);
      } else {
        const featuredChars =
          currentBannerData.chronicled?.fourStars?.characters || [];
        const featuredWeps =
          currentBannerData.chronicled?.fourStars?.weapons || [];
        const standard4StarWeapons = itemsDB.filter(
          (item) =>
            item.rarity === 4 &&
            item.type === "weapon" &&
            item.pool === "standard"
        );
        options = [
          ...featuredChars.map((name) => findItem(name)).filter(Boolean),
          ...featuredWeps.map((name) => findItem(name)).filter(Boolean),
          ...standard4StarWeapons,
        ];
      }
      break;
    default:
      options = itemsDB.filter((item) => item.rarity === rarity);
  }

  // Entferne Duplikate und sortiere die finale Liste
  const uniqueOptionsMap = new Map();
  options.forEach((item) => uniqueOptionsMap.set(item.name, item));
  return Array.from(uniqueOptionsMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
}

function showResolveModal(banner, rarity) {
  activeBannerContext = { banner, rarity };
  selectedItemForModal = "";

  let initialValue = "";
  const isCharacterBanner = banner === "character1" || banner === "character2";

  // Rufe die zentrale Funktion auf, um die korrekte Item-Liste zu erhalten
  let options = getAvailableItemsForBanner(banner, rarity);

  if (rarity === 5) {
    // Fall 1: Charakterbanner
    if (isCharacterBanner && state.characterShared.isGuaranteed) {
      // Hole den Namen des featured Charakters aus den aktuellen Banner-Daten
      const featuredItemName = currentBannerData[banner]?.fiveStar;
      const featuredItem = findItem(featuredItemName);
      if (featuredItem) {
        options = [featuredItem]; // Reduziere die Optionen auf nur diesen einen Charakter
        initialValue = featuredItemName;
      }
    }
    // Fall 2: Waffen- oder Chronicled-Banner
    else if (
      ["weapon", "chronicled"].includes(banner) &&
      state[banner].isGuaranteed
    ) {
      const courseItemName = state[banner].courseSetOn;
      const courseItem = findItem(courseItemName);
      if (courseItem) {
        options = [courseItem]; // Reduziere die Optionen auf das Kurs-Item
        initialValue = courseItemName;
      }
    }
  }

  selectedItemForModal = initialValue;

  createCustomSelect(
    "resolve-custom-select-wrapper",
    options,
    initialValue,
    (newValue) => {
      selectedItemForModal = newValue;
    }
  );

  const stars = "★".repeat(rarity);
  dom.resolveModalTitle.textContent = `${translations[state.language].itemReceived
    } ${stars}`;
  dom.resolveModalTitle.style.color =
    rarity === 5 ? "var(--gold-rarity)" : "var(--purple-rarity)";

  showModal("resolve-modal");
}

function handleResolveConfirm() {
  const name = selectedItemForModal.trim();
  if (!name) {
    showToast(translations[state.language].enterNamePrompt, "error");
    return;
  }
  let { banner, rarity } = activeBannerContext;
  let outcome;
  const isCharacterBanner = banner === "character1" || banner === "character2";
  const bannerState = state[banner]; // Individuelle Banner-Daten
  const targetState = isCharacterBanner ? state.characterShared : bannerState; // Geteilte oder individuelle Pity-Daten
  const item = findItem(name);
  if (!item) {
    showToast(
      translations[state.language].itemNotFound.replace("{name}", name),
      "error"
    );
    return;
  }
  if (item.rarity !== rarity) {
    showToast(
      translations[state.language].invalidItemForBanner
        .replace("{name}", name)
        .replace("{rarity}", rarity),
      "error"
    );
    return;
  }
  if (rarity === 5) {
    if (isCharacterBanner) {
      // WICHTIG: Prüfe das featuredItem des *spezifischen* Banners (character1 oder character2)
      const isFeatured =
        bannerState.featuredItem &&
        bannerState.featuredItem.toLowerCase() === item.name.toLowerCase();

      if (targetState.isGuaranteed && !isFeatured) {
        showToast(
          "Error: Received a standard character while on guarantee.",
          "error"
        );
        return;
      }
      outcome = targetState.isGuaranteed
        ? "guaranteed"
        : isFeatured
          ? "win"
          : "loss";
    } else if (banner === "weapon" || banner === "chronicled") {
      const isCourseSetItem =
        bannerState.courseSetOn &&
        bannerState.courseSetOn.toLowerCase() === item.name.toLowerCase();
      if (bannerState.isGuaranteed && !isCourseSetItem) {
        showToast(
          `Error: Received non-course item while on guarantee. Expected ${bannerState.courseSetOn}.`,
          "error"
        );
        return;
      }
      outcome = bannerState.isGuaranteed
        ? "guaranteed"
        : isCourseSetItem
          ? "win"
          : "loss";
    } else {
      outcome = "standard";
    }
  }
  resolveSinglePull(banner, rarity, item.name, outcome, 1);
  hideModal("resolve-modal");
}

function showTenPullModal(banner) {
  activeBannerContext = { banner };
  dom.pullEntriesContainer.innerHTML = ""; // Wichtig: Zuerst leeren

  const isCharacterBanner = banner === "character1" || banner === "character2";
  const targetState = isCharacterBanner ? state.characterShared : state[banner];
  const hardPity5star = ["weapon"].includes(banner) ? 80 : 90;

  const pullsUntilHardPity = hardPity5star - targetState.pity;

  if (pullsUntilHardPity <= 10 && targetState.isGuaranteed) {
    let guaranteedItemName = null;

    if (isCharacterBanner) {
      guaranteedItemName = currentBannerData[banner]?.fiveStar;
    } else if (["weapon", "chronicled"].includes(banner)) {
      guaranteedItemName = state[banner].courseSetOn;
    }
    if (guaranteedItemName) {
      addPullEntry({
        pullNumber: pullsUntilHardPity,
        name: guaranteedItemName,
        rarity: 5,
      });
    }
  }

  showModal("ten-pull-modal");
}

function addPullEntry(prefillData = null) {
  const container = dom.pullEntriesContainer;
  const entryId = `pull-entry-${Date.now()}`;

  const entryHTML = `
    <div class="pull-entry" id="${entryId}">
      <input type="number" id="pull-number-${entryId}" name="pull-number-${entryId}" class="resource-input text-center" placeholder="Nr." min="1" max="10">
      <div class="custom-select-wrapper" id="select-wrapper-${entryId}"></div>
      <select id="pull-rarity-${entryId}" name="pull-rarity-${entryId}" class="resource-input rarity-select">
        <option value="4">4-Star</option>
        <option value="5">5-Star</option>
      </select>
      <button data-action="remove-element" data-target="${entryId}" class="btn btn-danger text-xs">X</button>
    </div>`;
  container.insertAdjacentHTML("beforeend", entryHTML);

  const newEntry = document.getElementById(entryId);
  const raritySelect = newEntry.querySelector(".rarity-select");
  const selectWrapper = newEntry.querySelector(`#select-wrapper-${entryId}`);

  const renderItemSelect = () => {
    const { banner } = activeBannerContext;
    const isCharacterBanner =
      banner === "character1" || banner === "character2";
    const rarity = parseInt(raritySelect.value, 10);

    // =================================================================
    // 1. SIMULATE STATE BASED ON PREVIOUS ENTRIES IN THE MODAL
    // =================================================================
    const initialSimState = isCharacterBanner
      ? state.characterShared
      : state[banner];

    const allEntries = Array.from(container.querySelectorAll(".pull-entry"));
    const currentIndex = allEntries.indexOf(newEntry);
    const previousPullsInModal = allEntries
      .slice(0, currentIndex)
      .map((entry) => ({
        pullNumber:
          parseInt(entry.querySelector('input[type="number"]').value, 10) || 0,
        name:
          entry.querySelector(".custom-select-wrapper").dataset.selectedValue ||
          "",
        rarity: parseInt(entry.querySelector(".rarity-select").value, 10),
      }))
      .filter((p) => p.pullNumber > 0 && p.name)
      .map((p) => ({ ...p, item: findItem(p.name) })) // Add full item details
      .filter((p) => p.item) // Ensure item was found
      .sort((a, b) => a.pullNumber - b.pullNumber);

    // Use the existing simulator logic to get the correct state for *this specific pull*
    const currentSimState = calculateStateAfterPulls(
      initialSimState,
      previousPullsInModal.map((p) => p.item),
      banner
    );

    // =================================================================
    // 2. DETERMINE ITEM POOL BASED ON THE SIMULATED STATE
    // =================================================================
    let initialValue = "";
    let options;
    let overrideStatus = {};

    if (rarity === 4) {
      overrideStatus.is4StarGuaranteed = currentSimState.is4StarGuaranteed;
    }

    if (rarity === 5 && currentSimState.isGuaranteed) {
      let guaranteedItemName = null;
      if (isCharacterBanner) {
        guaranteedItemName = currentBannerData[banner]?.fiveStar;
      } else if (["weapon", "chronicled"].includes(banner)) {
        guaranteedItemName = state[banner].courseSetOn;
      }

      const guaranteedItem = findItem(guaranteedItemName);
      if (guaranteedItem) {
        options = [guaranteedItem];
        initialValue = guaranteedItemName;
      } else {
        options = getAvailableItemsForBanner(banner, rarity);
      }
    } else {
      // Normal case: No 5-star guarantee, or it's a 4-star pull.
      // The overrideStatus will correctly handle the 4-star guarantee context.
      options = getAvailableItemsForBanner(banner, rarity, overrideStatus);
    }

    // =================================================================
    // 3. CREATE THE DROPDOWN
    // =================================================================
    createCustomSelect(
      `select-wrapper-${entryId}`,
      options,
      initialValue,
      (newValue) => {
        selectWrapper.dataset.selectedValue = newValue;
        // When this value changes, all subsequent dropdowns must be re-rendered
        for (let i = currentIndex + 1; i < allEntries.length; i++) {
          allEntries[i].dispatchEvent(new Event("renderRequest"));
        }
      }
    );
    if (initialValue) {
      selectWrapper.dataset.selectedValue = initialValue;
    }
  };

  newEntry.addEventListener("renderRequest", renderItemSelect);
  raritySelect.addEventListener("change", renderItemSelect);

  if (prefillData) {
    const pullNumberInput = newEntry.querySelector('input[type="number"]');
    const removeButton = newEntry.querySelector(
      '[data-action="remove-element"]'
    );

    pullNumberInput.value = prefillData.pullNumber;
    pullNumberInput.disabled = true;
    raritySelect.value = prefillData.rarity;
    raritySelect.disabled = true;
    removeButton.disabled = true;
    removeButton.style.opacity = 0.5;

    const item = findItem(prefillData.name);
    if (item) {
      createCustomSelect(
        `select-wrapper-${entryId}`,
        [item],
        prefillData.name,
        () => { }
      );
      selectWrapper.dataset.selectedValue = prefillData.name;
      const trigger = selectWrapper.querySelector(".custom-select-trigger");
      if (trigger) {
        trigger.style.pointerEvents = "none";
        trigger.style.opacity = 0.7;
      }
    }
  } else {
    renderItemSelect();
  }
}

async function handleTenPullConfirm() {
  const { banner } = activeBannerContext;
  const lang = state.language;
  const isCharacterBanner = banner === "character1" || banner === "character2";
  const bannerState = state[banner];
  const targetState = isCharacterBanner ? state.characterShared : state[banner];
  const hardPity5star = ["weapon"].includes(banner) ? 80 : 90;
  const hardPity4star = 10;

  let results = [];
  for (const entry of Array.from(dom.pullEntriesContainer.children)) {
    const pullNumber = parseInt(
      entry.querySelector('input[type="number"]').value,
      10
    );
    const name =
      entry.querySelector(".custom-select-wrapper").dataset.selectedValue || "";
    const rarity = parseInt(entry.querySelector(".rarity-select").value, 10);

    if (!pullNumber || !name) {
      showToast(translations[lang].enterNamePrompt, "error");
      return;
    }
    if (pullNumber < 1 || pullNumber > 10) {
      showToast(translations[lang].invalidPullNumber, "error");
      return;
    }

    const item = findItem(name);
    if (!item || item.rarity !== rarity) {
      showToast(
        translations[lang].invalidItemForBanner
          .replace("{name}", name)
          .replace("{rarity}", rarity),
        "error"
      );
      return;
    }
    results.push({ pullNumber, name: item.name, rarity, item });
  }
  if (new Set(results.map((r) => r.pullNumber)).size !== results.length) {
    showToast(translations[lang].duplicatePullNumber, "error");
    return;
  }

  const sortedPulls = results.sort((a, b) => a.pullNumber - b.pullNumber);

  const timeline = Array.from({ length: 10 }, (_, i) => {
    const pullNumber = i + 1;
    const loggedPull = sortedPulls.find((p) => p.pullNumber === pullNumber);
    return {
      pullNumber,
      rarity: loggedPull ? loggedPull.rarity : 3,
      item: loggedPull ? loggedPull.item : null,
    };
  });

  let tempPity = targetState.pity;
  let tempPity4star = targetState.pity4star;
  for (const pull of timeline) {
    if (tempPity >= hardPity5star - 1 && pull.rarity !== 5) {
      showToast(translations[lang].pity5starExceeded, "error");
      return;
    }
    if (tempPity4star >= hardPity4star - 1 && pull.rarity < 4) {
      showToast(translations[lang].pity4starExceeded, "error");
      return;
    }
    tempPity++;
    tempPity4star++;
    if (pull.rarity === 5) {
      tempPity = 0;
      tempPity4star = 0;
    } else if (pull.rarity === 4) {
      tempPity4star = 0;
    }
  }

  if (!deductResources(banner, 10)) return;

  if (banner === "character1" || banner === "character2") {
    state[banner].totalPulls += 10;
  }

  let finalState = JSON.parse(JSON.stringify(targetState));
  let highestRarityInPull = 0;

  hideModal("ten-pull-modal");

  for (const pull of timeline) {
    finalState.pity++;
    finalState.pity4star++;

    if (pull.rarity >= 4) {
      if (pull.rarity > highestRarityInPull) highestRarityInPull = pull.rarity;
      const pity = pull.rarity === 5 ? finalState.pity : finalState.pity4star;
      let outcome;

      if (pull.rarity === 5) {
        if (isCharacterBanner) {
          const featuredItemName = currentBannerData[banner]?.fiveStar;
          const isFeatured =
            featuredItemName &&
            featuredItemName.toLowerCase() === pull.item.name.toLowerCase();
          outcome = finalState.isGuaranteed
            ? "guaranteed"
            : isFeatured
              ? "win"
              : "loss";

          if (outcome === "win") {
            finalState.isGuaranteed = false;
            const wasRadiance = await showCRConfirmationModal(); // Warten!
            if (wasRadiance) {
              outcome = "capturingRadiance"; // Specifischer Outcome für CR
              finalState.cState = 1;
            } else {
              finalState.cState = Math.max(0, finalState.cState - 1);
            }
            finalState.isCStateEstimated = false;
          } else if (outcome === "loss") {
            finalState.cState = Math.min(3, finalState.cState + 1); // Cap at 3
            finalState.isGuaranteed = true;
          } else {
            // guaranteed
            finalState.isGuaranteed = false;
          }
        } else if (["weapon", "chronicled"].includes(banner)) {
          const isCourseSetItem =
            bannerState.courseSetOn &&
            bannerState.courseSetOn.toLowerCase() ===
            pull.item.name.toLowerCase();
          outcome = finalState.isGuaranteed
            ? "guaranteed"
            : isCourseSetItem
              ? "win"
              : "loss";
        } else {
          outcome = "standard";
        }

        if (banner === "weapon" || banner === "chronicled") {
          if (bannerState.courseSetOn || banner === "chronicled") {
            finalState.isGuaranteed = outcome === "loss";
          }
        }

        finalState.pity = 0;
        if (banner === "weapon") {
          finalState.pity4star = 0;
        }
      } else if (pull.rarity === 4) {
        const has4StarGuaranteeSystem =
          isCharacterBanner || banner === "weapon";
        if (has4StarGuaranteeSystem) {
          const rateUpItems =
            (isCharacterBanner
              ? currentBannerData[banner]?.fourStars
              : currentBannerData.weapon?.fourStars) || [];
          const isRateUpItem = rateUpItems
            .map((n) => n.toLowerCase())
            .includes(pull.item.name.toLowerCase());

          if (isRateUpItem) {
            finalState.is4StarGuaranteed = false;
          } else {
            finalState.is4StarGuaranteed = true;
          }
        }
        finalState.pity4star = 0;
      }

      const pullId = Date.now() + pull.pullNumber;
      const now = new Date();

      let friendlyBannerName = banner;
      // lang is already defined at the top of the function
      if (banner === "character1" || banner === "character2") {
        const bannerData = currentBannerData[banner];
        if (bannerData) {
          const key = bannerData.bannerNameKey;
          if (key && translations[lang][key]) {
            friendlyBannerName = translations[lang][key];
          } else if (bannerData.fiveStar) {
            friendlyBannerName = `Banner ${bannerData.fiveStar}`;
          }
        }
      } else if (banner === "weapon") {
        friendlyBannerName = translations[lang].weaponBanner;
      } else if (banner === "chronicled") {
        const region = currentBannerData.chronicled?.region;
        const baseName = translations[lang].chronicledBanner;
        friendlyBannerName = region ? `${region} ${baseName}` : baseName;
      } else if (banner === "standard") {
        friendlyBannerName = translations[lang].standardBanner;
      }

      state[banner].history.unshift({
        name: pull.item.name,
        pity,
        rarity: pull.rarity,
        outcome,
        id: pullId,
        date: now.toLocaleDateString(),
        time: now.toLocaleTimeString(),
        banner: friendlyBannerName
      });
      updateInventoryOnPull(pull.item.name);
      lastPullId = pullId;
    }
  }

  if (highestRarityInPull === 5) {
    playSound("fiveStar");
    triggerFiveStarAnimation();
  } else if (highestRarityInPull === 4) {
    playSound("fourStar");
  }

  targetState.pity = finalState.pity;
  targetState.pity4star = finalState.pity4star;
  targetState.isGuaranteed = finalState.isGuaranteed;
  targetState.is4StarGuaranteed = finalState.is4StarGuaranteed;
  targetState.cState = finalState.cState; // C-State auch übertragen
  targetState.isCStateEstimated = finalState.isCStateEstimated; // Und den Schätz-Status
  state[banner].history.sort((a, b) => b.id - a.id);

  saveAndRefresh();
}

function renderHistory(banner) {
  const historyContainer = document.getElementById(`${banner}-history`);
  historyContainer.innerHTML = "";
  const lang = state.language;
  const filteredHistory = state[banner].history.filter(
    (item) =>
      state[banner].historyFilter === "all" ||
      item.rarity == state[banner].historyFilter
  );
  if (filteredHistory.length === 0) {
    historyContainer.innerHTML = `<p class="text-sm text-gray-500 text-center">${translations[lang].noMatchingPulls}</p>`;
    return;
  }
  filteredHistory.forEach((item) => {
    const itemDetails = findItem(item.name);
    const itemEl = document.createElement("div");
    itemEl.className = `history-item ${item.rarity === 5 ? "history-item-5star" : "history-item-4star"}`;

    // NEU: Outcome-Klasse hinzufügen
    if (item.outcome) {
      itemEl.classList.add(`history-outcome-${item.outcome}`);
    }

    if (item.id === lastPullId) {
      itemEl.classList.add("new-item");
      setTimeout(() => (lastPullId = 0), 500);
    }
    let pityText = `${translations[lang].pullsAt.replace("{pity}", item.pity)}`;
    let outcomeText = "";
    if (item.outcome) {
      let outcomeKey = `outcome${item.outcome.charAt(0).toUpperCase() + item.outcome.slice(1)
        }`;
      if (translations[lang][outcomeKey]) {
        outcomeText = ` ${translations[lang][outcomeKey]}`;
      }
    }
    const imageUrl = itemDetails
      ? getFandomImageUrl(itemDetails.type, itemDetails.name, "icon")
      : PLACEHOLDER_ICON;

    const stars = "★".repeat(item.rarity);

    const characterIdAttr =
      itemDetails?.type === "character"
        ? `data-character-id="${itemDetails.name
          .toLowerCase()
          .replace(/ /g, "_")}" style="cursor: pointer;"`
        : "";

    let metaText = "";
    if (item.date) {
      metaText = `<p class="text-xs text-gray-500 mt-0.5">${item.date} ${item.time || ""} ${item.banner ? `• ${item.banner}` : ""}</p>`;
    }

    itemEl.innerHTML = `<img src="${imageUrl}" alt="${item.name}" class="history-item-icon" ${characterIdAttr} onerror="this.style.display='none';"><div><p class="font-semibold">${item.name} <span class="text-yellow-400 text-xs align-middle">${stars}</span></p><p class="text-xs text-gray-400">${pityText}${outcomeText}</p>${metaText}</div><button data-action="show-confirm-modal" data-type="delete" data-banner="${banner}" data-id="${item.id}" class="ml-auto text-red-400 hover:text-red-600 text-xs font-semibold">X</button>`;

    historyContainer.appendChild(itemEl);
  });
}

function renderInventory() {
  renderInventoryFilters();

  const charGrid = document.getElementById("inventory-characters-grid");
  const weaponGrid = document.getElementById("inventory-weapons-grid");
  if (!charGrid || !weaponGrid) return;

  const sortButtons = document.querySelectorAll(
    '[data-action="set-inventory-sort"]'
  );
  sortButtons.forEach((btn) => {
    btn.classList.toggle(
      "active",
      btn.dataset.sortType === state.inventory.sort
    );
  });

  const { element: activeElement, weaponType: activeWeaponType } =
    state.inventory.filters;
  const sortType = state.inventory.sort;

  // Helper to get item details once
  const getItemDetails = (name) => findItem(name);

  const sortItems = (a, b) => {
    // a and b are now objects that already contain their item details if we pre-process
    // But to keep it compatible with existing structure if we don't fully refactor data flow:
    const itemA = a.itemDetails || findItem(a.name);
    const itemB = b.itemDetails || findItem(b.name);

    if (!itemA || !itemB) return 0;

    switch (sortType) {
      case "rarity_desc":
        if (itemA.rarity !== itemB.rarity) {
          return itemB.rarity - itemA.rarity;
        }
        return a.name.localeCompare(b.name);
      case "rarity_asc":
        if (itemA.rarity !== itemB.rarity) {
          return itemA.rarity - itemB.rarity;
        }
        return a.name.localeCompare(b.name);
      case "name_asc":
      default:
        return a.name.localeCompare(b.name);
    }
  };

  // --- CHARACTERS ---
  const characterEntries = Object.entries(state.inventory.characters).map(
    ([name, data]) => ({ name, data, itemDetails: getItemDetails(name) })
  );

  const filteredChars = characterEntries.filter(({ itemDetails }) => {
    if (!itemDetails) return false;
    const elementMatch = !activeElement || itemDetails.element === activeElement;
    const weaponTypeMatch =
      !activeWeaponType || itemDetails.weaponType === activeWeaponType;
    return elementMatch && weaponTypeMatch;
  });

  const sortedChars = filteredChars.sort(sortItems);

  if (sortedChars.length === 0) {
    charGrid.innerHTML = `<p class="col-span-full text-center text-gray-500">No characters match the current filter.</p>`;
  } else {
    // Batch DOM update
    charGrid.innerHTML = sortedChars.map(({ name, data, itemDetails }) => {
      const iconUrl = getFandomImageUrl("character", name);
      const hasPending = data.pendingConstellations > 0;
      const characterId = name.toLowerCase().replace(/ /g, "_");
      const elementClass = itemDetails.element
        ? `element-${itemDetails.element.toLowerCase()}`
        : "";

      return `
        <div 
          class="inventory-item-card rarity-${itemDetails.rarity} ${elementClass} ${hasPending ? "pending-upgrade" : ""
        }"
          data-character-id="${characterId}"
          title="View details for ${name}"
        >
          <img src="${iconUrl}" alt="${name}" class="item-icon" onerror="this.src='${PLACEHOLDER_ICON}'">
          <div class="constellation-overlay">C${data.constellation}</div>
          ${hasPending
          ? `<button class="pending-button" data-action="apply-constellation" data-name="${name}">+${data.pendingConstellations}</button>`
          : ""
        }
          <button class="edit-button" data-action="show-edit-inventory-modal" data-type="character" data-name="${name}" title="Edit ${name}">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l-.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15-.08a2 2 0 0 0 .73 2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <div class="item-name">${name}</div>
        </div>
      `;
    }).join("");
  }

  // --- WEAPONS ---
  // Pre-process weapons to include item details
  const weaponEntries = state.inventory.weapons.map(w => ({
    ...w,
    itemDetails: getItemDetails(w.name)
  }));

  const filteredWeapons = weaponEntries.filter((weapon) => {
    if (!activeWeaponType && !activeElement) {
      return true;
    }
    const item = weapon.itemDetails;
    if (!item) return false;
    const weaponTypeMatch =
      !activeWeaponType || item.element === activeWeaponType;
    return weaponTypeMatch;
  });

  const sortedWeapons = filteredWeapons.sort(sortItems);

  if (sortedWeapons.length === 0) {
    weaponGrid.innerHTML = `<p class="col-span-full text-center text-gray-500">No weapons match the current filter.</p>`;
  } else {
    // Batch DOM update
    weaponGrid.innerHTML = sortedWeapons.map((weapon) => {
      const item = weapon.itemDetails;
      if (!item) return "";

      const iconUrl = getFandomImageUrl("weapon", weapon.name);
      // Note: checking for refinement possibility requires looking at other weapons.
      // This is O(N^2) if we do it naively inside the loop.
      // Optimization: We can check this more efficiently if needed, but for now let's keep the logic 
      // but rely on the fact that we are iterating over the sorted array.
      // Actually, let's just keep the logic as is for correctness, but it's inside the map now.
      const canBeRefined =
        sortedWeapons.some(
          (w) => w.name === weapon.name && w.id !== weapon.id
        ) && weapon.refinement < 5;

      const weaponIdForDetail = weapon.name
        .toLowerCase()
        .replace(/\s/g, "_")
        .replace(/[^a-z0-9_-]/g, "");

      return `
        <div 
          class="inventory-item-card rarity-${item.rarity}"
          data-weapon-detail-id="${weaponIdForDetail}"
          data-item-type="weapon" 
          title="View details for ${weapon.name}"
        >
          <img src="${iconUrl}" alt="${weapon.name
        }" class="item-icon" onerror="this.src='${PLACEHOLDER_ICON}'">
          <div class="refinement-overlay">R${weapon.refinement}</div>
          ${canBeRefined
          ? `<button class="refine-indicator" data-action="show-refine-modal" data-weapon-id="${weapon.id}" title="Refine ${weapon.name}">+</button>`
          : ""
        }
          <button class="edit-button" data-action="show-edit-inventory-modal" data-type="weapon" data-id="${weapon.id
        }" title="Edit ${weapon.name}">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 0 2l-.15.08a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l-.22-.38a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1 0-2l.15-.08a2 2 0 0 0 .73 2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <div class="item-name">${weapon.name}</div>
        </div>
      `;
    }).join("");
  }
}

/**
 * Rendert die Team-Builder-Ansicht im Profil-Tab.
 */
function renderTeamBuilder() {
  const grid = document.getElementById("teams-grid");
  if (!grid) return;

  grid.innerHTML = state.teams
    .map((team, teamIndex) => {
      // 1. Sammle die Elemente der Teammitglieder
      const teamElements = team.members
        .map((member) => {
          if (member && member.character) {
            return findItem(member.character)?.element;
          }
          return null;
        })
        .filter(Boolean); // Entfernt alle null-Werte

      // 2. Erstelle das HTML für die Element-Icons
      const elementIconsHtml = [...new Set(teamElements)] // Entfernt Duplikate
        .map(
          (element) =>
            `<img src="${ELEMENT_ICONS[element]}" class="team-element-icon" alt="${element}">`
        )
        .join("");

      return `
      <div class="team-card relative">
        <button class="absolute top-2 right-2 text-red-500 hover:text-red-700 font-bold px-2 py-0.5 rounded bg-black/50 hover:bg-black/70 z-10 transition-colors" data-action="show-confirm-modal" data-type="delete-team" data-id="${teamIndex}" title="Delete Team">×</button>
        <div class="team-header mb-2 relative pr-8">
          <input 
            type="text" 
            class="team-name-input w-full bg-transparent font-bold text-lg focus:outline-none" 
            value="${team.name}" 
            data-action="rename-team"
            data-team-index="${teamIndex}"
            placeholder="Team Name"
          >
          <div class="team-elements-display flex justify-center flex-wrap mt-2 gap-1">${elementIconsHtml}</div>
        </div>
        <div class="team-slots-grid">
          ${team.members
          .map((member, memberIndex) => {
            if (member && member.character) {
              // --- Slot ist besetzt ---
              const charDetails = findItem(member.character);
              const weaponDetails = member.weaponId
                ? state.inventory.weapons.find(
                  (w) => w.id === member.weaponId
                )
                : null;
              const weaponItemDetails = weaponDetails
                ? findItem(weaponDetails.name)
                : null;

              return `
                <div class="team-member-card group" data-action="edit-team-member" data-team-index="${teamIndex}" data-member-index="${memberIndex}">
                  <div class="team-member-bg">
                    <img src="${getFandomImageUrl("character", charDetails.name, "multi_wish")}" onerror="this.onerror=null; this.src='${getFandomImageUrl("character", charDetails.name, "wish")}';" alt="${charDetails.name}">
                    <div class="team-member-gradient"></div>
                  </div>
                  <div class="team-member-content">
                    <div class="team-member-name text-element-${charDetails.element.toLowerCase()} w-full text-center text-lg drop-shadow-md z-10 leading-tight bg-black/40 rounded-t-md py-1 absolute top-0 left-0 right-0 backdrop-blur-sm border-b border-white/10">${charDetails.name}</div>
                    
                    <div class="team-member-equipment z-10 mt-auto">
                      ${weaponItemDetails
                  ? `
                          <div class="equipment-icon-wrapper rarity-${weaponItemDetails.rarity}" title="${weaponItemDetails.name}">
                              <img src="${getFandomImageUrl("weapon", weaponItemDetails.name)}" class="equipment-icon" />
                          </div>
                          `
                  : `
                          <div class="equipment-icon-wrapper empty">
                              <img src="${WEAPON_TYPE_ICONS[charDetails.weaponType]}" class="equipment-icon placeholder" />
                          </div>
                          `
                }

                      ${(member.artifacts && member.artifacts.length > 0)
                  ? member.artifacts.map((setId) => {
                    const setDetails = findItem(setId.replace(/_/g, " "));
                    return setDetails
                      ? `
                              <div class="equipment-icon-wrapper rarity-${setDetails.rarity}" title="${setDetails.name}">
                                  <img src="${getFandomImageUrl("artifact_set", setDetails.name)}" class="equipment-icon" />
                              </div>
                              `
                      : "";
                  }).join("")
                  : ``
                }
                    </div>
                  </div>
                </div>
              `;
            } else {
              // --- Slot ist leer ---
              return `
                <div class="team-member-card empty-slot group" data-action="add-team-member" data-team-index="${teamIndex}" data-member-index="${memberIndex}">
                  <span class="slot-add-icon text-5xl text-gold-dark opacity-50 transition-all duration-300 group-hover:scale-125 group-hover:opacity-100 group-hover:text-gold-accent">+</span>
                </div>
              `;
            }
          })
          .join("")}
        </div>
      </div>
    `;
    })
    .join("");
}

let teamModalContext = {};

/**
 * Öffnet das Modal zur Auswahl eines Charakters für einen leeren Team-Slot.
 * @param {number} teamIndex - Der Index des Teams.
 * @param {number} memberIndex - Der Index des Slots im Team.
 */
function showAddTeamMemberModal(teamIndex, memberIndex) {
  teamModalContext = { teamIndex, memberIndex, isEditing: false };
  const grid = document.getElementById("team-member-selection-grid");
  if (!grid) return;

  // 1. Hole die Namen der Charaktere, die du besitzt.
  const characterNamesInCollection = Object.keys(state.inventory.characters);

  if (characterNamesInCollection.length === 0) {
    showToast("You have no characters in your collection to add.", "error");
    return;
  }

  // 2. Wandle diese Namen in die vollständigen Charakter-Datenobjekte aus unserer DB um.
  const charactersWithDetails = characterNamesInCollection
    .map((charName) => findItem(charName))
    .filter(Boolean); // Stellt sicher, dass nur gefundene Charaktere in der Liste sind.

  // 3. Sortiere die Liste
  charactersWithDetails.sort((a, b) => {
    // Primäre Sortierung: Nach Seltenheit absteigend (5-Sterne zuerst)
    if (a.rarity !== b.rarity) {
      return b.rarity - a.rarity;
    }
    // Sekundäre Sortierung: Alphabetisch nach Name (A-Z)
    return a.name.localeCompare(b.name);
  });

  // 4. Erstelle das HTML mit der jetzt sortierten Liste.
  grid.innerHTML = charactersWithDetails
    .map((charDetails) => {
      if (!charDetails) return "";
      return `
      <div class="inventory-item-card rarity-${charDetails.rarity
        } element-${charDetails.element.toLowerCase()}" 
           data-action="select-team-character" 
           data-char-name="${charDetails.name}"
           title="${charDetails.name}">
        <img src="${getFandomImageUrl(
          "character",
          charDetails.name
        )}" class="item-icon" alt="${charDetails.name}">
        <div class="item-name">${charDetails.name}</div>
      </div>
    `;
    })
    .join("");

  showModal("add-team-member-modal");
}

/**
 * Öffnet das Modal zur Bearbeitung eines bereits hinzugefügten Team-Mitglieds.
 * @param {number} teamIndex - Der Index des Teams.
 * @param {number} memberIndex - Der Index des Slots im Team.
 */
function showEditTeamMemberModal(teamIndex, memberIndex) {
  teamModalContext = { teamIndex, memberIndex, isEditing: true };

  const member = state.teams[teamIndex].members[memberIndex];
  if (!member) return;

  teamModalContext.selectedWeaponId = member.weaponId;

  const charDetails = findItem(member.character);
  const header = document.getElementById("edit-team-member-header");
  header.innerHTML = `
    <img src="${getFandomImageUrl("character", charDetails.name)}" alt="${charDetails.name
    }" class="h-16 w-16 rounded-full border-2 border-gold-dark">
    <div>
      <h3 class="text-2xl font-bold">${charDetails.name}</h3>
      <p class="text-lg text-yellow-400">${"★".repeat(charDetails.rarity)}</p>
    </div>
  `;

  // 1. Finde heraus, welchen Waffentyp der Charakter benötigt.
  const characterWeaponType = charDetails.weaponType;

  const availableWeapons = state.inventory.weapons.filter((w) => {
    const isEquippedByOtherInTeam = state.teams[teamIndex].members.some(
      (m, idx) => m && idx !== memberIndex && m.weaponId === w.id
    );
    if (isEquippedByOtherInTeam) {
      return false;
    }
    // Bedingung 2: Passt der Waffentyp?
    const weaponDetails = findItem(w.name);
    if (!weaponDetails) {
      return false; // Sicherheitshalber, falls die Waffe nicht in der DB ist.
    }

    // In deiner DB ist der Waffentyp unter dem Schlüssel 'element' gespeichert.
    const typeMatches = weaponDetails.element === characterWeaponType;

    return typeMatches; // Gib die Waffe nur zurück, wenn der Typ passt.
  });

  createCustomSelect(
    "team-member-weapon-select-wrapper",
    availableWeapons.map((w) => findItem(w.name)).filter(Boolean),
    findItem(
      state.inventory.weapons.find((w) => w.id === member.weaponId)?.name
    )?.name || "",
    (newValue) => {
      const selectedWeapon = availableWeapons.find((w) => w.name === newValue);
      teamModalContext.selectedWeaponId = selectedWeapon
        ? selectedWeapon.id
        : null;
    }
  );

  const artifactContainer = document.getElementById(
    "team-member-artifact-selection"
  );
  const artifactSets = itemsDB
    .filter((item) => item.type === "artifact_set")
    .sort((a, b) => a.name.localeCompare(b.name));

  artifactContainer.innerHTML = artifactSets
    .map((set) => {
      const setId = set.name.toLowerCase().replace(/ /g, "_");
      const isChecked = (member.artifacts || []).includes(setId);

      return `
      <label class="flex items-center gap-2 p-1 cursor-pointer hover:bg-white/10 rounded">
        <input type="checkbox" value="${setId}" class="team-artifact-checkbox" ${isChecked ? "checked" : ""
        }>
        <img src="${getFandomImageUrl(
          "artifact_set",
          set.name
        )}" class="w-6 h-6 rounded-full">
        <span class="text-sm">${set.name}</span>
      </label>
    `;
    })
    .join("");

  showModal("edit-team-member-modal");
}

/**
 * Behandelt die Logik nach dem Klick auf "Speichern" oder "Entfernen" im Bearbeitungs-Modal
 * oder nach der Auswahl eines Charakters im Hinzufügen-Modal.
 */
function handleTeamUpdate(action, data) {
  const { teamIndex, memberIndex } = teamModalContext;

  if (teamIndex === undefined || memberIndex === undefined) {
    console.error("Team context is missing!");
    return;
  }

  const team = state.teams[teamIndex];
  if (!team) return;

  switch (action) {
    case "select-character":
      team.members[memberIndex] = {
        character: data.charName,
        weaponId: null,
        artifacts: [],
      };
      hideModal("add-team-member-modal");
      break;

    case "save-member": {
      const member = team.members[memberIndex];
      if (!member) return;

      const selectedArtifacts = Array.from(
        document.querySelectorAll(
          "#edit-team-member-modal .team-artifact-checkbox:checked"
        )
      )
        .map((cb) => cb.value)
        .slice(0, 2);

      member.weaponId = teamModalContext.selectedWeaponId;
      member.artifacts = selectedArtifacts;

      hideModal("edit-team-member-modal");
      break;
    }

    case "remove-member":
      team.members[memberIndex] = null;
      hideModal("edit-team-member-modal");
      break;
  }

  saveAndRefresh();
  teamModalContext = {};
}

function addNewTeam() {
  state.teams.push({
    name: `Team ${state.teams.length + 1}`,
    members: [null, null, null, null],
  });
  saveAndRefresh();
}

function deleteTeam(teamIndex) {
  state.teams.splice(teamIndex, 1);
  saveAndRefresh();
  hideModal("confirmation-modal");
}

function showEditInventoryModal(type, identifier) {
  editInventoryContext = { type, id: identifier };

  const levelSelect = document.getElementById("edit-inventory-level-select");
  const levelLabel = levelSelect.previousElementSibling;
  const pendingContainer = document.getElementById(
    "edit-inventory-pending-container"
  );
  const editGrid = levelSelect.closest(".grid");

  let itemDetails;
  levelSelect.innerHTML = "";

  // Remove any existing total-count display to prevent duplicates if function is called repeatedly (though modal usually clears)
  const existingCount = document.getElementById("edit-inventory-total-count");
  if (existingCount) existingCount.remove();

  if (type === "character") {
    const item = state.inventory.characters[identifier];
    if (!item) return;
    itemDetails = findItem(identifier);

    levelLabel.textContent = "Constellation";
    pendingContainer.style.display = "block";
    editGrid.classList.remove("grid-cols-1");
    editGrid.classList.add("grid-cols-2");

    for (let i = 0; i <= 6; i++) {
      levelSelect.innerHTML += `<option value="${i}">C${i}</option>`;
    }
    levelSelect.value = item.constellation;
    document.getElementById("edit-inventory-pending-input").value =
      item.pendingConstellations;

    // --- NEW: Calculate and Display Total Count ---
    // If totalCount exists, use it. If not, backward compatibility: C-Level + Pending + 1 (Base Copy)
    const effectiveTotal = (typeof item.totalCount !== "undefined")
      ? item.totalCount
      : (item.constellation + (item.pendingConstellations || 0) + 1);

    // Insert the display below the inputs
    const totalCountHTML = `
      <div id="edit-inventory-total-count" class="col-span-2 text-center mt-2 text-gray-400 text-sm">
        Total Obtained: <span class="text-white font-bold">${effectiveTotal}</span> times
      </div>
    `;
    editGrid.insertAdjacentHTML('beforeend', totalCountHTML);
    // ----------------------------------------------

  } else {
    // type === 'weapon'
    const weaponId = parseFloat(identifier);
    const item = state.inventory.weapons.find((w) => w.id === weaponId);
    if (!item) return;
    itemDetails = findItem(item.name);

    levelLabel.textContent = "Refinement";
    pendingContainer.style.display = "none";
    editGrid.classList.remove("grid-cols-2");
    editGrid.classList.add("grid-cols-1");

    for (let i = 1; i <= 5; i++) {
      levelSelect.innerHTML += `<option value="${i}">R${i}</option>`;
    }
    levelSelect.value = item.refinement;
  }

  if (!itemDetails) return;
  document.getElementById("edit-inventory-modal-icon").src = getFandomImageUrl(
    type,
    itemDetails.name
  );
  document.getElementById("edit-inventory-modal-name").textContent =
    itemDetails.name;

  showModal("edit-inventory-modal");
}

function handleEditInventorySave() {
  const { type, id } = editInventoryContext;
  if (!id) return;

  const newLevel = parseInt(
    document.getElementById("edit-inventory-level-select").value,
    10
  );

  if (type === "character") {
    const newPending =
      parseInt(
        document.getElementById("edit-inventory-pending-input").value,
        10
      ) || 0;
    state.inventory.characters[id].constellation = newLevel;
    state.inventory.characters[id].pendingConstellations = newPending;
    showToast(`${id}'s data updated!`, "success");
  } else {
    // type === 'weapon'
    const weaponId = parseFloat(id);
    const weapon = state.inventory.weapons.find((w) => w.id === weaponId);
    if (weapon) {
      weapon.refinement = newLevel;
      showToast(`${weapon.name}'s refinement updated!`, "success");
    }
  }

  saveAndRefresh();
  hideModal("edit-inventory-modal");
}

function handleEditInventoryDelete() {
  const { type, id } = editInventoryContext;
  if (!id) return;

  if (type === "character") {
    showConfirmationModal("confirm-delete-inventory-char", { itemName: id });
  } else {
    // type === 'weapon'
    const weaponId = parseFloat(id);
    const weapon = state.inventory.weapons.find((w) => w.id === weaponId);
    if (weapon) {
      showConfirmationModal("confirm-delete-inventory-weapon", {
        itemName: weapon.name,
        id: weapon.id,
      });
    }
  }
}

function deleteInventoryCharacter(name) {
  if (state.inventory.characters[name]) {
    delete state.inventory.characters[name];
    showToast(`${name} removed from your collection.`, "success");
    saveAndRefresh();
  }
  hideModal("edit-inventory-modal");
  hideModal("confirmation-modal");
}

function deleteInventoryWeapon(weaponId) {
  const id = parseFloat(weaponId);
  const initialLength = state.inventory.weapons.length;
  state.inventory.weapons = state.inventory.weapons.filter((w) => w.id !== id);
  if (state.inventory.weapons.length < initialLength) {
    showToast(`Weapon removed from your collection.`, "success");
    saveAndRefresh();
  }
  hideModal("edit-inventory-modal");
  hideModal("confirmation-modal");
}

function handleRefineWeapon(targetWeaponId) {
  const targetWeaponIndex = state.inventory.weapons.findIndex(
    (w) => w.id === targetWeaponId
  );
  if (targetWeaponIndex === -1) return;

  const targetWeapon = state.inventory.weapons[targetWeaponIndex];
  if (targetWeapon.refinement >= 5) {
    showToast("This weapon is already at maximum refinement.", "error");
    return;
  }

  // Finde die erste verfügbare Kopie (das "Material")
  const materialWeaponIndex = state.inventory.weapons.findIndex(
    (w) => w.name === targetWeapon.name && w.id !== targetWeapon.id
  );

  if (materialWeaponIndex === -1) {
    showToast("No available copies to use for refinement.", "error");
    return;
  }

  // Logik anwenden
  targetWeapon.refinement++;
  state.inventory.weapons.splice(materialWeaponIndex, 1); // Material-Waffe entfernen

  showToast(
    `${targetWeapon.name} refined to R${targetWeapon.refinement}!`,
    "success"
  );
  saveAndRefresh();
}

function setActiveBannerRotation() {
  const now = new Date();
  let activeRotation = null;

  // Finde die erste Rotation in der Liste, deren Enddatum in der Zukunft liegt
  for (const rotation of BANNER_ROTATIONS) {
    if (new Date(rotation.endDate) > now) {
      activeRotation = rotation;
      break;
    }
  }

  // Fallback: Wenn alle Banner abgelaufen sind, nimm einfach den letzten aus der Liste
  if (!activeRotation) {
    activeRotation = BANNER_ROTATIONS[BANNER_ROTATIONS.length - 1];
  }

  currentBannerData = activeRotation;

  if (state && state.character1) {
    // Sicherer Zugriff mit Optional Chaining und Fallback-Werten
    state.character1.featuredItem =
      currentBannerData.character1?.fiveStar || "";
    state.character2.featuredItem =
      currentBannerData.character2?.fiveStar || "";
    state.weapon.featuredItems = currentBannerData.weapon?.fiveStars || [];
  }

  if (state && state.weapon) {
    const prevCourse = state.weapon.courseSetOn;
    if (prevCourse) {
      const stillInRotation =
        Array.isArray(currentBannerData.weapon?.fiveStars) &&
        currentBannerData.weapon.fiveStars.some(
          (n) => n.toLowerCase() === prevCourse.toLowerCase()
        );
      if (!stillInRotation) {
        state.weapon.courseSetOn = "";
        state.weapon.isGuaranteed = false;
      }
    }
  }

  // Die gleiche Logik für das Chronicled Banner hinzufügen
  if (state && state.chronicled) {
    const prevChronicledCourse = state.chronicled.courseSetOn;
    if (prevChronicledCourse) {
      const chronicled5StarChars =
        currentBannerData.chronicled?.fiveStars?.characters || [];
      const chronicled5StarWeps =
        currentBannerData.chronicled?.fiveStars?.weapons || [];
      const allChronicled5Stars = [
        ...chronicled5StarChars,
        ...chronicled5StarWeps,
      ];

      const stillInRotation = allChronicled5Stars.some(
        (n) => n.toLowerCase() === prevChronicledCourse.toLowerCase()
      );

      if (!stillInRotation) {
        // Wenn der Kurs nicht mehr gültig ist, setze ihn und die Garantie zurück
        state.chronicled.courseSetOn = "";
        state.chronicled.isGuaranteed = false;
        console.log("Chronicled Wish course reset due to banner rotation.");
      }
    }
  }
}

function getProbability(pity, bannerType) {
  if (["weapon"].includes(bannerType)) {
    if (pity >= 79) return 100.0;
    if (pity < 62) return 0.7;
    return 0.7 + (pity - 61) * 7;
  } else {
    if (pity >= 89) return 100.0;
    if (pity < 73) return 0.6;
    return 0.6 + (pity - 72) * 6;
  }
}

/**
 * Berechnet die Wahrscheinlichkeit, einen 4-Sterne-Gegenstand zu erhalten, basierend auf der 4-Sterne-Pity.
 * Berücksichtigt eine steile Soft-Pity-Rampe ("Klippe"), die je nach Bannertyp variiert.
 * @param {number} pity4star - Die aktuelle 4-Sterne-Pity (0-9).
 * @param {string} bannerId - Die ID des Banners (z.B. 'weapon', 'character1').
 * @returns {number} Die Wahrscheinlichkeit in Prozent.
 */
function getProbability4Star(pity4star, bannerId) {
  // Waffenbanner: Soft Pity beginnt bei Pull 8 (pity ist 7)
  if (bannerId === "weapon") {
    if (pity4star >= 9) return 100.0; // Pull 10 (Hard Pity)
    if (pity4star >= 8) return 75.1; // Pull 9: Sehr hohe Chance
    if (pity4star >= 7) return 60.1; // Pull 8: Der erste große Sprung
    return 5.1; // Pulls 1-7: Basis-Chance
  }
  // Alle anderen Banner (Charakter, Chronicled, Standard)
  else {
    if (pity4star >= 9) return 100.0; // Pull 10 (Hard Pity)
    // Pull 9: Ein massiver Sprung.
    if (pity4star >= 8) return 75.1;
    return 5.1; // Pulls 1-8: Basis-Chance
  }
}

function calculateComprehensiveStats(history) {
  const stats = {
    totalPullsInHistory: 0,
    total5stars: 0,
    total4stars: 0,
    history5stars: [],
    history4stars: [],
    avg5starPity: "N/A",
    avg4starPity: "N/A",
    luckiest5star: null,
    unluckiest5star: null,
    luckiest4star: null,
    unluckiest4star: null,
    char4starCount: 0,
    weapon4starCount: 0,
    char5starCount: 0,
    weapon5starCount: 0,
    fiftyFiftyWins: 0,
    fiftyFiftyLosses: 0,
  };

  if (history.length === 0) {
    return stats;
  }

  stats.history5stars = history.filter((p) => p.rarity === 5);
  stats.history4stars = history.filter((p) => p.rarity === 4);
  stats.total5stars = stats.history5stars.length;
  stats.total4stars = stats.history4stars.length;

  stats.totalPullsInHistory = history
    .filter((p) => p.rarity === 5)
    .reduce((sum, p) => sum + p.pity, 0);

  if (stats.total5stars > 0) {
    const totalPity = stats.history5stars.reduce((sum, p) => sum + p.pity, 0);
    stats.avg5starPity = (totalPity / stats.total5stars).toFixed(1);
    stats.luckiest5star = stats.history5stars.reduce((prev, curr) =>
      prev.pity < curr.pity ? prev : curr
    );
    stats.unluckiest5star = stats.history5stars.reduce((prev, curr) =>
      prev.pity > curr.pity ? prev : curr
    );
  }

  if (stats.total4stars > 0) {
    const totalPity = stats.history4stars.reduce((sum, p) => sum + p.pity, 0);
    stats.avg4starPity = (totalPity / stats.total4stars).toFixed(1);
    stats.luckiest4star = stats.history4stars.reduce((prev, curr) =>
      prev.pity < curr.pity ? prev : curr
    );
    stats.unluckiest4star = stats.history4stars.reduce((prev, curr) =>
      prev.pity > curr.pity ? prev : curr
    );
  }

  stats.history4stars.forEach((p) => {
    const item = findItem(p.name);
    if (item?.type === "character") stats.char4starCount++;
    else if (item?.type === "weapon") stats.weapon4starCount++;
  });

  stats.history5stars.forEach((p) => {
    const item = findItem(p.name);
    if (item?.type === "character") stats.char5starCount++;
    else if (item?.type === "weapon") stats.weapon5starCount++;
    if (p.outcome === "win" || p.outcome === "capturingRadiance")
      stats.fiftyFiftyWins++;
    else if (p.outcome === "loss") stats.fiftyFiftyLosses++;
  });

  return stats;
}

function renderBannerStats(banner) {
  const bannerState = state[banner];
  const statsContainer = document.getElementById(`${banner}-stats`);
  const lang = state.language;

  const stats = calculateComprehensiveStats(bannerState.history);

  let totalPulls;
  if (banner === "character1" || banner === "character2") {
    totalPulls = bannerState.totalPulls;
  } else {
    totalPulls = bannerState.pity + stats.totalPullsInHistory;
  }

  if (totalPulls === 0 && stats.total5stars === 0) {
    statsContainer.innerHTML = "";
    return;
  }

  let statsHTML = `<p>${translations[lang].totalPulls} <span class="font-bold text-white">${totalPulls}</span></p>`;
  statsHTML += `<p>${translations[lang].total5stars} <span class="font-bold text-white">${stats.total5stars}</span> | ${translations[lang].total4stars} <span class="font-bold text-white">${stats.total4stars}</span></p>`;

  if (stats.avg5starPity !== "N/A")
    statsHTML += `<p>${translations[lang].avgPity} <span class="font-bold text-white">${stats.avg5starPity}</span></p>`;
  if (stats.avg4starPity !== "N/A")
    statsHTML += `<p>${translations[lang].avg4starPity} <span class="font-bold text-white">${stats.avg4starPity}</span></p>`;

  // Fall 1: Es ist ein Charakterbanner. Die Statistik muss das gesamte System widerspiegeln.
  if (banner === "character1" || banner === "character2") {
    // Kombiniere die Verläufe beider Charakterbanner
    const combinedHistory = [
      ...state.character1.history,
      ...state.character2.history,
    ];
    // Berechne die Gesamt-Statistiken für das kombinierte System
    const combinedStats = calculateComprehensiveStats(combinedHistory);

    // Hole die manuellen Verluste aus dem geteilten State
    const manualLosses = state.characterShared.manualAdjustments || 0;
    // Die Gesamtzahl der 50/50-Events ist die Summe aus allen Siegen, Niederlagen und manuellen Verlusten
    const totalEvents =
      combinedStats.fiftyFiftyWins +
      combinedStats.fiftyFiftyLosses +
      manualLosses;

    if (totalEvents > 0) {
      const winRate = (
        (combinedStats.fiftyFiftyWins / totalEvents) *
        100
      ).toFixed(1);
      statsHTML += `<p>${translations[lang].winRate5050} <span class="font-bold text-white">${winRate}%</span> (${combinedStats.fiftyFiftyWins}/${totalEvents})</p>`;
    }
  }
  // Fall 2: Es ist ein anderer Bannertyp. Die Statistik ist isoliert.
  else {
    const fiftyFiftyTotal = stats.fiftyFiftyWins + stats.fiftyFiftyLosses;
    const manualLosses = bannerState.manualAdjustments || 0;
    const totalEvents = fiftyFiftyTotal + manualLosses;

    if (totalEvents > 0) {
      const winRate = ((stats.fiftyFiftyWins / totalEvents) * 100).toFixed(1);
      // Wähle den korrekten Schlüssel (z.B. für Waffenbanner)
      const rateKey =
        banner === "weapon" || banner === "chronicled"
          ? "winRatePath"
          : "winRate5050";
      statsHTML += `<p>${translations[lang][rateKey]} <span class="font-bold text-white">${winRate}%</span> (${stats.fiftyFiftyWins}/${totalEvents})</p>`;
    }
  }

  if (banner === "standard") {
    if (stats.total5stars > 0)
      statsHTML += `<p>${translations[lang].standard5starRatio} <span class="font-bold text-white">${stats.char5starCount} / ${stats.weapon5starCount}</span></p>`;
  }
  if (stats.total4stars > 0)
    statsHTML += `<p>${translations[lang].ratio4star} <span class="font-bold text-white">${stats.char4starCount} / ${stats.weapon4starCount}</span></p>`;
  if (stats.luckiest5star)
    statsHTML += `<p>${translations[lang].luckiestPull} <span class="font-bold text-white">${stats.luckiest5star.name} (${stats.luckiest5star.pity})</span></p>`;
  if (stats.unluckiest5star)
    statsHTML += `<p>${translations[lang].unluckiestPull} <span class="font-bold text-white">${stats.unluckiest5star.name} (${stats.unluckiest5star.pity})</span></p>`;
  if (stats.luckiest4star)
    statsHTML += `<p>${translations[lang].luckiest4star} <span class="font-bold text-white">${stats.luckiest4star.name} (${stats.luckiest4star.pity})</span></p>`;
  if (stats.unluckiest4star)
    statsHTML += `<p>${translations[lang].unluckiest4star} <span class="font-bold text-white">${stats.unluckiest4star.name} (${stats.unluckiest4star.pity})</span></p>`;

  statsContainer.innerHTML = statsHTML;
}

/**
 * Controller-Funktion, die die Neuberechnung und Anzeige des Planners steuert.
 */
function updatePlannerCalculations() {
  // 1. Hole die reinen Daten von der Model-Logik
  const results = getPlannerCalculation();
  // 2. Gib die Daten an die View-Funktion, um das DOM zu aktualisieren
  renderPlannerResults(results);
}

/**
 * Rendert die schreibgeschützte Liste der Ziele im Hauptfenster.
 */
function renderGoalDisplayList() {
  const displayList = document.getElementById("goal-display-list");
  if (!displayList) return;

  const goals = state.planner.goals || [];

  if (goals.length === 0) {
    displayList.innerHTML = `<p class="text-center text-gray-500 text-sm">No goals set. Click below to manage your goals.</p>`;
    return;
  }

  displayList.innerHTML = goals
    .map((goal) => {
      const itemDetails = findItem(goal.target);
      if (!itemDetails) return ""; // Ziel-Item nicht gefunden

      const iconUrl = getFandomImageUrl(itemDetails.type, itemDetails.name);
      const targetLevel =
        itemDetails.type === "character"
          ? `C${goal.copies - 1}`
          : `R${goal.copies}`;
      const bannerName =
        {
          character1: "Character Banner 1",
          character2: "Character Banner 2",
          weapon: "Weapon Banner",
          chronicled: "Chronicled Wish",
        }[goal.banner] || "Unknown Banner";

      return `
      <div class="goal-display-item">
        <img src="${iconUrl}" alt="${itemDetails.name}" class="item-icon rarity-${itemDetails.rarity}" onerror="this.style.display='none'">
        <div class="item-info">
          <p class="item-name">${itemDetails.name}</p>
          <p class="item-banner">${bannerName}</p>
        </div>
        <div class="item-target">${targetLevel}</div>
      </div>
    `;
    })
    .join("");
}

/**
 * Öffnet das Modal zum Bearbeiten der Ziele und füllt es mit den aktuellen Daten.
 */
function showManageGoalsModal() {
  const editList = document.getElementById("goal-edit-list");
  if (!editList) return;

  editList.innerHTML = ""; // Leere die Liste zuerst

  const goals = state.planner.goals || [];
  if (goals.length > 0) {
    goals.forEach((goal) => renderGoalEditItem(goal));
  } else {
    // Wenn keine Ziele da sind, füge ein leeres hinzu
    renderGoalEditItem();
  }

  showModal("manage-goals-modal");
}

/**
 * Rendert eine einzelne, bearbeitbare Zeile im Ziel-Management-Modal.
 * @param {object|null} goalData - Die Daten eines existierenden Ziels zum Vor-ausfüllen.
 */
function renderGoalEditItem(goalData = null) {
  const editList = document.getElementById("goal-edit-list");

  // KORREKTUR: Benutze einen zuverlässigen Zähler statt Date.now()
  goalIdCounter++;
  const goalId = `goal-edit-${goalIdCounter}`;

  const goalItemHTML = `
    <div class="goal-edit-item" id="${goalId}">
      <select class="goal-banner-type resource-input"></select>
      <select class="goal-target-item resource-input"></select>
      <select class="goal-target-level resource-input"></select>
      <button data-action="remove-element" data-target="${goalId}" class="btn btn-danger !p-2">X</button>
    </div>
  `;
  editList.insertAdjacentHTML("beforeend", goalItemHTML);

  const newGoalElement = document.getElementById(goalId);
  const bannerSelect = newGoalElement.querySelector(".goal-banner-type");

  // Fülle das Banner-Dropdown
  bannerSelect.innerHTML = `
    <option value="character1">Character 1</option>
    <option value="character2">Character 2</option>
    <option value="weapon">Weapon</option>
    <option value="chronicled">Chronicled</option>
  `;

  if (goalData) {
    bannerSelect.value = goalData.banner;
  }

  // Fülle die anderen Dropdowns basierend auf dem Banner-Typ
  populateGoalItemDropdowns(bannerSelect, goalData);

  // Event Listener, um die Item-Liste bei Banner-Wechsel neu zu laden
  bannerSelect.addEventListener("change", () =>
    populateGoalItemDropdowns(bannerSelect)
  );
}

/**
 * Speichert die im Modal konfigurierten Ziele im State und aktualisiert die UI.
 */
function handleSaveGoals() {
  const editList = document.getElementById("goal-edit-list");
  const goalItems = editList.querySelectorAll(".goal-edit-item");

  const newGoals = [];
  goalItems.forEach((item) => {
    const bannerSelect = item.querySelector(".goal-banner-type");
    const itemSelect = item.querySelector(".goal-target-item");
    const levelSelect = item.querySelector(".goal-target-level");

    if (itemSelect.value) {
      newGoals.push({
        banner: bannerSelect.value,
        target: itemSelect.value,
        copies: parseInt(levelSelect.value, 10),
      });
    }
  });

  state.planner.goals = newGoals;

  checkAchievements("plannersVision");

  saveState(); // Speichert den neuen, korrekten State
  renderGoalDisplayList(); // Aktualisiert die Anzeige
  updatePlannerCalculations(); // Berechnet die Zahlen neu
  hideModal("manage-goals-modal");
  showToast("Goals saved and calculations updated!", "success");
}

// Hilfsfunktion, um zu prüfen, ob ein Item im aktuellen Banner ist
function findItemInCurrentBanner(bannerType, itemName) {
  switch (bannerType) {
    case "character1":
    case "character2":
      return currentBannerData[bannerType]?.fiveStar === itemName;
    case "weapon":
      return (currentBannerData.weapon?.fiveStars || []).includes(itemName);
    case "chronicled":
      const chars = currentBannerData.chronicled?.fiveStars?.characters || [];
      const weps = currentBannerData.chronicled?.fiveStars?.weapons || [];
      return [...chars, ...weps].includes(itemName);
    default:
      return false;
  }
}

/**
 * Exports ALL account data to a single JSON file.
 */
function exportData() {
  // 1. Sammle alle relevanten Daten aus dem localStorage
  const accountsMeta = JSON.parse(localStorage.getItem("accounts_meta"));
  const accountsData = {};

  if (accountsMeta && accountsMeta.accounts) {
    for (const account of accountsMeta.accounts) {
      const accountState = localStorage.getItem(`account_${account.id}`);
      if (accountState) {
        accountsData[account.id] = JSON.parse(accountState);
      }
    }
  }

  // 2. Erstelle das finale Export-Objekt (gleiche Struktur wie bei Drive)
  const dataToExport = {
    version: "multi-account-v1",
    accountsMeta: {
      activeAccountId: accountsMeta.activeAccountId,
      accounts: accountsMeta.accounts,
    },
    accountsData: accountsData,
  };

  // 3. Der Download-Teil bleibt gleich, verwendet aber das neue Objekt
  const dataStr = JSON.stringify(dataToExport, null, 2);
  const blob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  // Erstelle einen sinnvollen Dateinamen
  const activeAccountName =
    accountsMeta.accounts.find((acc) => acc.id === accountsMeta.activeAccountId)
      ?.name || "accounts";
  const date = new Date().toISOString().slice(0, 10);
  link.download = `genshin-pity-tracker-backup-[${activeAccountName}]-${date}.json`;

  link.href = url;
  link.click();
  URL.revokeObjectURL(url);

  showToast("All accounts exported successfully!", "success");
}

/**
 * Imports account data from a JSON file. Supports both new multi-account
 * and legacy single-account formats.
 * @param {File} file - The JSON file selected by the user.
 */
function importData(file) {
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const importedData = JSON.parse(e.target.result);

      // VERSUCH 1: Ist es das neue Multi-Account-Format?
      if (
        importedData.version === "multi-account-v1" &&
        importedData.accountsMeta &&
        importedData.accountsData
      ) {
        console.log("Importing multi-account data...");

        // Überschreibe die Metadaten
        localStorage.setItem(
          "accounts_meta",
          JSON.stringify(importedData.accountsMeta)
        );

        // Überschreibe alle Account-Daten
        for (const accountId in importedData.accountsData) {
          localStorage.setItem(
            `account_${accountId}`,
            JSON.stringify(importedData.accountsData[accountId])
          );
        }

        showToast(
          "All accounts imported successfully! Reloading...",
          "success"
        );
        setTimeout(() => window.location.reload(), 1500);
      }
      // VERSUCH 2: Ist es das alte Single-Account-Format?
      else if (importedData.character1 || importedData.character) {
        console.log("Importing legacy single-account data...");

        const newId = `acc_${Date.now()}`;
        const newName = prompt(
          "Please name this imported account:",
          "Imported Account"
        );
        if (!newName) return; // User hat abgebrochen

        // Erstelle neue Metadaten für diesen einen Account
        const newMeta = {
          activeAccountId: newId,
          accounts: [{ id: newId, name: newName }],
          isDriveConnected: false, // Bei manuellem Import ist Drive erstmal nicht verbunden
        };
        localStorage.setItem("accounts_meta", JSON.stringify(newMeta));

        // Speichere die alten Daten unter der neuen ID
        localStorage.setItem(`account_${newId}`, JSON.stringify(importedData));

        showToast(
          "Legacy data imported as a new account! Reloading...",
          "success"
        );
        setTimeout(() => window.location.reload(), 1500);
      }
      // FALL 3: Unbekanntes Format
      else {
        throw new Error(translations[state.language].invalidFileFormat);
      }
    } catch (error) {
      showToast(
        `${translations[state.language].dataImportError}${error.message}`,
        "error"
      );
    }
  };
  reader.readAsText(file);
}

function deductResources(banner, amount) {
  const isEventBanner = banner !== "standard";
  const fateType = isEventBanner ? "intertwinedFates" : "acquaintFates";
  const primoPool =
    state.primogems +
    (state.planner.useGenesisCrystals ? state.genesisCrystals : 0);
  const totalWishesAvailable =
    state[fateType] + Math.floor(primoPool / WISH_COST);
  if (totalWishesAvailable < amount) {
    showToast(translations[state.language].noResources, "error");
    return false;
  }
  let wishesToDeduct = amount;
  const fatesToUse = Math.min(wishesToDeduct, state[fateType]);
  state[fateType] -= fatesToUse;
  wishesToDeduct -= fatesToUse;
  if (wishesToDeduct > 0) {
    let primosNeeded = wishesToDeduct * WISH_COST;
    const primosToUse = Math.min(primosNeeded, state.primogems);
    state.primogems -= primosToUse;
    primosNeeded -= primosToUse;
    if (primosNeeded > 0 && state.planner.useGenesisCrystals) {
      const genesisToUse = Math.min(primosNeeded, state.genesisCrystals);
      state.genesisCrystals -= genesisToUse;
    }
  }
  return true;
}

function abbreviateName(name, maxLength = 15) {
  if (!name) return "";
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength).trim() + "...";
}

function renderCharts(banner) {
  const bannerState = state[banner];
  const history = bannerState.history;
  const chartEl = document.getElementById(`${banner}-chart`);
  const chartContainer = chartEl ? chartEl.parentElement : null;

  if (!chartContainer || history.length === 0) {
    if (charts[banner]) {
      charts[banner].destroy();
      delete charts[banner];
    }
    if (chartContainer) chartContainer.style.display = "none";
    return;
  }

  chartContainer.style.display = "block";

  const stats = calculateComprehensiveStats(history);

  // Logik zur automatischen Auswahl des besten Diagramms
  if (!bannerState.activeChart) {
    if (stats.total5stars > 2) {
      bannerState.activeChart = "pityHistory";
    } else if (stats.total4stars > 5) {
      bannerState.activeChart = "rarityDistribution";
    } else {
      bannerState.activeChart = "pityHistory"; // Fallback
    }
  }

  // Update der Button-Aktiv-Klassen
  const chartButtons = document.querySelectorAll(
    `[data-action="set-chart-type"][data-banner="${banner}"]`
  );
  chartButtons.forEach((btn) => {
    btn.classList.toggle(
      "active",
      btn.dataset.chart === bannerState.activeChart
    );
  });

  let chartConfig = {};
  const goldAccent = getComputedStyle(document.body)
    .getPropertyValue("--gold-accent")
    .trim();
  const goldRarity = "rgba(189, 105, 50, 0.7)";
  const purpleRarity = "rgba(162, 86, 225, 0.7)";
  const blueRarity = "rgba(86, 138, 225, 0.6)";

  switch (bannerState.activeChart) {
    case "pityHistory":
      const ctx = chartEl.getContext("2d");

      // Colors matching the CSS styles (with reduced opacity for "softer" look)
      const colorWin = "rgba(255, 215, 0, 0.35)";
      const colorGuaranteed = "rgba(34, 197, 94, 0.35)";
      const colorLoss = "rgba(239, 68, 68, 0.35)";

      // CR Gradient
      const crGradient = ctx.createLinearGradient(0, 0, 0, 400);
      crGradient.addColorStop(0, "rgba(86, 167, 239, 0.35)");
      crGradient.addColorStop(1, "rgba(232, 121, 249, 0.35)");

      const bgColors = stats.history5stars.map(p => {
        if (p.outcome === 'win') return colorWin;
        if (p.outcome === 'guaranteed') return colorGuaranteed;
        if (p.outcome === 'loss') return colorLoss;
        if (p.outcome === 'capturingRadiance') return crGradient;
        return goldRarity;
      }).reverse();

      const borderColors = stats.history5stars.map(p => {
        if (p.outcome === 'win') return "#ffd700";
        if (p.outcome === 'guaranteed') return "#22c55e";
        if (p.outcome === 'loss') return "#ef4444";
        if (p.outcome === 'capturingRadiance') return "#e879f9";
        return goldAccent;
      }).reverse();

      chartConfig = {
        type: "bar",
        data: {
          labels: stats.history5stars
            .map((p) => abbreviateName(p.name))
            .reverse(),
          datasets: [
            {
              label: "5★ Pity",
              data: stats.history5stars.map((p) => p.pity).reverse(),
              backgroundColor: bgColors,
              borderColor: borderColors,
              borderWidth: 2, // Slightly thicker border for better visibility
            },
          ],
        },
        options: {
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, max: 90 } },
        },
      };
      break;

    case "rarityDistribution":
      let totalPulls;
      if (banner === "character1" || banner === "character2") {
        totalPulls = bannerState.totalPulls;
      } else {
        totalPulls = bannerState.pity + stats.totalPullsInHistory;
      }

      const total3stars = totalPulls - stats.total5stars - stats.total4stars;
      chartConfig = {
        type: "doughnut",
        data: {
          labels: ["5-Star", "4-Star", "3-Star"],
          datasets: [
            {
              data: [stats.total5stars, stats.total4stars, total3stars],
              backgroundColor: [goldRarity, purpleRarity, blueRarity],
              borderColor: "#e4e2dc",
            },
          ],
        },
        options: {
          plugins: { legend: { position: "top" } },
        },
      };
      break;

    case "typeDistribution":
      const totalChars = stats.char5starCount + stats.char4starCount;
      const totalWeapons = stats.weapon5starCount + stats.weapon4starCount;
      chartConfig = {
        type: "pie",
        data: {
          labels: ["Characters", "Weapons"],
          datasets: [
            {
              data: [totalChars, totalWeapons],
              backgroundColor: [purpleRarity, goldRarity],
              borderColor: "#e4e2dc",
            },
          ],
        },
        options: {
          plugins: { legend: { position: "top" } },
        },
      };
      break;
  }

  // Allgemeine Chart-Optionen
  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: "#9ca3af" } },
    },
    scales: {
      y: { ticks: { color: "#9ca3af" } },
      x: { ticks: { color: "#9ca3af" } },
    },
  };

  const finalConfig = deepMerge(chartConfig, { options: commonOptions });

  const ctx = chartEl.getContext("2d");

  // Helper function to safely create the chart
  const createChart = () => {
    if (typeof Chart !== 'undefined') {
      if (charts[banner]) charts[banner].destroy();
      charts[banner] = new Chart(ctx, finalConfig);
    } else {
      // Retry if Chart.js hasn't loaded yet (due to defer)
      setTimeout(createChart, 100);
    }
  };

  createChart();
}

function applyBackgroundStyle(style) {
  const canvas = document.getElementById("animated-bg");
  const body = document.body;

  if (!canvas || !body) return;

  // Setze zuerst alles zurück
  canvas.classList.add("hidden");
  body.classList.remove("bg-stars");

  // Wende den gewählten Stil an
  switch (style) {
    case "icons":
      canvas.classList.remove("hidden");
      break;
    case "stars":
      body.classList.add("bg-stars");
      break;
    case "off":
      // Nichts weiter zu tun, alles ist schon aus
      break;
  }
}

function updateBackgroundButtons() {
  const buttons = document.querySelectorAll('[data-action="set-background"]');
  buttons.forEach((btn) => {
    btn.classList.toggle(
      "active",
      btn.dataset.bgStyle === state.backgroundStyle
    );
  });
}

function renderAchievementsList() {
  const container = document.getElementById("achievements-list");
  if (!container) return;

  const lang = state.language;
  let html = "";

  for (const id in ACHIEVEMENTS) {
    const achievement = ACHIEVEMENTS[id];
    const isUnlocked = state.achievements.unlocked.includes(id);

    const title = lang === "de" ? achievement.title_de : achievement.title;
    const description =
      lang === "de" ? achievement.description_de : achievement.description;

    html += `
            <div class="flex items-center gap-4 p-3 rounded-md transition-all duration-300 ${isUnlocked ? "bg-genshin-gold/10" : "bg-black/20 opacity-60"
      }">
                <div class="text-3xl">${achievement.icon}</div>
                <div>
                    <h5 class="font-bold ${isUnlocked ? "text-genshin-gold-light" : "text-gray-400"
      }">${title}</h5>
                    <p class="text-sm text-gray-400">${description}</p>
                </div>
            </div>
        `;
  }
  container.innerHTML = html;
}

async function generateShareCard(banner) {
  showModal("share-modal");
  const container = document.getElementById("share-card-container");
  const loader = document.getElementById("share-card-loader");
  const downloadBtn = document.getElementById("share-download-btn");

  container.innerHTML = "";
  loader.classList.remove("hidden");
  downloadBtn.disabled = true;

  const loadImageWithCors = (url) => {
    return new Promise((resolve) => {
      if (!url || url === PLACEHOLDER_ICON) {
        resolve({ success: false, url: PLACEHOLDER_ICON });
        return;
      }
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => resolve({ success: true, url: url });
      img.onerror = (err) => {
        console.error(`CORS-Ladefehler für Bild: ${url}`, err);
        resolve({ success: false, url: PLACEHOLDER_ICON });
      };
      img.src = url;
    });
  };

  const bannerState = state[banner];
  const lang = state.language;

  let bannerNameKey;
  if (banner === "character1" || banner === "character2") {
    bannerNameKey = "characterBanner"; // Benutze den allgemeinen Schlüssel
  } else {
    bannerNameKey = `${banner}Banner`; // Benutze den spezifischen Schlüssel für andere Banner
  }

  const stats = calculateComprehensiveStats(bannerState.history);

  const history5star = bannerState.history.filter((p) => p.rarity === 5);
  const history4star = bannerState.history.filter((p) => p.rarity === 4);

  const last5starPull = history5star[0];
  const last4starPull = history4star[0];

  if (!last5starPull && !last4starPull) {
    container.innerHTML = `<div class="share-card"><div class="share-card-content"><div class="share-card-header"><h4>No pulls yet!</h4></div></div></div>`;
    loader.classList.add("hidden");
    downloadBtn.disabled = true;
    return;
  }

  const featuredPull = last5starPull || last4starPull;
  const featuredItem = findItem(featuredPull.name);

  let secondaryPull = null;
  let secondaryItem = null;
  if (last5starPull && last4starPull && last5starPull.id !== last4starPull.id) {
    secondaryPull = last4starPull;
    secondaryItem = findItem(secondaryPull.name);
  }

  let luckIndicator = { text: "Average", color: "text-gray-300" };
  const avgPity = parseFloat(stats.avg5starPity);
  if (!isNaN(avgPity)) {
    const hardPity = ["weapon"].includes(banner) ? 80 : 90;
    if (avgPity < hardPity * 0.5)
      luckIndicator = { text: "Blessed", color: "text-green-400" };
    else if (avgPity < hardPity * 0.8)
      luckIndicator = { text: "Good", color: "text-blue-300" };
    else if (avgPity > hardPity * 0.95)
      luckIndicator = { text: "Cursed?", color: "text-red-400" };
  } else {
    luckIndicator.text = "N/A";
  }

  const itemImageUrl = getFandomImageUrl(
    featuredItem.type,
    featuredItem.name,
    "icon"
  );
  const backgroundImageUrl = getFandomImageUrl(
    featuredItem.type,
    featuredItem.name,
    "card"
  );
  const secondaryItemImageUrl = secondaryItem
    ? getFandomImageUrl(secondaryItem.type, secondaryItem.name, "icon")
    : null;

  const imagePromises = [
    loadImageWithCors(backgroundImageUrl),
    loadImageWithCors(itemImageUrl),
  ];
  if (secondaryItemImageUrl) {
    imagePromises.push(loadImageWithCors(secondaryItemImageUrl));
  }

  const [bgResult, itemResult, secondaryItemResult] = await Promise.all(
    imagePromises
  );

  let totalPulls;
  if (banner === "character1" || banner === "character2") {
    totalPulls = bannerState.totalPulls;
  } else {
    totalPulls = bannerState.pity + stats.totalPullsInHistory;
  }

  const cardHTML = `
    <div class="share-card improved-card" id="share-card-capture">
      <div class="share-card-bg" style="background-image: url('${bgResult.url
    }')"></div>
      <div class="share-card-overlay"></div>
      <div class="share-card-content">
        
        <div class="share-card-header">
          <h4>${translations[lang][bannerNameKey]} Fortune</h4>
        </div>

        <div class="share-card-main-feature">
          <div class="share-card-pull-image-container rarity-${featuredItem.rarity
    }">
            <img src="${itemResult.url
    }" class="share-card-pull-image" style="width: 100%; height: 100%; object-fit: cover;" />
          </div>
          <div class="pull-details">
            <p class="pull-name">${featuredItem.name}</p>
            <p class="pull-rarity-stars">${"★".repeat(featuredItem.rarity)}</p>
            <p class="pull-pity">${translations[lang].pullsAt.replace(
      "{pity}",
      featuredPull.pity
    )}</p>
          </div>
        </div>
        
        ${secondaryPull
      ? `
        <div class="share-card-secondary-feature">
            <img src="${(secondaryItemResult || {}).url || PLACEHOLDER_ICON
      }" class="secondary-icon rarity-${secondaryItem.rarity}" />
            <div class="secondary-details">
                <p class="secondary-title">Last 4★ Pull</p>
                <p class="secondary-name">${secondaryItem.name} (at ${secondaryPull.pity
      })</p>
            </div>
        </div>`
      : ""
    }

        <div class="share-card-stats improved-stats">
            <div class="stat-item">
                <span class="label">${translations[lang].totalPulls}</span>
                <span class="value">${totalPulls}</span>
            </div>
            <div class="stat-item">
                <span class="label">Avg. 5★ Pity</span>
                <span class="value">${stats.avg5starPity}</span>
            </div>
            <div class="stat-item">
                <span class="label">5★ Luck</span>
                <span class="value ${luckIndicator.color}">${luckIndicator.text
    }</span>
            </div>
            <div class="stat-item">
                <span class="label">Luckiest 5★</span>
                <span class="value">${stats.luckiest5star
      ? `${stats.luckiest5star.name.split(" ")[0]} (${stats.luckiest5star.pity
      })`
      : "N/A"
    }</span>
            </div>
        </div>
        <div class="share-card-footer">
            genshin-impact-pt.com
        </div>
      </div>
    </div>`;

  container.innerHTML = cardHTML;
  loader.classList.add("hidden");
  downloadBtn.disabled = false;
}

function downloadShareCard() {
  const card = document.getElementById("share-card-capture");
  const loader = document.getElementById("share-card-loader");
  const downloadBtn = document.getElementById("share-download-btn");

  if (!card) return;

  loader.classList.remove("hidden");
  downloadBtn.disabled = true;

  const TARGET_WIDTH = 768;
  const TARGET_HEIGHT = 1024;

  setTimeout(() => {
    html2canvas(card, {
      useCORS: true,
      backgroundColor: "#2f3446",

      width: card.offsetWidth,
      height: card.offsetHeight,

      scale: TARGET_WIDTH / card.offsetWidth,
    })
      .then((canvas) => {
        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = TARGET_WIDTH;
        finalCanvas.height = TARGET_HEIGHT;
        const ctx = finalCanvas.getContext("2d");

        ctx.drawImage(canvas, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);

        const link = document.createElement("a");
        const bannerName =
          card
            .querySelector(".share-card-header h4")
            ?.textContent.trim()
            .toLowerCase()
            .replace(/\s+/g, "-") || "banner";
        const itemName =
          card
            .querySelector(".pull-name")
            ?.textContent.trim()
            .replace(/\s+/g, "-") || "card";

        link.download = `genshin-stats-${bannerName}-${itemName}.png`;
        link.href = finalCanvas.toDataURL("image/png"); // Wichtig: finalCanvas benutzen!
        link.click();
      })
      .catch((err) => {
        console.error(
          "Oops, something went wrong creating the share card!",
          err
        );
        showToast("Error creating image. Check console for details.", "error");
      })
      .finally(() => {
        loader.classList.add("hidden");
        downloadBtn.disabled = false;
      });
  }, 300);
}

function initSettingsTabs() {
  const settingsTab = document.getElementById("settings-tab");
  if (!settingsTab) {
    return;
  }

  const tabButtonsContainer = settingsTab.querySelector(
    "#settings-tab-buttons"
  );
  const tabContentContainer = settingsTab.querySelector(
    "#settings-tab-content"
  );

  if (!tabButtonsContainer || !tabContentContainer) {
    console.error("Settings tab containers not found inside #settings-tab.");
    return;
  }

  const tabButtons = tabButtonsContainer.querySelectorAll(".settings-tab-btn");
  const tabPanels = tabContentContainer.querySelectorAll(".settings-tab-panel");

  tabButtons.forEach((button) => {
    button.addEventListener("click", () => {
      const targetTab = button.dataset.tab;
      if (!targetTab) return;

      playSound("click");

      tabButtons.forEach((btn) => {
        btn.classList.remove("active");
      });
      button.classList.add("active");

      tabPanels.forEach((panel) => {
        panel.classList.add("hidden");
      });

      const targetPanel = tabContentContainer.querySelector(
        `#${targetTab}-settings-tab`
      );
      if (targetPanel) {
        targetPanel.classList.remove("hidden");
      }
    });
  });
}

function runDataMigration() {
  // Schritt 1: Prüfen, ob diese Migration bereits durchgeführt wurde.
  if (state.migrationForGuaranteedStats) {
    return; // Migration schon erledigt, nichts zu tun.
  }

  console.log("Running one-time data migration for guaranteed stats...");

  // --- TEIL 1: MIGRATION FÜR CHARAKTERBANNER ---
  const combinedCharHistory = [
    ...state.character1.history,
    ...state.character2.history,
  ].sort((a, b) => a.id - b.id);

  const fiveStarCharHistory = combinedCharHistory.filter((p) => p.rarity === 5);
  let isCharGuaranteed = false;
  let charPhantomLosses = 0;

  for (const pull of fiveStarCharHistory) {
    if (pull.outcome === "loss") {
      isCharGuaranteed = true;
    } else if (pull.outcome === "win") {
      isCharGuaranteed = false;
    } else if (pull.outcome === "guaranteed") {
      if (!isCharGuaranteed) {
        charPhantomLosses++;
      }
      isCharGuaranteed = false;
    }
  }

  if (charPhantomLosses > 0) {
    if (!state.characterShared.manualAdjustments) {
      state.characterShared.manualAdjustments = 0;
    }
    state.characterShared.manualAdjustments += charPhantomLosses;
    console.log(
      `Character Banner Migration: Found and corrected ${charPhantomLosses} phantom 50/50 losses.`
    );
  }

  // --- TEIL 2: MIGRATION FÜR WAFFEN- UND CHRONICLED-BANNER ---
  const migrateSingleBanner = (bannerName) => {
    const bannerState = state[bannerName];
    if (!bannerState || !bannerState.history) return;

    const fiveStarHistory = bannerState.history
      .filter((p) => p.rarity === 5)
      .sort((a, b) => a.id - b.id); // Wichtig: Chronologisch sortieren

    let isBannerGuaranteed = false;
    let phantomLosses = 0;

    for (const pull of fiveStarHistory) {
      if (pull.outcome === "loss") {
        isBannerGuaranteed = true;
      } else if (pull.outcome === "win") {
        isBannerGuaranteed = false;
      } else if (pull.outcome === "guaranteed") {
        if (!isBannerGuaranteed) {
          phantomLosses++;
        }
        isBannerGuaranteed = false;
      }
    }

    if (phantomLosses > 0) {
      if (!bannerState.manualAdjustments) {
        bannerState.manualAdjustments = 0;
      }
      bannerState.manualAdjustments += phantomLosses;
      console.log(
        `${bannerName.charAt(0).toUpperCase() + bannerName.slice(1)
        } Banner Migration: Found and corrected ${phantomLosses} phantom path losses.`
      );
    }
  };

  // Führe die Migration für die relevanten Banner aus
  migrateSingleBanner("weapon");
  migrateSingleBanner("chronicled");

  // Schritt 3: Setze den Flag, damit die Migration nie wieder läuft.
  state.migrationForGuaranteedStats = true;

  // Wichtig: Speichere den neuen Zustand sofort.
  saveState();
}

// Globale Variablen für den UI-Zustand
let activeMainTab = "banners";
let activeBannerTab = "character";
let activeMinigameTab = "slime-breaker";

function initNavigation() {
  // Fängt Klicks auf die Hauptnavigation (Seitenleiste/untere Leiste) ab
  document.getElementById("main-nav").addEventListener("click", (e) => {
    const button = e.target.closest("button[data-tab-target]");
    if (button) {
      e.preventDefault();
      playSound("click");
      const path = `/${button.dataset.tabTarget}`;
      navigateTo(path);
    }
  });

  // Fängt Klicks auf die Banner-Unternavigation ab
  document.getElementById("banner-sub-nav").addEventListener("click", (e) => {
    const button = e.target.closest("button[data-banner-target]");
    if (button) {
      e.preventDefault();
      playSound("click");
      const path = `/banners/${button.dataset.bannerTarget}`;
      navigateTo(path);
    }
  });

  // Fängt Änderungen am mobilen Banner-Dropdown ab
  document
    .getElementById("banner-select-mobile")
    .addEventListener("change", (e) => {
      playSound("click");
      const path = `/banners/${e.target.value}`;
      navigateTo(path);
    });

  // Hört auf die "Zurück"/"Vorwärts"-Buttons des Browsers
  window.addEventListener("popstate", handleRouting);
}

function updateActiveViews() {
  // Container-Elemente
  const mainViews = {
    banners: document.getElementById("banners-view"),
    profile: document.getElementById("profile-tab"),
    minigame: document.getElementById("minigame-tab"),
    community: document.getElementById("community-tab"),
    resources: document.getElementById("resources-tab"),
    settings: document.getElementById("settings-tab"),
    guide: document.getElementById("guide-tab"),
  };

  if (activeMainTab === "community") {
    renderLiveNowSection();
    renderLatestVideosSection();
  }

  const bannerSubNav = document.getElementById("banner-sub-nav");

  if (
    (activeMainTab !== "minigame" || activeMinigameTab !== "slime-breaker") &&
    brickBreakerAnimationId
  ) {
    cancelAnimationFrame(brickBreakerAnimationId);
    brickBreakerAnimationId = null; // Spiel anhalten und ID zurücksetzen
    console.log("Slime Breaker paused because its tab is no longer active.");
  }

  // Hauptansichten steuern
  document
    .querySelectorAll("#main-nav button[data-tab-target]")
    .forEach((btn) => {
      const isTargetActive = btn.dataset.tabTarget === activeMainTab;
      btn.classList.toggle("active", isTargetActive);
    });

  for (const key in mainViews) {
    if (mainViews[key]) {
      mainViews[key].classList.toggle("hidden", key !== activeMainTab);
    }
  }

  // Unternavigation nur anzeigen, wenn 'banners' aktiv ist
  bannerSubNav.classList.toggle("hidden", activeMainTab !== "banners");

  if (activeMainTab === "banners") {
    // Banner-Ansichten (in #banners-view) steuern
    const bannerPanels = document.querySelectorAll("#banners-view .tab-panel");
    bannerPanels.forEach((panel) => {
      panel.classList.toggle("hidden", panel.id !== `${activeBannerTab}-tab`);
    });

    // Zustand der Unternavigation synchronisieren
    document.querySelectorAll("#banner-sub-buttons button").forEach((btn) => {
      btn.classList.toggle(
        "active",
        btn.dataset.bannerTarget === activeBannerTab
      );
    });
    document.getElementById("banner-select-mobile").value = activeBannerTab;
  }

  // Startet das Spiel nur, wenn der Minigame-Tab UND der Slime-Breaker-Unter-Tab aktiv sind
  // UND noch kein Spiel läuft.
  if (
    activeMainTab === "minigame" &&
    activeMinigameTab === "slime-breaker" &&
    !brickBreakerAnimationId
  ) {
    initBrickBreakerGame();
    console.log("Slime Breaker (re)started.");
  }

  // SEO-Teil für dynamische Titel
  const baseTitle = "Genshin Impact Pity Tracker";
  let pageTitle = baseTitle;

  if (activeMainTab === "profile") {
    pageTitle = `My Profile & Collection | ${baseTitle}`;
  } else if (activeMainTab === "resources") {
    pageTitle = `Resource & Goal Planner | ${baseTitle}`;
  } else if (activeMainTab === "guide") {
    pageTitle = `Pity System Guide & Rules | ${baseTitle}`;
  } else if (activeMainTab === "community") {
    pageTitle = `Community Hub | ${baseTitle}`;
  } else if (activeMainTab === "banners" && activeBannerTab !== "character") {
    const bannerName =
      activeBannerTab.charAt(0).toUpperCase() + activeBannerTab.slice(1);
    pageTitle = `${bannerName} Banner Tracker | ${baseTitle}`;
  }

  document.title = pageTitle;
}

function updateQuickstartForms() {
  ["character1", "character2", "weapon", "chronicled", "standard"].forEach(
    (banner) => {
      const bannerState = state[banner];
      const quickstartEl = document.getElementById(`${banner}-quickstart`);

      let currentPity = 0;
      if (banner === "character1" || banner === "character2") {
        currentPity = state.characterShared.pity;
      } else {
        currentPity = bannerState.pity;
      }

      if (quickstartEl) {
        if (
          currentPity === 0 &&
          bannerState.history.length === 0 &&
          !bannerState.isQuickstartUsed
        ) {
          quickstartEl.classList.remove("hidden");
        } else {
          quickstartEl.classList.add("hidden");
        }
      }
    }
  );
}

/**
 * Updates the C-State display for a specific character banner.
 * @param {string} bannerId - The ID of the banner (e.g., 'character1').
 */
function updateCStateDisplay(bannerId) {
  const displayEl = document.getElementById(`${bannerId}-c-state-display`);
  if (!displayEl) return;

  displayEl.className = "c-state-display";

  const { cState, isCStateEstimated } = state.characterShared;
  const lang = state.language;

  // Tooltip-Texte basierend auf dem C-State
  const tooltips = {
    en: [
      "State 0: Normal 50/50 chance.",
      "State 1: Normal 50/50 chance.",
      "State 2: Slightly boosted chance for a CR win on your next 50/50.",
      "State 3: GUARANTEED WIN! Your next 50/50 will be Capturing Radiance.",
    ],
    de: [
      "Status 0: Normale 50/50-Chance.",
      "Status 1: Normale 50/50-Chance.",
      "Status 2: Leicht erhöhte Chance auf einen CR-Sieg bei deinem nächsten 50/50.",
      "Status 3: GARANTIERTER GEWINN! Dein nächstes 50/50 wird 'Gefangener Glanz' sein.",
    ],
  };

  displayEl.textContent = `C-State: ${cState}${isCStateEstimated ? "?" : ""}`;

  let pressTimer;

  // Funktion zum Anzeigen des Tooltips
  const showTooltip = () => {
    showToast(tooltips[lang][cState] || "", "info", null);
  };

  // Funktion zum Verstecken des Tooltips
  const hideTooltip = () => {
    clearTimeout(pressTimer); // Wichtig, um den Long-Press abzubrechen
    hideToast();
  };

  // Desktop Events
  displayEl.onmouseenter = showTooltip;
  displayEl.onmouseleave = hideTooltip;

  // Mobile Events (Touch)
  displayEl.ontouchstart = (e) => {
    // Verhindert, dass das Hover-Event ebenfalls feuert
    e.preventDefault();
    pressTimer = setTimeout(showTooltip, 500); // Zeige Tooltip nach 500ms
  };

  displayEl.ontouchend = hideTooltip;
  displayEl.ontouchcancel = hideTooltip;
}

/**
 * Estimates the current C-State by analyzing the entire character wish history.
 * This is intended to be run once for users with existing data.
 */
function estimateCStateFromHistory() {
  const combinedHistory = [
    ...state.character1.history,
    ...state.character2.history,
  ].sort((a, b) => a.id - b.id); // Sort chronologically

  const fiveStarPulls = combinedHistory.filter((p) => p.rarity === 5);

  let estimatedState = 1; // Start at 1 as per the theory

  for (const pull of fiveStarPulls) {
    if (pull.outcome === "win") {
      estimatedState = Math.max(0, estimatedState - 1);
    } else if (pull.outcome === "loss") {
      estimatedState++;
    }
    // "guaranteed" pulls don't change the C-State
  }

  state.characterShared.cState = estimatedState;
  state.characterShared.isCStateEstimated = true; // Mark it as an estimate
  console.log(`Estimated C-State is: ${estimatedState}`);
}

/**
 * Shows the CR confirmation modal and returns a Promise that resolves with the user's choice.
 * @returns {Promise<boolean>} A promise that resolves to `true` if the user clicked "Yes", and `false` otherwise.
 */
function showCRConfirmationModal() {
  return new Promise((resolve) => {
    const modal = document.getElementById("cr-confirmation-modal");
    const yesBtn = document.getElementById("cr-confirm-yes");
    const noBtn = document.getElementById("cr-confirm-no");

    const newYesBtn = yesBtn.cloneNode(true);
    yesBtn.parentNode.replaceChild(newYesBtn, yesBtn);
    const newNoBtn = noBtn.cloneNode(true);
    noBtn.parentNode.replaceChild(newNoBtn, noBtn);

    const onYes = () => {
      hideModal("cr-confirmation-modal");
      resolve(true); // Das Versprechen mit "true" erfüllen
    };

    const onNo = () => {
      hideModal("cr-confirmation-modal");
      resolve(false); // Das Versprechen mit "false" erfüllen
    };

    newYesBtn.addEventListener("click", onYes);
    newNoBtn.addEventListener("click", onNo);

    showModal("cr-confirmation-modal");
  });
}

function runCStateMigration() {
  if (state.cStateMigrationComplete) return; // Verhindert mehrfaches Ausführen

  console.log("Running one-time C-State estimation...");
  estimateCStateFromHistory();
  state.cStateMigrationComplete = true; // Flag setzen
  saveState(); // Zustand mit dem neuen Flag speichern
}

function startBannerTimer(containerId) {
  const timerContainer = document.getElementById(containerId);
  if (!timerContainer) return;

  const bannerEndDate = new Date(currentBannerData.endDate);

  const intervalKey = `${containerId}Interval`;
  if (window[intervalKey]) {
    clearInterval(window[intervalKey]);
  }

  const updateTimer = () => {
    const lang = state.language;
    const now = new Date();
    const timeLeft = bannerEndDate - now;

    if (timeLeft <= 0) {
      clearInterval(window[intervalKey]);

      console.log("Banner-Zeit abgelaufen. Suche nach neuer Rotation...");

      // 1. Finde die neue aktive Rotation und setze sie global
      setActiveBannerRotation();

      // 2. Rendere die gesamte UI neu, damit die neuen Banner angezeigt werden
      updateUI();

      // 3. Starte alle Timer neu mit dem Enddatum der neuen Rotation
      startBannerTimer("banner-timer-container");
      startBannerTimer("character2-banner-timer-container");
      startBannerTimer("weapon-banner-timer-container");

      console.log("UI wurde auf die neue Banner-Rotation aktualisiert.");
      return; // Wichtig, um die alte Timer-Anzeige zu verhindern
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    timerContainer.innerHTML = `
      <div class="timer-grid">
        <div><span class="time-value">${days}</span><span class="time-label">d</span></div>
        <div><span class="time-value">${hours}</span><span class="time-label">h</span></div>
        <div><span class="time-value">${minutes}</span><span class="time-label">m</span></div>
        <div><span class="time-value">${seconds}</span><span class="time-label">s</span></div>
      </div>
    `;
  };

  updateTimer();
  window[intervalKey] = setInterval(updateTimer, 1000);
}

/**
 * Rendert das "Upcoming Banners"-Modal mit klickbaren Charakter-Karten
 * und zusätzlichen Waffen-Icons.
 */
function renderUpcomingBannerModal() {
  const container = document.getElementById("upcoming-banner-content");
  if (!container) return;

  const currentIndex = BANNER_ROTATIONS.findIndex(
    (rotation) => rotation.endDate === currentBannerData.endDate
  );

  const upcomingRotation = BANNER_ROTATIONS.find((rotation, index) => {
    return index > currentIndex && !rotation.phaseType;
  });

  if (!upcomingRotation) {
    container.innerHTML = `<p class="text-gray-400">Information on the next banners is not yet available.</p>`;
    return;
  }

  // --- HIER BEGINNT DIE LOGIK-ÄNDERUNG ---

  const createCard = (itemData) => {
    const itemDetails = findItem(itemData.name);
    if (!itemDetails) return "";

    const imageUrl = getFandomImageUrl(
      itemDetails.type,
      itemDetails.name,
      "icon"
    );
    const elementColor = ELEMENT_COLORS[itemDetails.element] || "transparent";
    const style = `--element-color: ${elementColor}; --element-glow: ${elementColor}40;`;

    let itemTypeHtml = "";
    let dataAttributes = ""; // Leerer String für data-Attribute

    if (itemDetails.type === "character") {
      const elementIconUrl = ELEMENT_ICONS[itemDetails.element] || "";
      const weaponIconUrl = WEAPON_TYPE_ICONS[itemDetails.weaponType] || "";

      // Füge das Waffen-Icon LINKS vom Element-Icon hinzu
      itemTypeHtml = `
        <div class="element-display">
          <img src="${weaponIconUrl}" alt="${itemDetails.weaponType}" class="element-icon" onerror="this.style.display='none'"/>
          <img src="${elementIconUrl}" alt="${itemDetails.element}" class="element-icon" onerror="this.style.display='none'"/>
          <span>${itemDetails.element} Character</span>
        </div>
      `;

      // Füge die notwendigen Attribute hinzu, damit unser Klick-Listener reagiert
      const characterId = itemDetails.name.toLowerCase().replace(/ /g, "_");
      dataAttributes = `data-character-id="${characterId}" style="cursor: pointer;"`;
    } else {
      // Für Waffen
      itemTypeHtml = `<span>Weapon</span>`;
      // Optional: Auch Waffen klickbar machen
      // const weaponId = itemDetails.name.toLowerCase().replace(/ /g, "_");
      // dataAttributes = `data-weapon-detail-id="${weaponId}" data-item-type="weapon" style="cursor: pointer;"`;
    }

    // Füge die dataAttributes zum Haupt-Wrapper hinzu
    return `
      <div class="animated-border-wrapper" style="${style}" ${dataAttributes}>
        <div class="upcoming-banner-card">
            <img src="${imageUrl}" alt="${itemDetails.name}" onerror="this.style.display='none'"/>
            <div>
                <p class="item-name">${itemDetails.name}</p>
                <div class="item-type">${itemTypeHtml}</div>
            </div>
        </div>
      </div>
    `;
  };

  // Der Rest der Funktion bleibt gleich
  const upcomingWeaponNames = upcomingRotation.weapon?.fiveStars || [];
  const upcomingWeapons = upcomingWeaponNames.map((name) => ({
    name,
    type: "weapon",
  }));

  container.innerHTML = `
        <div>
            <h4 class="text-lg font-semibold text-gold-accent mb-2">Char 1</h4>
            <div class="space-y-3">
                ${createCard({
    name: upcomingRotation.character1?.fiveStar || "TBA",
  })}
            </div>
        </div>
        <div>
            <h4 class="text-lg font-semibold text-gold-accent mb-2">Char 2</h4>
            <div class="space-y-3">
                ${createCard({
    name: upcomingRotation.character2?.fiveStar || "TBA",
  })}
            </div>
        </div>
        <div>
            <h4 class="text-lg font-semibold text-gold-accent mb-2">Weapon</h4>
            <div class="space-y-3">
                ${upcomingWeapons.map(createCard).join("")}
            </div>
        </div>
    `;
}

function renderInventoryFilters() {
  const filtersContainer = document.getElementById("inventory-filters");
  if (!filtersContainer) return;

  const elementGroup = filtersContainer.querySelector(
    '[data-filter-group="element"]'
  );
  const weaponGroup = filtersContainer.querySelector(
    '[data-filter-group="weaponType"]'
  );

  // Element-Buttons erstellen
  elementGroup.innerHTML = Object.entries(ELEMENT_ICONS)
    .map(
      ([element, iconUrl]) => `
    <button class="inventory-filter-btn ${state.inventory.filters.element === element ? "active" : ""
        }" data-filter-type="element" data-filter-value="${element}" title="${element}">
      <img src="${iconUrl}" alt="${element}">
    </button>
  `
    )
    .join("");

  // Waffen-Buttons erstellen
  weaponGroup.innerHTML = Object.entries(WEAPON_TYPE_ICONS)
    .map(
      ([weaponType, iconUrl]) => `
    <button class="inventory-filter-btn ${state.inventory.filters.weaponType === weaponType ? "active" : ""
        }" data-filter-type="weaponType" data-filter-value="${weaponType}" title="${weaponType}">
      <img src="${iconUrl}" alt="${weaponType}">
    </button>
  `
    )
    .join("");
}

let refineContext = {}; // Globale Variable für den Kontext

function showRefineModal(targetWeaponId) {
  const targetWeapon = state.inventory.weapons.find(
    (w) => w.id === targetWeaponId
  );
  if (!targetWeapon) return;

  const copies = state.inventory.weapons.filter(
    (w) => w.name === targetWeapon.name && w.id !== targetWeapon.id
  );
  if (copies.length === 0) {
    showToast("No copies available for refinement.", "error");
    return;
  }

  refineContext = { target: targetWeapon, copies: copies };

  const targetContainer = document.getElementById("refine-target-weapon");
  const materialSelect = document.getElementById("refine-material-select");

  const targetItemDetails = findItem(targetWeapon.name);
  const iconUrl = getFandomImageUrl("weapon", targetWeapon.name);

  targetContainer.innerHTML = `
    <img src="${iconUrl}" alt="${targetWeapon.name}" class="h-12 w-12 flex-shrink-0" onerror="this.src='${PLACEHOLDER_ICON}'" />
    <div>
      <p class="font-bold">${targetWeapon.name}</p>
      <p class="text-sm text-gold-dark">Current: R${targetWeapon.refinement}</p>
    </div>
  `;

  materialSelect.innerHTML = copies
    .map(
      (copy) =>
        `<option value="${copy.id}">Refinement R${copy.refinement
        } (ID: ...${String(copy.id).slice(-4)})</option>`
    )
    .join("");

  updateRefinePreview();
  showModal("refine-weapon-modal");
}

function updateRefinePreview() {
  const { target } = refineContext;
  const materialSelect = document.getElementById("refine-material-select");
  const previewContainer = document.getElementById("refine-result-preview");

  const selectedMaterialId = parseFloat(materialSelect.value);
  const materialWeapon = state.inventory.weapons.find(
    (w) => w.id === selectedMaterialId
  );

  if (!materialWeapon) {
    previewContainer.innerHTML = "";
    return;
  }

  const currentRefinement = target.refinement;
  const materialRefinement = materialWeapon.refinement;
  const newRefinement = Math.min(5, currentRefinement + materialRefinement);

  previewContainer.innerHTML = `
    R${currentRefinement} + R${materialRefinement}  →  <span class="text-green-400">New: R${newRefinement}</span>
  `;
}

function handleRefineConfirm() {
  const { target } = refineContext;
  const materialSelect = document.getElementById("refine-material-select");
  if (!target || !materialSelect) return;

  const selectedMaterialId = parseFloat(materialSelect.value);

  const targetWeaponIndex = state.inventory.weapons.findIndex(
    (w) => w.id === target.id
  );
  const materialWeaponIndex = state.inventory.weapons.findIndex(
    (w) => w.id === selectedMaterialId
  );

  if (targetWeaponIndex === -1 || materialWeaponIndex === -1) {
    showToast("An error occurred. Weapon not found.", "error");
    hideModal("refine-weapon-modal");
    return;
  }

  const targetWeapon = state.inventory.weapons[targetWeaponIndex];
  const materialWeapon = state.inventory.weapons[materialWeaponIndex];

  targetWeapon.refinement = Math.min(
    5,
    targetWeapon.refinement + materialWeapon.refinement
  );

  // Material-Waffe entfernen
  state.inventory.weapons.splice(materialWeaponIndex, 1);

  showToast(
    `${targetWeapon.name} refined to R${targetWeapon.refinement}!`,
    "success"
  );

  refineContext = {}; // Kontext zurücksetzen
  saveAndRefresh();
  hideModal("refine-weapon-modal");
}

/**
 * Steuert die Anzeige von speziellen Info-Blöcken während "Lull"- oder "Server Down"-Phasen.
 * @param {string} bannerId Die ID des Banners (z.B., "character1", "weapon").
 */
function handleSpecialBannerPhase(bannerId) {
  const interactiveContent = document.getElementById(
    `${bannerId}-interactive-content`
  );
  const lullInfo = document.getElementById(`${bannerId}-lull-info`);
  const serverDownInfo = document.getElementById(
    `${bannerId}-server-down-info`
  );

  const phaseType = currentBannerData.phaseType;

  if (phaseType === "lull") {
    if (bannerId !== "standard" && lullInfo) {
      interactiveContent.classList.add("hidden");
      lullInfo.classList.remove("hidden");
      startLullTimer(bannerId);
      renderYoutubeLinks("lull");
    }
  } else if (phaseType === "server_down") {
    if (serverDownInfo) {
      interactiveContent.classList.add("hidden");
      serverDownInfo.classList.remove("hidden");
      renderDowntimePreview(bannerId);
      startDowntimeTimer(bannerId);
      renderYoutubeLinks("server_down");
    }
  }
}

/**
 * Startet den Countdown-Timer für die Lull-Phase bis zum Beginn der Server-Wartung.
 * @param {string} bannerId Die ID des Banners, in dem der Timer angezeigt wird.
 */
function startLullTimer(bannerId) {
  const timerContainer = document.getElementById(`lull-timer-${bannerId}`);
  if (!timerContainer) return;

  // Das Ziel-Datum ist das Enddatum der aktuellen "lull"-Phase.
  // Das ist genau der Zeitpunkt, an dem die "server_down"-Phase beginnt.
  const maintenanceStartDate = new Date(currentBannerData.endDate);

  const intervalKey = `lullTimerInterval_${bannerId}`;
  if (window[intervalKey]) clearInterval(window[intervalKey]);

  const updateTimer = () => {
    const now = new Date();
    const timeLeft = maintenanceStartDate - now;

    if (timeLeft <= 0) {
      clearInterval(window[intervalKey]);
      // Wenn die Zeit abgelaufen ist, wird das System durch den normalen
      // Banner-Wechsel-Prozess sowieso die UI aktualisieren.
      setActiveBannerRotation();
      updateUI();
      return;
    }

    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    // Formatierung wie im Server-Down-Timer
    timerContainer.textContent = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  updateTimer();
  window[intervalKey] = setInterval(updateTimer, 1000);
}

/**
 * Startet den Countdown-Timer für die Server-Down-Phase.
 * @param {string} bannerId Die ID des Banners, in dem der Timer angezeigt wird.
 */
function startDowntimeTimer(bannerId) {
  const timerContainer = document.getElementById(`downtime-timer-${bannerId}`);
  if (!timerContainer) return;

  // Finde die NÄCHSTE Rotation nach der aktuellen "server_down"-Phase
  const currentIndex = BANNER_ROTATIONS.findIndex(
    (r) => r.endDate === currentBannerData.endDate
  );
  const nextRotation = BANNER_ROTATIONS[currentIndex + 1];
  if (!nextRotation) return;

  // Das Startdatum der nächsten Rotation ist das Enddatum der Server-Down-Phase
  const newVersionStartDate = new Date(currentBannerData.endDate);

  const intervalKey = `downtimeTimerInterval_${bannerId}`;
  if (window[intervalKey]) clearInterval(window[intervalKey]);

  const updateTimer = () => {
    const now = new Date();
    const timeLeft = newVersionStartDate - now;

    if (timeLeft <= 0) {
      clearInterval(window[intervalKey]);
      // Hier passiert dasselbe wie im normalen Banner-Timer: App neu laden/aktualisieren
      setActiveBannerRotation();
      updateUI();
      // Starte die normalen Timer neu
      startBannerTimer("banner-timer-container");
      // ... und für die anderen Banner auch
      return;
    }

    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    timerContainer.textContent = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  updateTimer();
  window[intervalKey] = setInterval(updateTimer, 1000);
}

/**
 * Zeigt eine Vorschau der nächsten Banner in der Server-Down-Anzeige an.
 * @param {string} bannerId Die ID des Banners, in dem die Vorschau angezeigt wird.
 */
function renderDowntimePreview(bannerId) {
  const previewContainer = document.getElementById(
    `downtime-preview-${bannerId}`
  );
  if (!previewContainer) return;

  const currentIndex = BANNER_ROTATIONS.findIndex(
    (r) => r.endDate === currentBannerData.endDate
  );
  const nextRotation = BANNER_ROTATIONS[currentIndex + 1];

  if (!nextRotation) {
    previewContainer.innerHTML = `<p>Coming soon...</p>`;
    return;
  }

  const char1 = findItem(nextRotation.character1?.fiveStar);
  const char2 = findItem(nextRotation.character2?.fiveStar);

  // Hilfsfunktion, um wiederholten Code zu vermeiden
  const createPreviewCard = (character) => {
    if (!character) return "";

    const characterId = character.name.toLowerCase().replace(/ /g, "_");

    return `
            <!-- Diese div bekommt jetzt die data-Attribute und einen cursor-pointer -->
            <div 
                class="flex items-center gap-2 p-2 rounded bg-black bg-opacity-20 justify-center cursor-pointer hover:bg-black/40 transition-colors"
                data-character-id="${characterId}"
            >
                <img src="${getFandomImageUrl(
      "character",
      character.name,
      "icon"
    )}" class="h-8 w-8 rounded-full border-2 border-gold-dark" onerror="this.style.display='none'">
                <span class="font-bold text-sm text-gold-light">${character.name
      }</span>
            </div>
        `;
  };

  previewContainer.innerHTML =
    createPreviewCard(char1) + createPreviewCard(char2);
}

const TWITCH_CHANNELS = [
  "genshinimpactofficial",
  "zy0x",
  "mistahfeet",
  "doro44",
  "SharkH3art",
  "thejonathon",
];

const YOUTUBE_CHANNELS = {
  official: [
    { name: "Official Genshin Impact", id: "UCiS882YPwZt1NfaM0gR0D9Q" },
  ],
  guides: [
    { name: "Zy0x", id: "UCgo5pn2U8qbl_WYXU8Q6f0w" },
    { name: "mistahfeet", id: "UCKPikWKJCCenLfmaVHl0R7A" },
    { name: "Ramen Gamer", id: "UCp9mut4N2ByD4KNYKqmv0bA" },
    { name: "Hey Raion", id: "UC9VGcKCAsQ4zQIWr-wNCyOw" },
  ],
  creative: [
    { name: "RaidenShotgun", id: "UCX1jnI9TeMSVfEMU71up25w" },
    { name: "ChibiWanderers Teapot", id: "UC0IbkLdWKXUmODozGaHBXPw" },
    { name: "Doro44", id: "UCQ3TV3Z4UHlNiiPSCsgvTsw" },
    { name: "GIBlacky", id: "UCljuaE6Hi-DWvetT7Ka2y3Q" },
    { name: "jadealy", id: "UCETaObjm6u-rv8JR8anExrw" },
    { name: "KillerKokomi", id: "UC0QJyOhDc9x0geIkoTNBOvw" },
    { name: "sharkh3art", id: "UCPTjzBgnx3hi4tSPlh-cKaQ" },
    { name: "Thejonathon", id: "UCYhmIuoe_5tqdQlB7AhWq0w" },
  ],
};

/**
 * Ruft die Live-Twitch-Streams sicher über unseren Cloudflare Worker ab.
 */
async function getLiveTwitchStreams() {
  try {
    // Ruft jetzt den spezifischen Twitch-Endpunkt auf
    const response = await fetch("https://genshin-impact-pt.com/api/twitch");
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error("Error fetching from Twitch API worker:", error);
    return [];
  }
}

/**
 * Überprüft, welche YouTube-Kanäle gerade live sind.
 */
async function getLiveYouTubeStreams() {
  const liveStreams = [];
  const allYouTubeChannels = [
    ...YOUTUBE_CHANNELS.official,
    ...YOUTUBE_CHANNELS.guides,
    ...YOUTUBE_CHANNELS.creative,
  ];

  for (const channel of allYouTubeChannels) {
    try {
      // Ruft für jeden Kanal den sicheren Worker auf
      const response = await fetch(
        `https://genshin-impact-pt.com/api/youtube-live?id=${channel.id}`
      );
      const data = await response.json();

      if (data.isLive) {
        liveStreams.push({
          user_name: channel.name,
          thumbnail_url: `https://i.ytimg.com/vi/${data.videoId}/mqdefault_live.jpg`,
          viewer_count: "Live",
          title: `Live Stream: ${channel.name}`,
          url: `https://www.youtube.com/watch?v=${data.videoId}`,
          platform: "youtube",
        });
      }
    } catch (e) {
      console.error(
        `Error checking YouTube live status for ${channel.name}:`,
        e
      );
    }
  }
  return liveStreams;
}

/**
 * Holt die neuesten Videos von YouTube und zeigt sie im Community Hub an.
 */
async function renderLatestVideosSection() {
  const container = document.getElementById("latest-videos-container");
  if (!container) return;

  container.innerHTML = `<p class="text-gray-400 text-center md:col-span-2 lg:col-span-3">Fetching latest Genshin videos...</p>`;

  const allYouTubeChannels = [
    ...YOUTUBE_CHANNELS.guides,
    ...YOUTUBE_CHANNELS.creative,
  ];
  const channelsToFeature = getRandomElements(allYouTubeChannels, 4);
  let allVideos = [];

  for (const channel of channelsToFeature) {
    try {
      const response = await fetch(
        `https://genshin-impact-pt.com/api/youtube-rss?id=${channel.id}`
      );
      const filteredVideos = await response.json();

      // Füge den Kanalnamen zu jedem Video hinzu und packe sie in unsere Gesamtliste
      filteredVideos.forEach((video) => {
        allVideos.push({ ...video, channelName: channel.name });
      });
    } catch (e) {
      console.error(`Could not fetch videos for ${channel.name}`, e);
    }
  }

  if (allVideos.length === 0) {
    container.innerHTML = `<p class="text-gray-400 text-center md:col-span-2 lg:col-span-3">No recent Genshin videos found from featured creators.</p>`;
    return;
  }

  // Nimm die 6 neuesten Videos aus allen Ergebnissen
  const sortedVideos = allVideos.sort(() => 0.5 - Math.random()).slice(0, 6);

  container.innerHTML = sortedVideos
    .map(
      (video) => `
        <div class="video-card" data-action="play-youtube-video" data-video-id="${video.videoId}">
            <img src="${video.thumbnail}" class="thumbnail" alt="Video thumbnail">
            <div class="video-overlay"></div>
            <div class="play-icon">▶</div>
            <div class="video-info">
                <p class="video-title">${video.title}</p>
                <p class="video-channel">${video.channelName}</p>
            </div>
        </div>
    `
    )
    .join("");
}

/**
 * Öffnet das Modal und spielt ein YouTube-Video ab.
 * @param {string} videoId Die ID des YouTube-Videos.
 */
function showYouTubeModal(videoId) {
  const modal = document.getElementById("youtube-player-modal");
  const player = document.getElementById("youtube-iframe");
  if (!modal || !player) return;

  player.src = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  showModal("youtube-player-modal");
}

/**
 * Rendert die "Live Now"-Sektion im Community Hub.
 */
async function renderLiveNowSection() {
  const section = document.getElementById("live-now-section");
  const container = document.getElementById("live-now-container");
  if (!container || !section) return;

  container.innerHTML = `<p class="text-gray-400 text-center md:col-span-2 lg:col-span-3">Checking for live streams...</p>`;
  section.classList.remove("hidden");

  const [twitchStreams, youtubeStreams] = await Promise.all([
    getLiveTwitchStreams(),
    getLiveYouTubeStreams(),
  ]);

  const allLiveStreams = [
    ...twitchStreams.map((s) => ({
      ...s,
      platform: "twitch",
      url: `https://twitch.tv/${s.user_login}`,
    })),
    ...youtubeStreams,
  ];

  if (allLiveStreams.length === 0) {
    section.classList.add("hidden");
    return;
  }

  let html = "";
  allLiveStreams.forEach((stream) => {
    const thumbnailUrl = stream.thumbnail_url
      .replace("{width}", "320")
      .replace("{height}", "180");
    const platformColor = stream.platform === "twitch" ? "#9146FF" : "#FF0000";
    html += `
            <a href="${stream.url
      }" target="_blank" rel="noopener noreferrer" class="live-card">
                <div class="thumbnail-container">
                    <img src="${thumbnailUrl}" class="thumbnail" alt="Stream thumbnail for ${stream.user_name
      }">
                    <div class="live-indicator" style="background-color: ${platformColor};">LIVE</div>
                    ${stream.viewer_count
        ? `<div class="viewer-count">${typeof stream.viewer_count === "number"
          ? stream.viewer_count.toLocaleString()
          : stream.viewer_count
        }</div>`
        : ""
      }
                </div>
                <div class="stream-info">
                    <p class="stream-title" style="border-left-color: ${platformColor};">${stream.title || stream.user_name
      }</p>
                    <p class="stream-user">${stream.user_name} on ${stream.platform.charAt(0).toUpperCase() + stream.platform.slice(1)
      }</p>
                </div>
            </a>
        `;
  });

  container.innerHTML = html;
}

/**
 * Wählt zufällig eine Anzahl von Elementen aus einem Array.
 * @param {Array} arr Das Array, aus dem ausgewählt wird.
 * @param {number} num Die Anzahl der auszuwählenden Elemente.
 * @returns {Array} Ein neues Array mit den zufälligen Elementen.
 */
function getRandomElements(arr, num) {
  return [...arr].sort(() => 0.5 - Math.random()).slice(0, num);
}

/**
 * Generiert und rendert die YouTube-Link-Buttons für eine bestimmte Phase.
 * @param {string} phase 'lull' oder 'server_down'
 */
function renderYoutubeLinks(phase) {
  const containers = document.querySelectorAll(
    `.youtube-links-container-${phase}`
  );
  if (containers.length === 0) return;

  let channelsToShow = [];
  if (phase === "lull") {
    channelsToShow = [
      ...YOUTUBE_CHANNELS.official,
      ...getRandomElements(YOUTUBE_CHANNELS.guides, 2),
    ];
  } else {
    // server_down
    channelsToShow = getRandomElements(YOUTUBE_CHANNELS.creative, 3);
  }

  let buttonsHTML = channelsToShow
    .map(
      (channel) => `
        <a href="https://www.youtube.com/channel/${channel.id}" target="_blank" class="btn btn-youtube w-full text-sm">
            ${channel.name}
        </a>
    `
    )
    .join("");

  buttonsHTML += `
        <a href="#" 
           class="btn btn-youtube-special w-full text-sm"
           data-action="open-random-playlist" 
        >
            ✨ Surprise Me!
        </a>
    `;

  containers.forEach((container) => {
    container.innerHTML = buttonsHTML;
  });
}

/**
 * Wählt einen zufälligen YouTube-Kanal und öffnet dessen Upload-Playlist in einem neuen Tab.
 */
function openRandomPlaylist() {
  const allChannels = [
    ...YOUTUBE_CHANNELS.guides,
    ...YOUTUBE_CHANNELS.creative,
  ];
  const randomChannel = getRandomElements(allChannels, 1)[0];

  if (!randomChannel) {
    console.error("Could not select a random channel.");
    return;
  }

  const uploadPlaylistId = "UU" + randomChannel.id.substring(2);
  const url = `https://www.youtube.com/playlist?list=${uploadPlaylistId}`;

  // Öffnet die URL in einem neuen Tab
  window.open(url, "_blank");
}

/**
 * Wendet während der Lull- oder Server-Down-Phase einen einheitlichen Hintergrund
 * auf alle Banner-Karten an, basierend auf dem nächsten anstehenden Charakter.
 */
function applySpecialPhaseBackgrounds() {
  // 1. Finde die nächste *echte* Banner-Rotation (überspringt Lull/Down-Phasen)
  const currentIndex = BANNER_ROTATIONS.findIndex(
    (r) => r.endDate === currentBannerData.endDate
  );
  const upcomingRotation = BANNER_ROTATIONS.find((rotation, index) => {
    return index > currentIndex && !rotation.phaseType;
  });

  // Wenn keine nächste Rotation gefunden wird (Ende der Liste), tue nichts.
  if (!upcomingRotation) return;

  // 2. Hole dir den Namen und die Bild-URL des "char1" aus dieser Rotation
  const nextCharName = upcomingRotation.character1?.fiveStar;
  if (!nextCharName) return;

  const itemDetails = findItem(nextCharName);
  if (!itemDetails) return;

  const backgroundUrl = getFandomImageUrl(
    itemDetails.type,
    itemDetails.name,
    "card"
  );

  // 3. Definiere alle Banner-Karten, die den Hintergrund erhalten sollen
  const bannerCardIds = [
    "character1-banner-card",
    "character2-banner-card",
    "weapon-banner-card",
    "chronicled-banner-card",
    "standard-banner-card",
  ];

  // 4. Wende den Stil auf jede einzelne Karte an
  bannerCardIds.forEach((id) => {
    const card = document.getElementById(id);
    if (card) {
      card.style.backgroundImage = `linear-gradient(rgba(47, 52, 70, 0.88), rgba(47, 52, 70, 0.99)), url('${backgroundUrl}')`;
    }
  });
}

function renderChangelogModal() {
  const container = document.getElementById("changelog-content");
  if (!container) return;

  const lang = state.language;
  let html = `
    <!-- NEUER, AUFFÄLLIGER BACKUP-HINWEIS -->
    <div class="backup-warning-box">
        <h4 class="font-semibold text-lg" data-i18n-key="backupReminderTitle">${translations[lang].backupReminderTitle}</h4>
        <p class="text-sm mt-1" data-i18n-key="backupReminderText">${translations[lang].backupReminderText}</p>
    </div>

    <div class="p-3 rounded-md border border-dashed border-gold-dark bg-black bg-opacity-20 mb-6">
      <h4 class="font-semibold text-lg text-gold-accent" data-i18n-key="changelogRefreshTitle">${translations[lang].changelogRefreshTitle}</h4>
      <p class="text-sm" data-i18n-key="changelogRefreshDesktop">${translations[lang].changelogRefreshDesktop}</p>
      <p class="text-sm mt-1" data-i18n-key="changelogRefreshMobile">${translations[lang].changelogRefreshMobile}</p>
    </div>
  `;

  CHANGELOG.forEach((entry) => {
    html += `
      <div class="mb-4">
        <h4 class="font-bold text-xl">${entry.version
      } <span class="text-base font-normal text-gray-400">- ${entry.date
      }</span></h4>
        <ul class="list-disc list-inside mt-2 space-y-1 text-gray-300">
          ${entry.changes.map((change) => `<li>${change}</li>`).join("")}
        </ul>
      </div>
    `;
  });

  container.innerHTML = html;
}

function renderCommunityHub() {
  const grid = document.getElementById("community-grid");
  if (!grid) return;

  let html = "";
  COMMUNITY_LINKS.forEach((section) => {
    // Füge eine Überschrift für jede Sektion hinzu
    html += `<h2 class="text-2xl font-bold text-gold-accent md:col-span-2 lg:col-span-3 mb-2">${section.category}</h2>`;

    section.links.forEach((link) => {
      html += `
                <div class="community-card">
                    <div class="flex flex-col text-center items-center p-6 h-full">
                        <img src="${link.icon}" alt="${link.title}" class="h-16 w-16 mb-4" onerror="this.style.display='none'">
                        <h3 class="text-xl font-bold font-headings text-gold-light">${link.title}</h3>
                        <p class="text-sm text-gray-400 flex-grow my-2">${link.description}</p>
                        <a href="${link.url}" target="_blank" rel="noopener noreferrer" class="btn btn-primary mt-4 w-full">
                            Visit
                        </a>
                    </div>
                </div>
            `;
    });
  });

  grid.innerHTML = html;
}

function checkForNewVersion() {
  // Stellt sicher, dass die Eigenschaft existiert, falls ein alter Speicherstand geladen wird
  if (!state.lastSeenVersion) {
    state.lastSeenVersion = "0.0.0";
  }

  // Einfacher String-Vergleich funktioniert für das Format "x.y.z"
  if (APP_VERSION > state.lastSeenVersion) {
    const indicator = document.querySelector(".changelog-new-indicator");
    if (indicator) {
      indicator.classList.remove("hidden");
    }
  }
}

/**
 * Schaltet die sichtbaren Tabs im Profil-Bereich visuell um.
 * @param {string} targetTab - Die ID des anzuzeigenden Tabs (z.B. 'analysis' or 'collection').
 */
function setActiveProfileTab(targetTab) {
  // Alle Buttons deaktivieren, dann den richtigen aktivieren
  document.querySelectorAll(".profile-tab-btn").forEach((btn) => {
    btn.classList.toggle("active", btn.dataset.profileTab === targetTab);
  });

  // Alle Inhalts-Panels verstecken, dann das richtige anzeigen
  document.querySelectorAll(".profile-content").forEach((content) => {
    content.classList.add("hidden");
  });
  const targetContent = document.getElementById(`profile-${targetTab}-content`);
  if (targetContent) {
    targetContent.classList.remove("hidden");
  }
}

function initEventListeners() {
  // ==========================================================================
  // 1. SETUP FÜR DIE INTERAKTIVE LOGIK
  // ==========================================================================
  let currentlySelectedItem = null; // Speichert die aktuell ausgewählte Inventar-Karte
  let lastInputMethod = "mouse"; // Erkennt, ob Maus oder Touch verwendet wird
  let touchMoved = false; // Verhindert, dass Scrollen als "Tippen" zählt

  // Listener, die die Eingabemethode erkennen
  document.body.addEventListener(
    "touchstart",
    () => {
      lastInputMethod = "touch";
      touchMoved = false;
    },
    { passive: true }
  );

  document.body.addEventListener(
    "touchmove",
    () => {
      touchMoved = true;
    },
    { passive: true }
  );

  // ==========================================================================
  // 2. DER ZENTRALE UND EINZIGE BODY-KLICK-LISTENER
  // Dieser Listener steuert alle Klick-Interaktionen mit einer klaren Prioritäten-Reihenfolge.
  // ==========================================================================
  document.body.addEventListener("click", (e) => {
    // Identifiziere alle möglichen Klick-Ziele
    const actionElement = e.target.closest("[data-action]");
    const characterIcon = e.target.closest("[data-character-id]");
    const weaponIcon = e.target.closest("[data-weapon-detail-id]");
    const profileTabButton = e.target.closest(".profile-tab-btn");
    const filterButton = e.target.closest(".inventory-filter-btn");
    const clickedCard = e.target.closest(".inventory-item-card");

    // --- PRIORITÄT 1: EXPLIZITE AKTIONEN ([data-action]) ---
    // Wenn ein Button mit einer klaren Anweisung geklickt wird, wird diese immer zuerst ausgeführt.
    if (actionElement) {
      handleDataAction(actionElement, e);
      // Hebe eine eventuelle Auswahl auf, da eine Aktion ausgeführt wurde.
      if (currentlySelectedItem) {
        currentlySelectedItem.classList.remove("selected");
        currentlySelectedItem = null;
      }
      return; // Beende die Funktion hier, um Konflikte zu verhindern.
    }

    // --- PRIORITÄT 2: TAB-NAVIGATION ---
    // Wenn ein Tab-Button geklickt wird, wechsle den Tab.
    if (profileTabButton) {
      e.preventDefault(); // Wichtig, um das Standard-Verhalten zu stoppen
      playSound("click");
      const target = profileTabButton.dataset.profileTab;
      navigateTo(`/profile/${target}`); // Navigiere zur neuen URL
      return;
    }
    if (filterButton) {
      const type = filterButton.dataset.filterType;
      const value = filterButton.dataset.filterValue;

      playSound("click");

      // Wenn der bereits aktive Button geklickt wird, deaktiviere den Filter
      if (state.inventory.filters[type] === value) {
        state.inventory.filters[type] = null;
      } else {
        state.inventory.filters[type] = value;
      }

      // Zustand speichern und UI neu rendern
      saveAndRefresh();
      return;
    }

    // --- PRIORITÄT 3: INTERAKTIVE ICONS (INFO-MODALS) ---
    // Wenn ein Charakter- oder Waffen-Icon geklickt wurde.
    const interactiveIcon = characterIcon || weaponIcon;
    if (interactiveIcon) {
      // TOUCH-LOGIK: Zwei-Phasen-Klick (nur für Inventar-Karten)
      if (lastInputMethod === "touch" && !touchMoved && clickedCard) {
        if (clickedCard === currentlySelectedItem) {
          // Zweiter Tap -> Info-Modal öffnen und Auswahl aufheben
          openInfoModal(interactiveIcon);
          currentlySelectedItem.classList.remove("selected");
          currentlySelectedItem = null;
        } else {
          // Erster Tap -> Karte auswählen
          playSound("click");
          if (currentlySelectedItem) {
            currentlySelectedItem.classList.remove("selected");
          }
          clickedCard.classList.add("selected");
          currentlySelectedItem = clickedCard;
        }
      } else {
        // MAUS-LOGIK (oder Klick außerhalb des Inventars): Direkte Aktion
        openInfoModal(interactiveIcon);
      }
      return; // Beende die Funktion.
    }

    // --- PRIORITÄT 4: KLICK INS LEERE ZUM ABWÄHLEN ---
    // Wenn nichts Interaktives geklickt wurde, aber ein Item ausgewählt ist, hebe die Auswahl auf.
    if (currentlySelectedItem) {
      currentlySelectedItem.classList.remove("selected");
      currentlySelectedItem = null;
    }
  });

  // ==========================================================================
  // 3. HILFSFUNKTIONEN FÜR DEN ZENTRALEN LISTENER
  // ==========================================================================

  // Öffnet das korrekte Info-Modal für Charaktere oder Waffen
  function openInfoModal(element) {
    playSound("click");
    if (element.dataset.characterId) {
      showCharacterDetailModal(element.dataset.characterId);
    } else if (element.dataset.weaponDetailId) {
      showItemDetailModal(
        element.dataset.itemType,
        element.dataset.weaponDetailId
      );
    }
  }

  document
    .getElementById("c-state-manual-select")
    ?.addEventListener("change", (e) => {
      const value = parseInt(e.target.value, 10);
      if (value === -1) {
        estimateCStateFromHistory();
      } else {
        state.characterShared.cState = value;
        state.characterShared.isCStateEstimated = false; // Manuell gesetzt, also kein Schätzwert
      }
      saveAndRefresh();
    });

  // Behandelt alle Klicks auf Elemente mit [data-action]
  function handleDataAction(element, event = null) {
    if (
      (element.tagName === "BUTTON" || element.tagName === "A") &&
      element.disabled
    )
      return;
    if (
      element.tagName === "BUTTON" ||
      element.classList.contains("pending-button") ||
      element.classList.contains("refine-indicator")
    ) {
      playSound("click");
    }
    const d = element.dataset;
    switch (d.action) {
      case "show-edit-inventory-modal":
        if (event) event.stopPropagation();
        showEditInventoryModal(d.type, d.type === "character" ? d.name : d.id);
        break;
      case "set-language":
        setLanguage(d.lang);
        break;
      case "set-theme":
        setTheme(d.theme);
        break;
      case "set-background":
        state.backgroundStyle = d.bgStyle;
        applyBackgroundStyle(state.backgroundStyle);
        updateBackgroundButtons();
        saveState();
        break;
      case "export-data":
        exportData();
        break;
      case "import-data-trigger":
        dom.importInput.click();
        break;
      case "add-wish":
        addWishes(d.banner, parseInt(d.amount, 10));
        break;
      case "buy-fate":
        if (state.starglitter < 5) {
          showToast(translations[state.language].notEnoughStarglitter, "error");
          return;
        }
        state.starglitter -= 5;
        if (d.fateType === "intertwined") state.intertwinedFates++;
        else if (d.fateType === "acquaint") state.acquaintFates++;
        saveAndRefresh();
        break;
      case "show-ten-pull-modal":
        showTenPullModal(d.banner);
        break;
      case "show-resolve-modal":
        showResolveModal(d.banner, parseInt(d.rarity, 10));
        break;
      case "toggle-guarantee":
        toggleGuarantee(d.banner);
        break;
      case "set-wish-priority":
        state[d.banner].wishPriority = d.priority;
        saveAndRefresh();
        break;
      case "set-history-filter":
        state[d.banner].historyFilter = d.filter;
        saveAndRefresh();
        break;
      case "show-confirm-modal":
        showConfirmationModal(d.type, {
          banner: d.banner,
          id: d.id ? parseInt(d.id, 10) : undefined,
        });
        break;
      case "hide-modal":
        hideModal(d.target);
        if (d.target === "youtube-player-modal") {
          document.getElementById("youtube-iframe").src = "";
        }
        break;
      case "cancel-confirmation":
        hideModal(d.target);
        updateUI();
        break;
      case "show-modal":
        if (event) event.preventDefault();
        showModal(d.target);
        if (d.target === "changelog-modal") {
          const indicator = document.querySelector(".changelog-new-indicator");
          if (indicator) indicator.classList.add("hidden");
          state.lastSeenVersion = APP_VERSION;
          saveState();
        }
        break;
      case "show-item-detail":
        showItemDetailModal(d.itemType, d.itemId);
        break;
      case "add-pull-entry":
        addPullEntry();
        break;
      case "save-pity":
        {
          const banner = d.banner;
          const inputEl = document.getElementById(`${banner}-pity-input`);
          const pityValue = parseInt(inputEl.value, 10);
          const hardPity =
            banner === "character1" ||
              banner === "character2" ||
              banner === "standard" ||
              banner === "chronicled"
              ? 90
              : 80;
          if (!isNaN(pityValue)) {
            const clampedPity = Math.max(0, Math.min(pityValue, hardPity - 1));
            if (banner === "character1" || banner === "character2") {
              state.characterShared.pity = clampedPity;
            } else {
              state[banner].pity = clampedPity;
            }
            state[banner].isQuickstartUsed = true;
            showToast("Pity saved!", "success");
            saveAndRefresh();
          } else {
            showToast("Invalid number", "error");
          }
        }
        break;
      case "remove-element":
        document.getElementById(d.target)?.remove();
        break;
      case "set-chart-type":
        state[d.banner].activeChart = d.chart;
        renderCharts(d.banner);
        saveState();
        break;
      case "download-share-card":
        downloadShareCard();
        break;
      case "show-share-modal":
        generateShareCard(d.banner);
        break;
      case "show-upcoming-banner-modal":
        showModal("upcoming-banner-modal");
        break;
      case "switch-character-banner":
        activeCharacterBanner = d.bannerTarget;
        updateActiveCharacterBannerView();
        break;
      case "minigame-restart":
        document.getElementById("game-canvas")?.restartGame();
        break;
      case "minigame-reset-highscore":
        showConfirmationModal("reset-highscore", {});
        break;
      case "toggle-mute":
        state.audio.soundEffects = !state.audio.soundEffects;
        if (state.audio.soundEffects && state.audio.masterVolume === 0) {
          state.audio.masterVolume = 0.25;
        }
        updateAudioUI();
        saveState();
        break;
      case "apply-constellation":
        {
          const charName = d.name;
          const character = state.inventory.characters[charName];
          if (character && character.pendingConstellations > 0) {
            showConfirmationModal("apply-constellation", {
              itemName: charName,
            });
          }
        }
        break;
      case "show-add-inventory-modal":
        showAddInventoryModal(d.type);
        break;
      case "show-refine-modal":
        if (event) event.stopPropagation();
        showRefineModal(parseFloat(d.weaponId));
        break;
      case "confirm-refine":
        handleRefineConfirm();
        break;
      case "set-inventory-sort":
        state.inventory.sort = d.sortType;
        saveAndRefresh(); // Speichert die neue Einstellung und rendert die UI neu
        break;
      case "open-random-playlist":
        if (event) event.preventDefault(); // Verhindert, dass der Link zu "#" springt
        openRandomPlaylist();
        break;
      case "play-youtube-video":
        showYouTubeModal(d.videoId);
        break;
      case "add-account":
        createNewAccount();
        break;

      case "rename-account":
        renameCurrentAccount();
        break;

      case "delete-account":
        deleteCurrentAccount();
        break;
      case "show-wish-simulator":
        showWishSimulator();
        break;
      case "close-wish-simulator":
        // Wichtig: Animationen stoppen, falls das Modal geschlossen wird
        document.getElementById("simulator-reveal-grid").innerHTML = "";
        hideModal("wish-simulator-modal");
        break;
      case "start-simulation":
        startSimulation();
        break;
      case "simulate-again":
        // Setzt die Ergebnis-Ansicht zurück und zeigt das Setup wieder an
        document
          .getElementById("simulator-setup-view")
          .classList.remove("hidden");
        document
          .getElementById("simulator-results-view")
          .classList.add("hidden");
        document.getElementById("simulator-reveal-grid").innerHTML = "";
        break;
      case "simulate-next-page":
        {
          // Alle aktuellen Karten ausblenden lassen
          const grid = document.getElementById("simulator-reveal-grid");
          grid.querySelectorAll(".sim-pull-card").forEach((card) => {
            card.classList.add("exiting");
          });

          // Warten, bis die Animation abgeschlossen ist, dann die nächste Seite laden
          setTimeout(() => {
            animateNextSimulationPage();
          }, 500); // Muss zur CSS transition-duration passen
        }
        break;
      case "rename-team":
        break;
      case "add-new-team":
        addNewTeam();
        break;
      case "add-team-member":
        showAddTeamMemberModal(
          parseInt(d.teamIndex, 10),
          parseInt(d.memberIndex, 10)
        );
        break;
      case "edit-team-member":
        showEditTeamMemberModal(
          parseInt(d.teamIndex, 10),
          parseInt(d.memberIndex, 10)
        );
        break;
      case "save-member":
        handleTeamUpdate("save-member");
        break;
      case "remove-member":
        handleTeamUpdate("remove-member");
        break;
      case "show-manage-goals-modal":
        showManageGoalsModal();
        break;
      case "add-goal-to-edit-list":
        renderGoalEditItem();
        break;
      case "save-goals":
        handleSaveGoals();
        break;
      case "navigate-to-guide":
        if (event) event.preventDefault();
        navigateTo(`/guide/${d.guideTarget}`);
        break;
    }
  }

  // ==========================================================================
  // 4. ALLE ANDEREN, NICHT-DELEGIERTEN EVENT LISTENERS
  // Diese sind spezifisch für Formular-Elemente und ändern keinen globalen Zustand.
  // ==========================================================================
  document
    .getElementById("refine-material-select")
    ?.addEventListener("change", updateRefinePreview);
  dom.primogemInput.addEventListener("blur", updateResources);
  dom.genesisInput.addEventListener("blur", updateResources);
  dom.starglitterInput.addEventListener("blur", updateResources);
  dom.intertwinedInput.addEventListener("blur", updateResources);
  dom.acquaintInput.addEventListener("blur", updateResources);
  dom.welkinCheckbox.addEventListener("change", updateResources);
  dom.bpCheckbox.addEventListener("change", updateResources);
  dom.useGenesisCheckbox.addEventListener("change", updateResources);
  dom.importInput.addEventListener("change", (event) => {
    const file = event.target.files[0];
    if (file) {
      importData(file);
    }
    event.target.value = "";
  });
  document
    .getElementById("resolve-confirm-btn")
    .addEventListener("click", handleResolveConfirm);
  document
    .getElementById("ten-pull-confirm-btn")
    .addEventListener("click", handleTenPullConfirm);
  dom.confirmationConfirmBtn.addEventListener("click", () => {
    const {
      action: a,
      banner: b,
      id: i,
      itemName: n,
    } = dom.confirmationConfirmBtn.dataset;
    if (a === "reset") handleResetConfirm(b);
    else if (a === "delete") handleDeleteConfirm(b, parseInt(i, 10));
    else if (a === "set-course") handleSetCourseConfirm(b, n);
    else if (a === "reset-highscore") handleResetHighscoreConfirm();
    else if (a === "apply-constellation") handleApplyConstellationConfirm(n);
    else if (a === "confirm-delete-inventory-char") deleteInventoryCharacter(n);
    else if (a === "confirm-delete-inventory-weapon") deleteInventoryWeapon(i);
    else if (a === "delete-team") deleteTeam(parseInt(i, 10));
  });
  document
    .getElementById("add-inventory-confirm-btn")
    .addEventListener("click", handleAddInventoryConfirm);
  document
    .getElementById("edit-inventory-save-btn")
    .addEventListener("click", handleEditInventorySave);
  document
    .getElementById("edit-inventory-delete-btn")
    .addEventListener("click", handleEditInventoryDelete);
  document
    .getElementById("profile-name-input")
    ?.addEventListener("blur", handleProfileUpdate);
  document
    .querySelectorAll('input[name="traveler-choice"]')
    ?.forEach((radio) => {
      radio.addEventListener("change", handleProfileUpdate);
    });
  const volumeSlider = document.getElementById("volume-slider");
  if (volumeSlider) {
    // Das 'input'-Event feuert bei jeder kleinen Bewegung des Reglers.
    volumeSlider.addEventListener("input", (e) => {
      // 1. Lese den neuen Wert aus dem Regler und konvertiere ihn in eine Zahl.
      const newVolume = parseFloat(e.target.value);

      // 2. Aktualisiere den globalen Zustand mit dem neuen Wert.
      state.audio.masterVolume = newVolume;

      // 3. Wenn die Lautstärke über 0 ist, stelle sicher, dass die Effekte "an" sind.
      if (newVolume > 0) {
        state.audio.soundEffects = true;
      }

      // 4. Aktualisiere die UI (vor allem das Stumm-Symbol).
      updateAudioUI();

      // 5. Speichere den neuen Zustand.
      saveState();
    });
  }
  document
    .getElementById("authorize-button")
    .addEventListener("click", handleAuthClick);
  document
    .getElementById("save-to-drive-button")
    .addEventListener("click", saveToDrive);
  document
    .getElementById("load-from-drive-button")
    .addEventListener("click", loadFromDrive);
  document
    .getElementById("disconnect-drive-button")
    .addEventListener("click", handleSignoutClick);
  document.getElementById("account-select")?.addEventListener("change", (e) => {
    const selectedAccountId = e.target.value;
    switchAccount(selectedAccountId);
  });
  const minigameNav = document.getElementById("minigame-sub-nav");
  if (minigameNav) {
    minigameNav.addEventListener("click", (e) => {
      const button = e.target.closest("button[data-minigame-tab]");
      if (!button) return;

      playSound("click");
      const path = `/minigame/${button.dataset.minigameTab}`;
      navigateTo(path);
    });
  }
  // Listener für das Charakter-Auswahl-Modal im Team-Builder
  document
    .getElementById("add-team-member-modal")
    .addEventListener("click", (e) => {
      const charCard = e.target.closest(
        '[data-action="select-team-character"]'
      );
      if (charCard) {
        handleTeamUpdate("select-character", {
          charName: charCard.dataset.charName,
        });
      }
    });

  // Listener für das Umbenennen von Teams (beim Verlassen des Feldes)
  document.getElementById("teams-grid").addEventListener(
    "blur",
    (e) => {
      if (e.target.matches('[data-action="rename-team"]')) {
        const teamIndex = parseInt(e.target.dataset.teamIndex, 10);
        const newName = e.target.value;
        state.teams[teamIndex].name = newName;
        saveState();
      }
    },
    true
  );

  const artifactModal = document.getElementById("edit-team-member-modal");
  if (artifactModal) {
    artifactModal.addEventListener("click", (e) => {
      // Prüfen, ob eine Checkbox geklickt wurde
      if (e.target.classList.contains("team-artifact-checkbox")) {
        // 1. Alle aktuell ausgewählten Checkboxen im Modal finden
        const checkedBoxes = artifactModal.querySelectorAll(
          ".team-artifact-checkbox:checked"
        );

        // 2. Wenn mehr als 2 ausgewählt sind, die Aktion rückgängig machen
        if (checkedBoxes.length > 2) {
          showToast("You can only select up to 2 artifact sets.", "error");
          e.target.checked = false; // Den gerade gesetzten Haken wieder entfernen
        }
      }
    });
  }
}
function handleFileLaunch() {
  if ("launchQueue" in window) {
    window.launchQueue.setConsumer(async (launchParams) => {
      // Prüfe, ob die App mit Dateien gestartet wurde
      if (!launchParams.files.length) {
        return;
      }
      const fileHandle = launchParams.files[0];
      const file = await fileHandle.getFile();

      importData(file);
    });
  }
}

function handleApplyConstellationConfirm(name) {
  const character = state.inventory.characters[name];
  if (character && character.pendingConstellations > 0) {
    if (character.constellation < 6) {
      character.constellation++;
      character.pendingConstellations--;
      saveAndRefresh();
    } else {
      showToast("Character is already at C6. Cannot apply.", "error");
    }
  }
  hideModal("confirmation-modal");
}

function handleResetHighscoreConfirm() {
  state.minigame.highScore = 0;
  saveState();
  hideModal("confirmation-modal");
}

function initAnimatedBackground() {
  const canvas = document.getElementById("animated-bg");

  if (!canvas) return;

  const ctx = canvas.getContext("2d");
  let particles = [];
  let loadedIcons = [];

  // Diese Variablen speichern die letzte bekannte Größe
  let lastWidth = 0;
  let lastHeight = 0;

  const iconUrls = [
    ...Object.values(ELEMENT_ICONS),
    ...Object.values(WEAPON_TYPE_ICONS),
    CURRENCY_ICONS.primogems,
  ];

  const loadIcons = async () => {
    const promises = iconUrls.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = () => {
          console.warn(`Could not load icon: ${src}`);
          resolve(null);
        };
      });
    });
    loadedIcons = (await Promise.all(promises)).filter(Boolean);
  };

  const setupParticles = () => {
    // WICHTIG: Lese die Größe direkt vom CSS-gerenderten Element, nicht vom Window-Objekt.
    // Das synchronisiert die Zeichenfläche mit der stabilen 100svh-Größe.
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    lastWidth = canvas.width;
    lastHeight = canvas.height;

    particles = [];
    const particleCount = Math.floor((canvas.width * canvas.height) / 15000);

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        icon: loadedIcons[Math.floor(Math.random() * loadedIcons.length)],
        size: Math.random() * 15 + 12,
        opacity: Math.random() * 0.6 + 0.2,
      });
    }
  };

  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < -p.size) p.x = canvas.width + p.size;
      if (p.x > canvas.width + p.size) p.x = -p.size;
      if (p.y < -p.size) p.y = canvas.height + p.size;
      if (p.y > canvas.height + p.size) p.y = -p.size;
      ctx.globalAlpha = p.opacity;
      if (p.icon) {
        ctx.drawImage(p.icon, p.x, p.y, p.size, p.size);
      }
      ctx.globalAlpha = 1.0;
    });
    requestAnimationFrame(animate);
  };

  // Diese Funktion prüft, ob die Größenänderung groß genug ist
  const handleResize = () => {
    const newWidth = window.innerWidth;
    const newHeight = window.innerHeight;

    // Führe setupParticles nur aus, wenn sich die Breite ändert (z.B. Rotation)
    if (newWidth !== lastWidth) {
      console.log(
        "Significant resize detected (width changed). Re-rendering particles."
      );
      setupParticles();
    }
  };

  window.addEventListener("resize", debounce(handleResize, 250));

  // Initialisierung
  loadIcons().then(() => {
    setupParticles();
    animate();
  });
}

function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function initInteractiveGuide() {
  const tabsContainer = document.querySelector(".guide-tabs");
  const slider = document.querySelector(".guide-panels-slider");
  const panelsContainer = document.querySelector(".guide-panels-container");
  const tabs = document.querySelectorAll(".guide-tab-btn");
  const panels = document.querySelectorAll(".guide-panel");

  if (!tabsContainer || !slider || !panelsContainer) return;

  let touchstartX = 0;
  let touchendX = 0;
  let touchstartY = 0;
  let touchendY = 0;

  panelsContainer.addEventListener(
    "touchstart",
    (e) => {
      touchstartX = e.changedTouches[0].screenX;
      touchstartY = e.changedTouches[0].screenY;
    },
    { passive: true }
  );

  panelsContainer.addEventListener("touchend", (e) => {
    touchendX = e.changedTouches[0].screenX;
    touchendY = e.changedTouches[0].screenY;
    handleSwipe();
  });

  const handleSwipe = () => {
    const allTabs = Array.from(tabs);
    const currentIndex = allTabs.findIndex((tab) =>
      tab.classList.contains("active")
    );
    const deltaX = touchendX - touchstartX;
    const deltaY = touchendY - touchstartY;

    // Nur bei horizontalem Swipe reagieren
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 30) {
      let nextIndex = currentIndex;
      if (deltaX < 0 && currentIndex < tabs.length - 1) {
        // Swipe nach links
        nextIndex = currentIndex + 1;
      }
      if (deltaX > 0 && currentIndex > 0) {
        // Swipe nach rechts
        nextIndex = currentIndex - 1;
      }

      if (nextIndex !== currentIndex) {
        const nextTab = allTabs[nextIndex];

        // Umgekehrte Map erstellen, um von der Tab-ID zur Page-ID zu kommen
        const guidePageIdMap = {
          "what-is-pity": "guide-tab-1",
          "how-to-use": "guide-tab-2",
          "banner-rules": "guide-tab-3",
          "capturing-radiance": "guide-tab-cr",
          "wish-simulator": "guide-tab-sim",
          "character-wiki": "guide-tab-4",
          privacy: "guide-tab-5",
          planner: "guide-tab-planner",
        };
        const reverseMap = Object.fromEntries(
          Object.entries(guidePageIdMap).map(([key, value]) => [value, key])
        );

        const pageId = reverseMap[nextTab.id];
        if (pageId) {
          // KORREKTUR: Rufe den Router auf, anstatt die Klassen direkt zu ändern
          navigateTo(`/guide/${pageId}`);
        }
      }
    }
  };
}

/**
 * Zentralisierte Funktion zum Aktivieren eines bestimmten Guide-Tabs und Panels.
 * @param {string} pageId - Die ID der Unterseite (z.B. "planner", "what-is-pity").
 */
function setActiveGuideTab(pageId) {
  const guidePageIdMap = {
    "what-is-pity": "guide-tab-1",
    "how-to-use": "guide-tab-2",
    "banner-rules": "guide-tab-3",
    "capturing-radiance": "guide-tab-cr",
    "wish-simulator": "guide-tab-sim",
    "character-wiki": "guide-tab-4",
    privacy: "guide-tab-5",
    planner: "guide-tab-planner",
  };

  const tabs = document.querySelectorAll(".guide-tab-btn");
  const panels = document.querySelectorAll(".guide-panel");

  // Finde den Ziel-Tab. Wenn die pageId ungültig ist, nimm den ersten Tab als Standard.
  const targetTabId = guidePageIdMap[pageId] || "guide-tab-1";

  let targetIndex = -1;
  tabs.forEach((tab, index) => {
    if (tab.id === targetTabId) {
      targetIndex = index;
    }
  });

  // Fallback, falls etwas schiefgeht: Immer den ersten Tab anzeigen.
  if (targetIndex === -1) targetIndex = 0;

  // Alle Tabs/Panels durchgehen und nur den Ziel-Index aktivieren.
  tabs.forEach((tab, i) => {
    const isActive = i === targetIndex;
    tab.classList.toggle("active", isActive);
    tab.setAttribute("aria-selected", String(isActive));
  });

  panels.forEach((panel, i) => {
    panel.classList.toggle("active", i === targetIndex);
  });
}

/**
 * Verarbeitet die aktuelle URL-Route, aktualisiert die Ansicht und die Meta-Tags.
 */
function handleRouting() {
  const path = window.location.pathname;
  const segments = path.split("/").filter(Boolean);

  const mainTabTarget = segments[0] || "banners";
  const subTabTarget = segments[1];

  let metaKey = "default";
  if (segments.length === 2) {
    metaKey = segments.join("/"); // z.B. 'guide/capturing-radiance'
  } else if (segments.length === 1) {
    metaKey = segments[0]; // z.B. 'guide' oder 'profile'
  }

  // Lade die Metadaten für die aktuelle Sprache und Route
  const lang = state.language;
  const routeMeta = (META_DATA[metaKey] || META_DATA.default)[lang];

  // --- META-TAGS AKTUALISIEREN ---
  document.title = routeMeta.title;
  const canonicalUrl = `https://genshin-impact-pt.com${path}`;

  // Standard-Tags
  document
    .querySelector("#meta-description")
    ?.setAttribute("content", routeMeta.description);

  // Open Graph Tags
  document.querySelector("#og-title")?.setAttribute("content", routeMeta.title);
  document
    .querySelector("#og-description")
    ?.setAttribute("content", routeMeta.description);
  document.querySelector("#og-url")?.setAttribute("content", canonicalUrl);

  // Twitter Tags
  document
    .querySelector("#twitter-title")
    ?.setAttribute("content", routeMeta.title);
  document
    .querySelector("#twitter-description")
    ?.setAttribute("content", routeMeta.description);
  document.querySelector("#twitter-url")?.setAttribute("content", canonicalUrl);

  // --- ANSICHT AKTUALISIEREN ---
  const validMainTabs = [
    "banners",
    "profile",
    "minigame",
    "community",
    "resources",
    "settings",
    "guide",
  ];
  activeMainTab = validMainTabs.includes(mainTabTarget)
    ? mainTabTarget
    : "banners";

  if (activeMainTab === "banners") {
    const validBannerTabs = ["character", "weapon", "chronicled", "standard"];
    activeBannerTab = validBannerTabs.includes(subTabTarget)
      ? subTabTarget
      : "character";
  }

  if (activeMainTab === "minigame") {
    const validMinigameTabs = ["slime-breaker", "wish-simulator"];
    // Wenn kein Unter-Tab angegeben ist, ist slime-breaker der Standard
    activeMinigameTab = validMinigameTabs.includes(subTabTarget)
      ? subTabTarget
      : "slime-breaker";
    updateActiveMinigameView();
  }

  updateActiveViews();

  if (activeMainTab === "profile") {
    const profileSubTab = subTabTarget || "analysis";
    setActiveProfileTab(profileSubTab);
  }

  if (activeMainTab === "guide") {
    // Rufe einfach die zentrale Funktion auf. Sie kümmert sich um alles.
    // Wenn subTabTarget leer ist, wird automatisch der erste Tab angezeigt.
    setActiveGuideTab(subTabTarget);
  }
}

function updateActiveMinigameView() {
  const minigameNav = document.getElementById("minigame-sub-nav");
  const panels = document.querySelectorAll(".minigame-panel");

  if (!minigameNav || !panels) return;

  // Buttons aktualisieren
  minigameNav.querySelectorAll("button[data-minigame-tab]").forEach((btn) => {
    btn.classList.toggle(
      "active",
      btn.dataset.minigameTab === activeMinigameTab
    );
  });

  // Panels anzeigen/verstecken
  panels.forEach((panel) => {
    panel.classList.toggle("hidden", panel.id !== `${activeMinigameTab}-panel`);
  });
}

/**
 * Navigiert zu einer neuen Route und aktualisiert die URL in der Adressleiste.
 * @param {string} path - Der neue Pfad, z.B. '/profile' oder '/banners/weapon'.
 */
function navigateTo(path) {
  // Ändert die URL in der Browser-Leiste
  window.history.pushState({}, "", path);
  // Führt unsere Routing-Logik aus, um die Ansicht zu aktualisieren
  handleRouting();
}

function initBrickBreakerGame() {
  // =================================================================
  // 1. SETUP & VARIABLENDEKLARATION
  // =================================================================
  const canvas = document.getElementById("game-canvas");
  if (!canvas) return;
  const ctx = canvas.getContext("2d");

  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const logicalWidth = rect.width;
  const logicalHeight = rect.height;

  const assets = {};
  let assetsLoaded = 0;
  const assetsToLoad = {
    ...Object.fromEntries(
      Object.entries(SLIME_ICONS).map(([elem, url]) => [
        `slime_${elem.toLowerCase()}`,
        url,
      ])
    ),
    powerUp_addBall: CURRENCY_ICONS.intertwinedFates,
    ball: ELEMENT_ICONS.Pyro,
    background:
      "/image-proxy/Achievement:_Colors_of_the_Rainbow?file=Namecard_Background_Achievement_Colors_of_the_Rainbow.png",
    player: "/image-proxy/Paimon?file=Heated_Battle_Mode_Paimon.png",
    nudgeSticker1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_03_Diona_2.png",
    nudgeSticker2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_03_Diona_1.png",
    gameOverSticker:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_02_Qiqi_1.png",
    powerUp_elementalBurst: CURRENCY_ICONS.primogems,
    sticker_huTao1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_05_Hu_Tao_4.png",
    sticker_huTao2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_05_Hu_Tao_1.png",
    sticker_huTao3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_05_Hu_Tao_6.png",
    sticker_ganyu1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_04_Ganyu_6.png",
    sticker_ganyu2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_04_Ganyu_5.png",
    sticker_ganyu3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_04_Ganyu_1.png",
    sticker_zhongli1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_03_Zhongli_3.png",
    sticker_zhongli2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_03_Zhongli_4.png",
    sticker_zhongli3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_03_Zhongli_1.png",
    sticker_kazuha1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_07_Kaedehara_Kazuha_1.png",
    sticker_kazuha2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_07_Kaedehara_Kazuha_2.png",
    sticker_kazuha3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_07_Kaedehara_Kazuha_4.png",
    sticker_kokomi1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_09_Sangonomiya_Kokomi_4.png",
    sticker_kokomi2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_09_Sangonomiya_Kokomi_3.png",
    sticker_kokomi3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_09_Sangonomiya_Kokomi_1.png",
    sticker_nahida1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_19_Nahida_3.png",
    sticker_nahida2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_19_Nahida_5.png",
    sticker_nahida3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_19_Nahida_4.png",
    sticker_raiden1:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_13_Raiden_Shogun_4.png",
    sticker_raiden2:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_13_Raiden_Shogun_2.png",
    sticker_raiden3:
      "/image-proxy/Chat/Gallery?file=Icon_Emoji_Paimon%27s_Paintings_13_Raiden_Shogun_1.png",
  };
  const assetKeys = Object.keys(assetsToLoad);
  const totalAssets = assetKeys.length;

  let lastTime = 0;

  let gameState = "loading";
  let currentBalls,
    ballsToLaunch,
    launchInterval,
    turnCount,
    roundStartTime,
    turnsSinceLastPowerUp;

  let isSpedUp = false;
  let showSpeedUpText = false;
  let nudgeTimerStartTime = 0;
  let showNudgeSticker1 = false;
  let showNudgeSticker2 = false;
  let stickerAnimation = { scale: 0, alpha: 0 };
  let activeStickerSequence = null;

  let player = {},
    mouse = {},
    balls = [],
    blocks = [],
    powerUps = [];
  let particles = [];
  let shake = { magnitude: 0, duration: 0, startTime: 0, active: false };
  let activeEffects = [];
  let combo = {
    count: 0,
    displayCount: 0,
    lastHitTime: 0,
    maxDisplayTime: 2000,
    scale: 1.2,
    alpha: 0,
  };

  const blockConfig = { cols: 8, width: logicalWidth / 9, padding: 4 };
  const ballSettings = {
    speed: logicalWidth * 1.1,
    maxSpeed: logicalWidth * 3,
    speedUpDelay: 5000,
    speedUpFactor: 1.005,
  };
  const ballRadius = logicalWidth * 0.025;
  const powerUpRadius = logicalWidth * 0.04;
  const playerRadius = logicalWidth * 0.05;
  const animationSpeed = logicalHeight * 0.6;
  const POWERUP_CONFIG = {
    pyro: {
      stickerKeys: ["sticker_huTao1", "sticker_huTao2", "sticker_huTao3"],
      effect: () => {
        const pyroSlimes = blocks.filter((b) => b.spriteKey === "slime_pyro");
        const targets = getRandomElements(
          pyroSlimes,
          Math.min(pyroSlimes.length, 3)
        );
        targets.forEach((target, index) => {
          setTimeout(() => {
            const explosionRadius = blockConfig.width * 2.5;
            activeEffects.push({
              type: "explosion",
              x: target.x,
              y: target.y,
              radius: explosionRadius,
              maxLifespan: 500,
              lifespan: 500,
              color: ELEMENT_COLORS.Pyro,
            });
            soundQueue.push({ type: "destroy", volume: 1.0 });
            triggerShake(8, 300);

            for (let i = blocks.length - 1; i >= 0; i--) {
              const block = blocks[i];
              const dist = Math.hypot(block.x - target.x, block.y - target.y);
              if (dist < explosionRadius) {
                block.hp = 0;
              }
            }
          }, index * 200);
        });
      },
    },
    cryo: {
      stickerKeys: ["sticker_ganyu1", "sticker_ganyu2", "sticker_ganyu3"],
      effect: () => {
        const randomBlock = getRandomElements(blocks, 1)[0];
        if (!randomBlock) return;

        const { x: colX, y: rowY } = randomBlock;
        activeEffects.push({
          type: "frost_line",
          x: colX,
          y: 0,
          width: 10,
          height: logicalHeight,
          maxLifespan: 3000,
          lifespan: 3000,
          color: ELEMENT_COLORS.Cryo,
        });
        activeEffects.push({
          type: "frost_line",
          x: 0,
          y: rowY,
          width: logicalWidth,
          height: 10,
          maxLifespan: 3000,
          lifespan: 3000,
          color: ELEMENT_COLORS.Cryo,
        });
        soundQueue.push({ type: "swirl", volume: 0.8 });

        blocks.forEach((block) => {
          if (
            Math.abs(block.x - colX) < block.width / 2 ||
            Math.abs(block.y - rowY) < block.height / 2
          ) {
            block.isFrozen = true;
          }
        });
      },
    },
    geo: {
      stickerKeys: ["sticker_zhongli1", "sticker_zhongli2", "sticker_zhongli3"],
      effect: () => {
        const colsToDestroy = [
          ...new Set(
            getRandomElements(blocks, 2).map((b) =>
              Math.floor(b.x / (blockConfig.width + blockConfig.padding))
            )
          ),
        ];
        colsToDestroy.forEach((colIndex, i) => {
          setTimeout(() => {
            const colX =
              colIndex * (blockConfig.width + blockConfig.padding) +
              (blockConfig.width / 2 + blockConfig.padding);
            activeEffects.push({
              type: "geo_pillar",
              x: colX,
              maxLifespan: 800,
              lifespan: 800,
              color: ELEMENT_COLORS.Geo,
            });
            soundQueue.push({ type: "destroy", volume: 1.0 });
            triggerShake(10, 400);

            blocks.forEach((block) => {
              if (Math.abs(block.x - colX) < block.width / 2) {
                block.hp = 0;
              }
            });
          }, i * 300);
        });
      },
    },
    anemo: {
      stickerKeys: ["sticker_kazuha1", "sticker_kazuha2", "sticker_kazuha3"],
      effect: () => {
        const lowestY = Math.max(...blocks.map((b) => b.y), 0);
        activeEffects.push({
          type: "anemo_sweep",
          y: lowestY,
          maxLifespan: 1000,
          lifespan: 1000,
          color: ELEMENT_COLORS.Anemo,
        });
        soundQueue.push({ type: "swirl", volume: 1.0 });
        triggerShake(5, 500);

        for (let i = blocks.length - 1; i >= 0; i--) {
          if (blocks[i].y >= lowestY - blocks[i].height / 2) {
            blocks[i].hp = 0;
          }
        }
      },
    },
    hydro: {
      stickerKeys: ["sticker_kokomi1", "sticker_kokomi2", "sticker_kokomi3"],
      effect: () => {
        const hydroSlimes = blocks.filter((b) => b.spriteKey === "slime_hydro");
        const targets = getRandomElements(
          hydroSlimes,
          Math.min(hydroSlimes.length, 5)
        );
        targets.forEach((target, i) => {
          setTimeout(() => {
            target.hp = 0;
            activeEffects.push({
              type: "bubble_pop",
              x: target.x,
              y: target.y,
              radius: target.width,
              maxLifespan: 400,
              lifespan: 400,
              color: ELEMENT_COLORS.Hydro,
            });
            soundQueue.push({ type: "hit", volume: 0.8 });
          }, i * 100);
        });
      },
    },
    dendro: {
      stickerKeys: ["sticker_nahida1", "sticker_nahida2", "sticker_nahida3"],
      effect: () => {
        const dendroSlimes = blocks.filter(
          (b) => b.spriteKey === "slime_dendro" && !b.isSpawned
        );
        dendroSlimes.forEach((slime) => {
          slime.hp = 0;
        });
      },
    },
    electro: {
      stickerKeys: ["sticker_raiden1", "sticker_raiden2", "sticker_raiden3"],
      effect: () => {
        // Wählt die Hälfte aller Slimes zufällig aus
        const targets = getRandomElements(blocks, Math.ceil(blocks.length / 2));

        targets.forEach((target, i) => {
          // Blitze schlagen gestaffelt ein für einen coolen Effekt
          setTimeout(() => {
            target.hp = 0; // Direkte Zerstörung

            // Visueller Blitz-Effekt von oben
            activeEffects.push({
              type: "lightning_strike",
              x: target.x,
              y: target.y,
              maxLifespan: 400,
              lifespan: 400,
              color: ELEMENT_COLORS.Electro,
            });

            soundQueue.push({ type: "destroy", volume: 0.8 });
            triggerShake(6, 150);
          }, i * 50); // Sehr schnelle Abfolge
        });
      },
    },
  };
  const SLIME_CONFIG = {
    hydro: {
      spriteKey: "slime_hydro",
      spawnTurn: 1,
      capTurn: 15,
      onDefeat: (block) => {
        activeEffects.push({
          type: "puddle",
          x: block.x,
          y: block.y,
          radius: blockConfig.width * 0.8,
          maxLifespan: 8000,
          lifespan: 8000,
          color: ELEMENT_COLORS.Hydro,
          hasSplitBall: false,
        });
      },
    },
    anemo: {
      spriteKey: "slime_anemo",
      spawnTurn: 16,
      capTurn: 30,
      onDefeat: (block) => {
        const shockwaveRadius = blockConfig.width * 3.0;
        const pushStrength = 200;
        soundQueue.push({ type: "push", volume: 0.7 });
        activeEffects.push({
          type: "shockwave",
          x: block.x,
          y: block.y,
          radius: shockwaveRadius,
          maxLifespan: 400,
          lifespan: 400,
          color: ELEMENT_COLORS.Anemo,
        });
        blocks.forEach((otherBlock) => {
          const dist = Math.hypot(
            otherBlock.x - block.x,
            otherBlock.y - block.y
          );
          if (dist > 0 && dist < shockwaveRadius) {
            const angle = Math.atan2(
              otherBlock.y - block.y,
              otherBlock.x - block.x
            );
            otherBlock.pushVx =
              (otherBlock.pushVx || 0) +
              Math.cos(angle) * pushStrength * (1 - dist / shockwaveRadius);
            otherBlock.pushVy =
              (otherBlock.pushVy || 0) +
              Math.sin(angle) * pushStrength * (1 - dist / shockwaveRadius);
          }
        });
      },
    },
    dendro: {
      spriteKey: "slime_dendro",
      spawnTurn: 31,
      capTurn: 45,
      onDefeat: (block) => {
        if (block.isSpawned) {
          return;
        }
        const spawnCount = 2;
        for (let i = 0; i < spawnCount; i++) {
          blocks.push({
            x: block.x + (Math.random() - 0.5) * blockConfig.width,
            y: block.y + (Math.random() - 0.5) * blockConfig.width,
            width: blockConfig.width * 0.6,
            height: blockConfig.width * 0.6,
            hp: 1,
            sprite: assets.slime_dendro,
            spriteKey: "slime_dendro",
            isShieldBroken: false,
            isSpawned: true,
          });
        }
      },
    },
    pyro: {
      spriteKey: "slime_pyro",
      spawnTurn: 46,
      capTurn: 60,
      onDefeat: (block) => {
        const explosionRadius = blockConfig.width * 1.5;
        const explosionDamage = Math.max(1, Math.floor(turnCount / 10));
        activeEffects.push({
          type: "explosion",
          x: block.x,
          y: block.y,
          radius: explosionRadius,
          maxLifespan: 300,
          lifespan: 300,
          color: ELEMENT_COLORS.Pyro,
        });
        blocks.forEach((otherBlock) => {
          const dist = Math.hypot(
            otherBlock.x - block.x,
            otherBlock.y - block.y
          );
          if (dist < explosionRadius) {
            otherBlock.hp -= explosionDamage;
            activeEffects.push({
              type: "hit_spark",
              x: otherBlock.x,
              y: otherBlock.y,
              maxLifespan: 200,
              lifespan: 200,
            });
          }
        });
        soundQueue.push({ type: "destroy", volume: 1.0 });
      },
    },
    cryo: {
      spriteKey: "slime_cryo",
      spawnTurn: 61,
      capTurn: 75,
      onDefeat: () => {
        balls.forEach((ball) => {
          ball.vx *= 0.5;
          ball.vy *= 0.5;
        });
        activeEffects.push({
          type: "fullscreen_frost",
          maxLifespan: 1000,
          lifespan: 1000,
          color: ELEMENT_COLORS.Cryo,
        });
      },
    },
    electro: {
      spriteKey: "slime_electro",
      spawnTurn: 76,
      capTurn: 90,
      onDefeat: (block) => {
        const targets = [];
        const maxTargets = 3;
        const chainDamage = Math.max(1, Math.floor(turnCount / 20));
        const otherBlocks = [...blocks].sort(() => 0.5 - Math.random());
        for (const otherBlock of otherBlocks) {
          if (targets.length < maxTargets) {
            targets.push(otherBlock);
          } else {
            break;
          }
        }
        if (targets.length > 0) {
          activeEffects.push({
            type: "chain_lightning",
            startX: block.x,
            startY: block.y,
            targets: targets.map((t) => ({ x: t.x, y: t.y })),
            maxLifespan: 400,
            lifespan: 400,
            color: ELEMENT_COLORS.Electro,
          });
          targets.forEach((target) => {
            target.hp -= chainDamage;
            activeEffects.push({
              type: "hit_spark",
              x: target.x,
              y: target.y,
              maxLifespan: 200,
              lifespan: 200,
            });
          });
        }
      },
    },
    geo: {
      spriteKey: "slime_geo",
      spawnTurn: 91,
      capTurn: 100,
      onDefeat: null,
    },
  };

  // =================================================================
  // 2. FUNKTIONSDEKLARATIONEN
  // =================================================================

  async function loadAssets() {
    const loadingPromise = new Promise((resolve) => {
      if (totalAssets === 0) {
        resolve();
        return;
      }
      for (const key of assetKeys) {
        const url = assetsToLoad[key];
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          assets[key] = img;
          assetsLoaded++;
          if (assetsLoaded === totalAssets) resolve();
        };
        img.onerror = () => {
          console.error(`Konnte Asset nicht laden: ${key} at ${url}`);
          assetsLoaded++;
          if (assetsLoaded === totalAssets) resolve();
        };
        img.src = url;
      }
    });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Asset loading timed out")), 10000)
    );
    return Promise.race([loadingPromise, timeoutPromise]);
  }

  function initNewGame() {
    state.minigame.isActiveGame = true;
    state.minigame.turnCount = 1;
    state.minigame.currentBalls = 5;
    state.minigame.blocks = [];
    state.minigame.powerUps = [];
    state.minigame.score = 0;
    state.minigame.playerNextX = logicalWidth / 2;
    state.minigame.turnsSinceLastPowerUp = 0;
    loadStateIntoGame();
    spawnNewBlocks(40 + blockConfig.width / 2);
  }

  function loadStateIntoGame() {
    turnCount = state.minigame.turnCount;
    currentBalls = state.minigame.currentBalls;
    turnsSinceLastPowerUp = state.minigame.turnsSinceLastPowerUp || 0;

    blocks = state.minigame.blocks.map((b) => ({
      ...b,
      sprite: assets[b.spriteKey],
    }));
    powerUps = state.minigame.powerUps;

    player = {
      x: state.minigame.playerNextX,
      y: logicalHeight - 30,
      radius: playerRadius,
      nextX: state.minigame.playerNextX,
    };
    mouse = {
      x: logicalWidth / 2,
      y: logicalHeight / 2,
      targetX: logicalWidth / 2,
      targetY: logicalHeight / 2,
    };
    balls = [];
    ballsToLaunch = 0;
    roundStartTime = 0;
    isSpedUp = false;
    showSpeedUpText = false;
    nudgeTimerStartTime = 0;
    showNudgeSticker1 = false;
    showNudgeSticker2 = false;
  }

  function spawnNewBlocks(startY) {
    let ballPowerUpShouldSpawn = false;
    const baseChance = 0.4;
    const pityTriggerRound = 2;
    const emptySlotChance = 0.35;
    const isPityTriggered = turnsSinceLastPowerUp >= pityTriggerRound;

    if (turnCount > 1 && (isPityTriggered || Math.random() < baseChance)) {
      ballPowerUpShouldSpawn = true;
    }

    let burstPowerUpShouldSpawn = false;
    if (turnCount > 5 && Math.random() < 0.15) {
      burstPowerUpShouldSpawn = true;
    }

    const cycle = Math.floor((turnCount - 1) / 100);
    const effectiveTurn = ((turnCount - 1) % 100) + 1;

    const availableSlimes = Object.values(SLIME_CONFIG)
      .filter((s) => effectiveTurn >= s.spawnTurn)
      .sort((a, b) => b.spawnTurn - a.spawnTurn);

    const weightedPool = [];
    if (availableSlimes.length > 0) {
      availableSlimes.forEach((slime, index) => {
        let weight = 0;
        if (index === 0) weight = 12;
        else if (index === 1) weight = 5;
        else if (index === 2) weight = 2;
        else weight = 1;
        for (let i = 0; i < weight; i++) {
          weightedPool.push(slime.spriteKey);
        }
      });
    } else {
      weightedPool.push(SLIME_CONFIG.anemo.spriteKey);
    }

    let ballPowerUpCol = -1;
    let burstPowerUpCol = -1;
    let availableCols = Array.from({ length: blockConfig.cols }, (_, i) => i);

    if (ballPowerUpShouldSpawn) {
      ballPowerUpCol = getRandomElements(availableCols, 1)[0];
      availableCols = availableCols.filter((c) => c !== ballPowerUpCol);
      turnsSinceLastPowerUp = 0;
    } else {
      turnsSinceLastPowerUp++;
    }

    if (burstPowerUpShouldSpawn) {
      burstPowerUpCol = getRandomElements(availableCols, 1)[0];
    }

    for (let c = 0; c < blockConfig.cols; c++) {
      const x =
        c * (blockConfig.width + blockConfig.padding) +
        (blockConfig.width / 2 + blockConfig.padding);
      const y = startY;

      if (c === ballPowerUpCol) {
        powerUps.push({ x, y, radius: powerUpRadius, type: "addBall" });
      } else if (c === burstPowerUpCol) {
        powerUps.push({ x, y, radius: powerUpRadius, type: "elementalBurst" });
      } else if (Math.random() < emptySlotChance) {
        // Empty
      } else {
        const randomSlimeKey =
          weightedPool[Math.floor(Math.random() * weightedPool.length)];
        const slimeTypeConfig = Object.values(SLIME_CONFIG).find(
          (s) => s.spriteKey === randomSlimeKey
        );
        const cycleBonus = cycle * 100;
        let hp;
        if (effectiveTurn <= slimeTypeConfig.capTurn) {
          hp = effectiveTurn + cycleBonus;
        } else {
          hp = slimeTypeConfig.capTurn + cycleBonus;
        }
        blocks.push({
          x,
          y,
          width: blockConfig.width,
          height: blockConfig.width,
          hp: hp,
          sprite: assets[randomSlimeKey],
          spriteKey: randomSlimeKey,
          isShieldBroken: false,
        });
      }
    }
  }

  function triggerElementalBurst() {
    if (activeStickerSequence) return;

    const presentElements = [
      ...new Set(blocks.map((b) => b.spriteKey.split("_")[1])),
    ];
    if (presentElements.length === 0) {
      console.log("No elements on field for burst."); // Debugging-Hilfe
      return;
    }

    const randomElement = getRandomElements(presentElements, 1)[0];
    const config = POWERUP_CONFIG[randomElement];
    if (!config) {
      console.log(`No power-up config found for element: ${randomElement}`); // Debugging-Hilfe
      return;
    }

    // --- START: ANGEPASSTER CODE ---
    // Hol die geladenen Bilder anhand der Keys aus der Konfiguration
    const loadedStickers = config.stickerKeys
      .map((key) => assets[key])
      .filter(Boolean);

    // Breche ab, wenn aus irgendeinem Grund die Sticker nicht geladen wurden.
    if (loadedStickers.length === 0) {
      console.log(
        `Stickers for ${randomElement} not loaded, executing effect directly.`
      );
      if (config.effect) config.effect();
      return;
    }

    activeStickerSequence = {
      stickers: loadedStickers, // Verwende die tatsächlich geladenen Bilder
      effect: config.effect,
      currentIndex: 0,
      startTime: Date.now(),
      stage: "intro",
    };
    // --- ENDE: ANGEPASSTER CODE ---
  }

  function getClampedAngle() {
    let dy = mouse.y - player.y;
    let dx = mouse.x - player.x;
    if (dy >= 0) {
      dy = -1;
    }
    const angle = Math.atan2(dy, dx);
    const minAngle = 0.05;
    if (Math.abs(angle) < minAngle) {
      return -minAngle;
    }
    if (Math.abs(angle - -Math.PI) < minAngle) {
      return -Math.PI + minAngle;
    }
    return angle;
  }

  function handlePointerMove(e) {
    if (gameState !== "aiming") return;
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    mouse.targetX = x;
    mouse.targetY = y;
  }

  function handlePointerUp() {
    if (gameState === "gameOver") {
      cleanupEventListeners();
      initBrickBreakerGame();
      return;
    }
    if (gameState !== "aiming") return;

    gameState = "shooting";
    ballsToLaunch = currentBalls;
    roundStartTime = Date.now();
    isSpedUp = false;
    showSpeedUpText = false;
    showNudgeSticker1 = false;
    showNudgeSticker2 = false;
    stickerAnimation = { scale: 0, alpha: 0 };
    nudgeTimerStartTime = 0;

    const angle = getClampedAngle();
    const startY = player.y - player.radius - ballRadius;

    launchInterval = setInterval(() => {
      if (ballsToLaunch > 0) {
        const vx = Math.cos(angle) * ballSettings.speed;
        const vy = Math.sin(angle) * ballSettings.speed;
        balls.push({
          x: player.x,
          y: startY,
          radius: ballRadius,
          vx,
          vy,
          isReturned: false,
        });
        ballsToLaunch--;
      } else {
        clearInterval(launchInterval);
      }
    }, 100);
  }

  function cleanupEventListeners() {
    canvas.removeEventListener("mousemove", handlePointerMove);
    canvas.removeEventListener("mouseup", handlePointerUp);
    canvas.removeEventListener("touchmove", handlePointerMove);
    canvas.removeEventListener("touchend", handlePointerUp);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      if (brickBreakerAnimationId) {
        cancelAnimationFrame(brickBreakerAnimationId);
        brickBreakerAnimationId = null;
      }
    } else {
      if (
        !brickBreakerAnimationId &&
        gameState !== "gameOver" &&
        gameState !== "loading"
      ) {
        lastTime = 0;
        brickBreakerAnimationId = requestAnimationFrame(gameLoop);
      }
    }
  }

  function update(deltaTime) {
    if (deltaTime > 0.1) return;

    if (gameState === "aiming") {
      const aimSmoothingFactor = 0.3;
      const interpolationFactor =
        1 - Math.pow(1 - aimSmoothingFactor, deltaTime * 60);
      mouse.x += (mouse.targetX - mouse.x) * interpolationFactor;
      mouse.y += (mouse.targetY - mouse.y) * interpolationFactor;
    }

    if (gameState === "shooting") {
      const targetScale = combo.count > 0 ? 1.0 : 1.2;
      const targetAlpha = combo.count > 0 ? 1.0 : 0.0;
      const interpolationFactor = 1 - Math.pow(0.01, deltaTime);

      combo.alpha += (targetAlpha - combo.alpha) * interpolationFactor * 5;
      combo.scale += (targetScale - combo.scale) * interpolationFactor * 5;

      if (
        combo.count > 0 &&
        Date.now() - combo.lastHitTime > combo.maxDisplayTime
      ) {
        combo.count = 0;
      }
      if (combo.displayCount < combo.count) {
        combo.displayCount += Math.max(
          1,
          Math.ceil(
            (combo.count - combo.displayCount) * interpolationFactor * 5
          )
        );
      } else if (combo.count === 0) {
        combo.displayCount = 0;
      }

      let firstBallReturned = false;
      for (const ball of balls) {
        if (ball.isReturned) {
          firstBallReturned = true;
          break;
        }
      }

      if (
        nudgeTimerStartTime === 0 &&
        ballsToLaunch === 0 &&
        firstBallReturned
      ) {
        nudgeTimerStartTime = Date.now();
      }

      if (nudgeTimerStartTime > 0) {
        const nudgeTimeElapsed = Date.now() - nudgeTimerStartTime;

        if (nudgeTimeElapsed > 30000 && !showNudgeSticker2) {
          showNudgeSticker2 = true;
          showNudgeSticker1 = false;
          stickerAnimation = { scale: 0, alpha: 0 };
          balls.forEach((ball) => {
            if (!ball.isReturned) {
              ball.vy = Math.max(Math.abs(ball.vy), ballSettings.speed * 0.5);
              ball.vx += (Math.random() - 0.5) * 100;
            }
          });
        } else if (
          nudgeTimeElapsed > 20000 &&
          !showNudgeSticker1 &&
          !showNudgeSticker2
        ) {
          showNudgeSticker1 = true;
          stickerAnimation = { scale: 0, alpha: 0 };
        }
      }

      if (showNudgeSticker1 || showNudgeSticker2) {
        const targetScale = 1.0;
        const targetAlpha = 1.0;
        const interpolationFactorAnim = 1 - Math.pow(0.1, deltaTime);
        stickerAnimation.scale +=
          (targetScale - stickerAnimation.scale) * interpolationFactorAnim;
        stickerAnimation.alpha +=
          (targetAlpha - stickerAnimation.alpha) * interpolationFactorAnim;
      }

      if (
        !isSpedUp &&
        Date.now() - roundStartTime > ballSettings.speedUpDelay
      ) {
        isSpedUp = true;
        showSpeedUpText = true;
      }

      const ballDamage = 1;
      let returnedCount = 0;
      let firstBallReturnedThisFrame = false;

      for (let i = balls.length - 1; i >= 0; i--) {
        const ball = balls[i];
        if (ball.isReturned) {
          returnedCount++;
          continue;
        }

        if (isSpedUp) {
          const currentSpeed = Math.hypot(ball.vx, ball.vy);
          if (currentSpeed < ballSettings.maxSpeed) {
            const speedIncrease = 1 + 0.3 * deltaTime;
            ball.vx *= speedIncrease;
            ball.vy *= speedIncrease;
          }
        }

        const distanceToTravel = Math.hypot(ball.vx, ball.vy) * deltaTime;
        const subSteps = Math.max(
          1,
          Math.ceil(distanceToTravel / (ball.radius * 0.5))
        );
        const stepDeltaTime = deltaTime / subSteps;

        for (let step = 0; step < subSteps; step++) {
          if (ball.isReturned) break;

          ball.x += ball.vx * stepDeltaTime;
          ball.y += ball.vy * stepDeltaTime;

          if (ball.x - ball.radius < 0) {
            ball.x = ball.radius;
            ball.vx *= -1;
            soundQueue.push({ type: "bounce", volume: 0.2 });
          }
          if (ball.x + ball.radius > logicalWidth) {
            ball.x = logicalWidth - ball.radius;
            ball.vx *= -1;
            soundQueue.push({ type: "bounce", volume: 0.2 });
          }
          if (ball.y - ball.radius < 0) {
            ball.y = ball.radius;
            ball.vy *= -1;
            soundQueue.push({ type: "bounce", volume: 0.2 });
          }

          for (let j = blocks.length - 1; j >= 0; j--) {
            const block = blocks[j];
            if (rectCircleCollision(block, ball)) {
              if (block.isFrozen) {
                const colX = block.x;
                const rowY = block.y;

                for (let k = blocks.length - 1; k >= 0; k--) {
                  const otherBlock = blocks[k];
                  if (
                    Math.abs(otherBlock.x - colX) < otherBlock.width / 2 ||
                    Math.abs(otherBlock.y - rowY) < otherBlock.height / 2
                  ) {
                    otherBlock.hp = 0;
                  }
                }
                soundQueue.push({ type: "destroy", volume: 1.0 });
                triggerShake(10, 400);
              }
              if (block.spriteKey === "slime_geo" && !block.isShieldBroken) {
                block.isShieldBroken = true;
                triggerShake(2, 100);
                for (let k = 0; k < 10; k++)
                  particles.push({
                    x: block.x,
                    y: block.y,
                    vx: (Math.random() - 0.5) * 3,
                    vy: (Math.random() - 0.5) * 3,
                    size: Math.random() * 2,
                    color: "#a8a29e",
                    lifespan: 40,
                    initialLifespan: 40,
                  });
              } else {
                block.hp -= ballDamage;
              }

              state.minigame.score += ballDamage;
              combo.count++;
              combo.lastHitTime = Date.now();

              if (combo.count > (state.minigame.maxCombo || 0)) {
                state.minigame.maxCombo = combo.count;
                checkAchievements();
              }

              soundQueue.push({ type: "hit", volume: 0.4 });

              const dx = ball.x - block.x;
              const dy = ball.y - block.y;
              const overlapX = ball.radius + block.width / 2 - Math.abs(dx);
              const overlapY = ball.radius + block.height / 2 - Math.abs(dy);

              if (overlapX < overlapY) {
                ball.x += overlapX * Math.sign(dx);
                ball.vx *= -1;
              } else {
                ball.y += overlapY * Math.sign(dy);
                ball.vy *= -1;
              }
              break;
            }
          }

          for (let k = powerUps.length - 1; k >= 0; k--) {
            const powerUp = powerUps[k];
            if (!powerUp) continue;
            const dist = Math.hypot(ball.x - powerUp.x, ball.y - powerUp.y);
            if (dist < ball.radius + powerUp.radius) {
              if (powerUp.type === "addBall") {
                currentBalls++;
                soundQueue.push({ type: "powerup", volume: 1.0 });
              } else if (powerUp.type === "elementalBurst") {
                triggerElementalBurst();
              }
              powerUps.splice(k, 1);
            }
          }

          if (!ball.hasBeenSplit) {
            for (const effect of activeEffects) {
              if (effect.type === "puddle" && !effect.hasSplitBall) {
                const dist = Math.hypot(ball.x - effect.x, ball.y - effect.y);
                if (dist < ball.radius + effect.radius) {
                  effect.hasSplitBall = true;
                  ball.hasBeenSplit = true;
                  const angle = Math.atan2(ball.vy, ball.vx);
                  const angleOffset = Math.PI / 4;
                  const newAngle =
                    angle + (Math.random() > 0.5 ? angleOffset : -angleOffset);
                  balls.push({
                    ...ball,
                    vx: Math.cos(newAngle) * ballSettings.speed,
                    vy: Math.sin(newAngle) * ballSettings.speed,
                    isReturned: false,
                    hasBeenSplit: true,
                  });
                  soundQueue.push({ type: "split", volume: 0.8 });
                  break;
                }
              }
            }
          }

          if (ball.y + ball.radius > player.y && !ball.isReturned) {
            ball.isReturned = true;
            if (!firstBallReturnedThisFrame) {
              player.nextX = ball.x;
              firstBallReturnedThisFrame = true;
            }
          }
        }

        blocks.forEach((block) => {
          if (block.pushVx || block.pushVy) {
            block.x += block.pushVx * deltaTime;
            block.y += block.pushVy * deltaTime;
            const decay = 1 - 8 * deltaTime;
            block.pushVx *= decay;
            block.pushVy *= decay;
            if (Math.abs(block.pushVx) < 1 && Math.abs(block.pushVy) < 1) {
              delete block.pushVx;
              delete block.pushVy;
            }
          }
        });

        for (let i = 0; i < blocks.length; i++) {
          for (let j = i + 1; j < blocks.length; j++) {
            const blockA = blocks[i];
            const blockB = blocks[j];
            const dist = Math.hypot(blockA.x - blockB.x, blockA.y - blockB.y);
            const min_dist = blockA.width / 2 + blockB.width / 2;
            if (dist < min_dist) {
              const overlap = min_dist - dist;
              const angle = Math.atan2(
                blockA.y - blockB.y,
                blockA.x - blockB.x
              );
              const moveX = (overlap / 2) * Math.cos(angle);
              const moveY = (overlap / 2) * Math.sin(angle);
              blockA.x += moveX;
              blockA.y += moveY;
              blockB.x -= moveX;
              blockB.y -= moveY;
            }
          }
        }

        blocks.forEach((block) => {
          const radius = block.width / 2;
          if (block.x - radius < 0) block.x = radius;
          if (block.x + radius > logicalWidth) block.x = logicalWidth - radius;
          if (block.y - radius < 0) block.y = radius;
          if (block.y + radius > player.y - 20)
            block.y = player.y - 20 - radius;
        });

        if (ball.isReturned) {
          returnedCount++;
        }
      }

      if (balls.length > 0 && returnedCount === balls.length) {
        endTurn();
      }
    } else if (gameState === "animating") {
      let allInPosition = true;
      const moveFactor = 1 - Math.pow(0.01, deltaTime);

      if (Math.abs(player.x - player.nextX) > 0.1) {
        player.x += (player.nextX - player.x) * moveFactor * 10;
        allInPosition = false;
      } else {
        player.x = player.nextX;
      }

      blocks.forEach((block) => {
        if (block.y < block.targetY) {
          block.y += animationSpeed * deltaTime;
          block.y = Math.min(block.y, block.targetY);
          allInPosition = false;
        }
      });
      powerUps.forEach((powerUp) => {
        if (powerUp.y < powerUp.targetY) {
          powerUp.y += animationSpeed * deltaTime;
          powerUp.y = Math.min(powerUp.y, powerUp.targetY);
          allInPosition = false;
        }
      });
      if (allInPosition) {
        for (let i = powerUps.length - 1; i >= 0; i--) {
          if (powerUps[i].y > logicalHeight) powerUps.splice(i, 1);
        }
        gameState = "aiming";
        if (state.minigame.score > state.minigame.highScore) {
          state.minigame.highScore = state.minigame.score;
        }
        state.minigame.isActiveGame = true;
        state.minigame.turnCount = turnCount;
        state.minigame.currentBalls = currentBalls;
        state.minigame.blocks = blocks.map((b) => ({
          ...b,
          sprite: null,
          spriteKey: b.spriteKey,
          isShieldBroken: b.isShieldBroken,
        }));
        state.minigame.powerUps = powerUps;
        state.minigame.playerNextX = player.x;
        state.minigame.turnsSinceLastPowerUp = turnsSinceLastPowerUp;
        saveState();
        checkAchievements();
      }
    }

    if (activeStickerSequence) {
      const sequenceTime = Date.now() - activeStickerSequence.startTime;
      const { stickers, effect } = activeStickerSequence;
      if (activeStickerSequence.stage === "intro" && sequenceTime > 1000) {
        // Länger halten (war 1500, aber Animation startet sofort)
        activeStickerSequence.stage = "main";
        activeStickerSequence.currentIndex = 1;
        activeStickerSequence.startTime = Date.now();
        if (effect) effect(); // Effekt startet mit dem zweiten Sticker
      } else if (
        activeStickerSequence.stage === "main" &&
        sequenceTime > 2500
      ) {
        // Haupteffekt länger sichtbar (war 2000)
        activeStickerSequence.stage = "outro";
        activeStickerSequence.currentIndex = stickers.length > 2 ? 2 : -1;
        activeStickerSequence.startTime = Date.now();
      } else if (
        activeStickerSequence.stage === "outro" &&
        sequenceTime > 1200
      ) {
        // Outro-Sticker auch länger halten (war 1500)
        activeStickerSequence = null;
      }
    }

    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1;
      p.lifespan--;
      if (p.lifespan <= 0) {
        particles.splice(i, 1);
      }
    }

    for (let i = activeEffects.length - 1; i >= 0; i--) {
      const effect = activeEffects[i];
      effect.lifespan -= deltaTime * 1000;
      if (effect.lifespan <= 0) {
        activeEffects.splice(i, 1);
      }
    }

    let defeatedInPass;
    do {
      defeatedInPass = [];
      for (let i = blocks.length - 1; i >= 0; i--) {
        if (blocks[i].hp <= 0) {
          defeatedInPass.push(blocks[i]);
          blocks.splice(i, 1);
        }
      }
      for (const block of defeatedInPass) {
        state.minigame.score += turnCount * 10;
        triggerShake(4, 150);
        createParticles(block.x, block.y, block.spriteKey);
        const slimeConfig = Object.values(SLIME_CONFIG).find(
          (s) => s.spriteKey === block.spriteKey
        );
        if (slimeConfig && slimeConfig.onDefeat) {
          slimeConfig.onDefeat(block);
        }
        combo.count += 5;
        soundQueue.push({ type: "destroy", volume: 0.6 });
      }
    } while (defeatedInPass.length > 0);
  }

  function endTurn() {
    showNudgeSticker1 = false;
    showNudgeSticker2 = false;
    nudgeTimerStartTime = 0;

    combo.count = 0;
    combo.displayCount = 0;
    combo.alpha = 0;

    turnCount++;
    balls = [];
    showSpeedUpText = false;
    const moveDistance = blockConfig.width + blockConfig.padding;

    const newRowY = 40 + blockConfig.width / 2 - moveDistance;
    spawnNewBlocks(newRowY);

    blocks.forEach((block) => {
      block.targetY = block.y + moveDistance;
    });
    powerUps.forEach((powerUp) => {
      powerUp.targetY = powerUp.y + moveDistance;
    });

    for (const block of blocks) {
      if (block.targetY + block.height / 2 > player.y - 20) {
        gameState = "gameOver";
        soundQueue.push({ type: "gameover", volume: 1.0 });
        state.minigame.isActiveGame = false;
        state.minigame.blocks = [];
        state.minigame.powerUps = [];
        if (state.minigame.score > state.minigame.highScore) {
          state.minigame.highScore = state.minigame.score;
        }
        saveState();
        checkAchievements();
        return;
      }
    }
    gameState = "animating";
  }

  function draw() {
    ctx.save();

    const now = Date.now();
    if (shake.active && now < shake.startTime + shake.duration) {
      const progress = (now - shake.startTime) / shake.duration;
      const currentMagnitude = shake.magnitude * (1 - progress);
      const x = (Math.random() - 0.5) * currentMagnitude;
      const y = (Math.random() - 0.5) * currentMagnitude;
      ctx.translate(x, y);
    } else {
      shake.active = false;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (assets.background && assets.background.complete) {
      ctx.globalAlpha = 0.4;
      drawCoverImage(ctx, assets.background, logicalWidth, logicalHeight);
      ctx.globalAlpha = 1.0;
    }
    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
    ctx.fillRect(0, 0, logicalWidth, logicalHeight);

    if (gameState === "loading") {
      ctx.fillStyle = "white";
      ctx.font = "16px Noto Serif";
      ctx.textAlign = "center";
      ctx.fillText(
        `Loading Assets... (${assetsLoaded}/${totalAssets})`,
        logicalWidth / 2,
        logicalHeight / 2
      );
      ctx.restore();
      return;
    }
    if (gameState === "gameOver") {
      if (assets.gameOverSticker && assets.gameOverSticker.complete) {
        ctx.save();
        const now = Date.now();
        const yOffset = Math.sin(now / 500) * 10;
        const rotation = Math.sin(now / 700) * 0.05;
        const stickerSize = logicalWidth * 0.5;
        ctx.translate(logicalWidth / 2, logicalHeight / 2 - 40);
        ctx.rotate(rotation);
        drawImageWithAspectRatio(
          assets.gameOverSticker,
          0,
          yOffset,
          stickerSize,
          stickerSize
        );
        ctx.restore();
      }
      ctx.fillStyle = "white";
      ctx.font = `${logicalWidth * 0.05}px Noto Serif`;
      ctx.textAlign = "center";
      ctx.fillText(
        `You reached Round ${turnCount}`,
        logicalWidth / 2,
        logicalHeight / 2 + 60
      );
      ctx.font = `${logicalWidth * 0.04}px Noto Serif`;
      ctx.fillText(`Tap to restart`, logicalWidth / 2, logicalHeight / 2 + 100);
      ctx.restore();
      return;
    }

    ctx.fillStyle = "white";
    ctx.font = `bold ${logicalWidth * 0.04}px Noto Serif`;
    ctx.textAlign = "left";
    ctx.fillText(`Score: ${state.minigame.score}`, 10, 25);
    ctx.textAlign = "right";
    ctx.fillText(`High: ${state.minigame.highScore}`, logicalWidth - 10, 25);

    if (showSpeedUpText) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      ctx.font = "14px Noto Serif";
      ctx.textAlign = "center";
      ctx.fillText("Speeding Up...", logicalWidth / 2, 12);
    }

    if (assets.player && assets.player.complete) {
      drawImageWithAspectRatio(
        assets.player,
        player.x,
        player.y,
        player.radius * 2,
        player.radius * 2
      );
    }

    let visibleBalls = 0;
    for (const ball of balls) {
      if (!ball.isReturned) {
        if (assets.ball && assets.ball.complete) {
          drawImageWithAspectRatio(
            assets.ball,
            ball.x,
            ball.y,
            ball.radius * 2,
            ball.radius * 2
          );
        }
        visibleBalls++;
      }
    }
    if (gameState === "aiming") {
      ctx.fillStyle = "white";
      ctx.font = `bold ${logicalWidth * 0.03}px Noto Serif`;
      ctx.textAlign = "center";
      ctx.fillText(
        `x${currentBalls}`,
        player.x,
        player.y - player.radius - ballRadius * 2
      );
    } else if (gameState === "shooting") {
      const ballsLeft = ballsToLaunch + visibleBalls;
      ctx.fillStyle = "white";
      ctx.font = `bold ${logicalWidth * 0.03}px Noto Serif`;
      ctx.textAlign = "center";
      ctx.fillText(
        `x${ballsLeft}`,
        player.x,
        player.y - player.radius - ballRadius * 2
      );
    }
    if (gameState === "aiming") {
      const points = calculateTrajectory();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    }

    for (const block of blocks) {
      if (block.sprite && block.sprite.complete) {
        ctx.save();
        if (block.isFrozen) {
          ctx.filter = "saturate(0) brightness(1.5)";
        }
        if (block.spriteKey === "slime_geo" && !block.isShieldBroken) {
          ctx.globalAlpha = 0.5;
        }
        drawImageWithAspectRatio(
          block.sprite,
          block.x,
          block.y,
          block.width,
          block.width
        );
        ctx.restore();

        if (block.spriteKey === "slime_geo" && !block.isShieldBroken) {
          ctx.fillStyle = "#fde047";
          ctx.beginPath();
          ctx.moveTo(block.x, block.y - block.height * 0.4);
          ctx.lineTo(
            block.x - block.width * 0.25,
            block.y - block.height * 0.2
          );
          ctx.lineTo(
            block.x - block.width * 0.25,
            block.y + block.height * 0.1
          );
          ctx.lineTo(block.x, block.y + block.height * 0.3);
          ctx.lineTo(
            block.x + block.width * 0.25,
            block.y + block.height * 0.1
          );
          ctx.lineTo(
            block.x + block.width * 0.25,
            block.y - block.height * 0.2
          );
          ctx.closePath();
          ctx.fill();
        }
      }
      ctx.fillStyle = "white";
      ctx.font = `bold ${logicalWidth * 0.04}px Noto Serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.strokeStyle = "black";
      ctx.lineWidth = 3;
      ctx.strokeText(block.hp, block.x, block.y);
      ctx.fillText(block.hp, block.x, block.y);
    }

    for (const powerUp of powerUps) {
      const img =
        powerUp.type === "addBall"
          ? assets.powerUp_addBall
          : assets.powerUp_elementalBurst;
      if (img && img.complete) {
        drawImageWithAspectRatio(
          img,
          powerUp.x,
          powerUp.y,
          powerUp.radius * 2,
          powerUp.radius * 2
        );
      }
    }

    for (const p of particles) {
      ctx.beginPath();
      ctx.globalAlpha = Math.max(0, p.lifespan / p.initialLifespan);
      ctx.fillStyle = p.color;
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1.0;

    for (const effect of activeEffects) {
      const progress = 1 - effect.lifespan / effect.maxLifespan;
      ctx.globalAlpha = 1 - progress;

      if (effect.type === "explosion") {
        ctx.beginPath();
        ctx.arc(effect.x, effect.y, effect.radius * progress, 0, Math.PI * 2);
        ctx.fillStyle = effect.color + "80";
        ctx.fill();
      } else if (effect.type === "puddle") {
        const dropCount = 5;
        for (let i = 0; i < dropCount; i++) {
          const angle = (i / dropCount) * Math.PI * 2 + Date.now() / 1000;
          const distance = effect.radius * 0.6;
          const dropX = effect.x + Math.cos(angle) * distance;
          const dropY = effect.y + Math.sin(angle) * distance;
          const sizePulse = Math.sin(Date.now() / 200 + i) * 2 + 5;
          ctx.beginPath();
          const alpha = Math.sin((1 - progress) * Math.PI) * 0.8;
          ctx.globalAlpha = alpha;
          ctx.fillStyle = effect.color + "E6";
          ctx.arc(dropX, dropY, sizePulse, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (effect.type === "frost_line") {
        const alpha = Math.sin((1 - progress) * Math.PI);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = effect.color + "CC";
        ctx.fillRect(effect.x, effect.y, effect.width, effect.height);
      } else if (effect.type === "geo_pillar") {
        const pillarHeight = logicalHeight * progress;
        const y = logicalHeight - pillarHeight;
        ctx.fillStyle = effect.color + "E6";
        ctx.fillRect(
          effect.x - blockConfig.width / 2,
          y,
          blockConfig.width,
          pillarHeight
        );
      } else if (effect.type === "anemo_sweep") {
        const sweepHeight = 50 * (1 - progress);
        ctx.fillStyle = effect.color + "80";
        ctx.fillRect(
          0,
          effect.y - logicalHeight * progress,
          logicalWidth,
          sweepHeight
        );
      } else if (effect.type === "bubble_pop") {
        ctx.strokeStyle = effect.color + "AA";
        ctx.lineWidth = 8 * (1 - progress);
        ctx.beginPath();
        ctx.arc(effect.x, effect.y, effect.radius * progress, 0, Math.PI * 2);
        ctx.stroke();
      } else if (effect.type === "lightning_strike") {
        ctx.strokeStyle = effect.color;
        ctx.lineWidth = 5 * (1 - progress); // Blitz wird dünner
        ctx.shadowColor = effect.color;
        ctx.shadowBlur = 20;

        ctx.beginPath();
        ctx.moveTo(effect.x, 0); // Start von oben
        const segments = 5;
        for (let i = 1; i <= segments; i++) {
          const t = i / segments;
          const x = effect.x + (Math.random() - 0.5) * 20 * (1 - t); // Zick-Zack
          const y = effect.y * t;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      } else if (effect.type === "shockwave") {
        ctx.beginPath();
        ctx.strokeStyle = effect.color + "80";
        ctx.lineWidth = 10 * (1 - progress);
        ctx.arc(effect.x, effect.y, effect.radius * progress, 0, Math.PI * 2);
        ctx.stroke();
      } else if (effect.type === "fullscreen_frost") {
        ctx.fillStyle = effect.color + "40";
        const alpha = Math.sin(progress * Math.PI) * 0.25;
        ctx.globalAlpha = alpha;
        ctx.fillRect(0, 0, logicalWidth, logicalHeight);
      } else if (effect.type === "chain_lightning") {
        ctx.strokeStyle = effect.color;
        ctx.lineWidth = 3;
        ctx.shadowColor = effect.color;
        ctx.shadowBlur = 10;
        let lastX = effect.startX;
        let lastY = effect.startY;
        effect.targets.forEach((target) => {
          ctx.beginPath();
          ctx.moveTo(lastX, lastY);
          const segments = 10;
          for (let i = 1; i <= segments; i++) {
            const t = i / segments;
            const x = lastX + (target.x - lastX) * t;
            const y = lastY + (target.y - lastY) * t;
            const wiggle =
              i === segments ? 0 : (Math.random() - 0.5) * 15 * (1 - progress);
            ctx.lineTo(x + wiggle, y + wiggle);
          }
          ctx.stroke();
          lastX = target.x;
          lastY = target.y;
        });
        ctx.shadowBlur = 0;
      } else if (effect.type === "hit_spark") {
        ctx.fillStyle = "#FFFFFF";
        const size = 10 * (1 - progress);
        ctx.fillText("★", effect.x, effect.y - 10 * progress);
      }
    }
    ctx.globalAlpha = 1.0;

    if (
      (showNudgeSticker1 || showNudgeSticker2) &&
      stickerAnimation.alpha > 0.01
    ) {
      const stickerImg = showNudgeSticker2
        ? assets.nudgeSticker2
        : assets.nudgeSticker1;
      if (stickerImg && stickerImg.complete) {
        ctx.save();
        const stickerSize = logicalWidth * 0.25;
        const x = logicalWidth - stickerSize / 2 - 20;
        const y = logicalHeight - stickerSize / 2 - 50;
        ctx.translate(x, y);
        ctx.scale(stickerAnimation.scale, stickerAnimation.scale);
        ctx.globalAlpha = stickerAnimation.alpha;
        const rotation = Math.sin(Date.now() / 100) * 0.1;
        ctx.rotate(rotation);
        drawImageWithAspectRatio(stickerImg, 0, 0, stickerSize, stickerSize);
        ctx.restore();
      }
    }

    if (activeStickerSequence) {
      const stickerIndex = activeStickerSequence.currentIndex;
      if (stickerIndex !== -1) {
        const stickerImg = activeStickerSequence.stickers[stickerIndex];
        if (stickerImg && stickerImg.complete) {
          ctx.save();
          const sequenceTime = Date.now() - activeStickerSequence.startTime;

          const stickerSize = logicalWidth * 0.4;
          const x = logicalWidth / 2;
          const y = logicalHeight / 2;

          // --- START: NEUE SMOOTHE ANIMATION ---
          let scale = 0;
          let alpha = 0;
          const animInDuration = 400; // Einblenden in 0.4s
          const animOutStart = 1000; // Ausblenden beginnt nach 1s (für Intro/Outro)
          const animOutDuration = 200; // Ausblenden in 0.2s

          // Sanftes Einblenden (Ease-Out)
          if (sequenceTime < animInDuration) {
            const progress = sequenceTime / animInDuration;
            scale = 1 - Math.pow(1 - progress, 3); // Kubische Ease-Out-Kurve
            alpha = progress;
          } else {
            scale = 1;
            alpha = 1;
          }

          // Sanftes Ausblenden für Intro- und Outro-Sticker
          if (
            (activeStickerSequence.stage === "intro" ||
              activeStickerSequence.stage === "outro") &&
            sequenceTime > animOutStart
          ) {
            const progress = (sequenceTime - animOutStart) / animOutDuration;
            if (progress < 1) {
              alpha = 1 - progress;
            } else {
              alpha = 0;
            }
          }

          ctx.translate(x, y);
          ctx.scale(scale, scale);
          ctx.globalAlpha = alpha;

          const rotation = Math.sin(Date.now() / 200 + stickerIndex) * 0.05; // Leichte, individuelle Schwingung
          ctx.rotate(rotation);

          drawImageWithAspectRatio(stickerImg, 0, 0, stickerSize, stickerSize);
          ctx.restore();
        }
      }
    }

    if (combo.alpha > 0) {
      ctx.save();
      const x = logicalWidth / 2;
      const y = logicalHeight * 0.35;
      ctx.translate(x, y);
      ctx.scale(combo.scale, combo.scale);
      ctx.globalAlpha = combo.alpha;
      ctx.font = `bold ${logicalWidth * 0.15}px Cinzel`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const gradient = ctx.createLinearGradient(0, -30, 0, 30);
      gradient.addColorStop(0, "#FFEFC6");
      gradient.addColorStop(1, "#D9A353");
      ctx.fillStyle = gradient;
      ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 3;
      ctx.shadowOffsetY = 3;
      ctx.fillText(Math.floor(combo.displayCount), 0, 0);
      ctx.font = `bold ${logicalWidth * 0.05}px Cinzel`;
      ctx.fillStyle = "#E8D3A3";
      ctx.fillText("HITS!", 0, 45);
      ctx.restore();
    }

    ctx.restore();
  }

  function drawImageWithAspectRatio(
    img,
    x,
    y,
    containerWidth,
    containerHeight
  ) {
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const containerRatio = containerWidth / containerHeight;
    let drawWidth, drawHeight;
    if (imgRatio > containerRatio) {
      drawWidth = containerWidth;
      drawHeight = containerWidth / imgRatio;
    } else {
      drawHeight = containerHeight;
      drawWidth = containerHeight * imgRatio;
    }
    ctx.drawImage(
      img,
      x - drawWidth / 2,
      y - drawHeight / 2,
      drawWidth,
      drawHeight
    );
  }

  function drawCoverImage(ctx, img, canvasWidth, canvasHeight) {
    const canvasRatio = canvasWidth / canvasHeight;
    const imgRatio = img.naturalWidth / img.naturalHeight;
    let sx, sy, sWidth, sHeight;

    if (imgRatio > canvasRatio) {
      sHeight = img.naturalHeight;
      sWidth = sHeight * canvasRatio;
      sx = (img.naturalWidth - sWidth) / 2;
      sy = 0;
    } else {
      sWidth = img.naturalWidth;
      sHeight = sWidth / canvasRatio;
      sx = 0;
      sy = (img.naturalHeight - sHeight) / 2;
    }
    ctx.drawImage(
      img,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      canvasWidth,
      canvasHeight
    );
  }

  function calculateTrajectory() {
    const angle = getClampedAngle();
    const startY = player.y - player.radius - ballRadius;
    let simBall = {
      x: player.x,
      y: startY,
      vx: Math.cos(angle) * ballSettings.speed,
      vy: Math.sin(angle) * ballSettings.speed,
      radius: ballRadius,
    };

    const trajectoryPoints = [{ x: player.x, y: startY }];
    const maxSteps = 200;
    const maxBounces = 3;
    let bounces = 0;

    for (let i = 0; i < maxSteps && bounces <= maxBounces; i++) {
      const simStep = 0.016;
      simBall.x += simBall.vx * simStep;
      simBall.y += simBall.vy * simStep;

      if (
        simBall.x - simBall.radius < 0 ||
        simBall.x + simBall.radius > logicalWidth
      ) {
        simBall.vx *= -1;
        simBall.x += simBall.vx * simStep;
        trajectoryPoints.push({ x: simBall.x, y: simBall.y });
        bounces++;
      }
      if (simBall.y - simBall.radius < 0) {
        simBall.vy *= -1;
        simBall.y += simBall.vy * simStep;
        trajectoryPoints.push({ x: simBall.x, y: simBall.y });
        bounces++;
      }
      let hitBlock = false;
      for (const block of blocks) {
        if (rectCircleCollision(block, simBall)) {
          trajectoryPoints.push({ x: simBall.x, y: simBall.y });
          hitBlock = true;
          break;
        }
      }
      if (hitBlock) return trajectoryPoints;
    }
    trajectoryPoints.push({ x: simBall.x, y: simBall.y });
    return trajectoryPoints;
  }

  function rectCircleCollision(rect, circle) {
    const closestX = Math.max(
      rect.x - rect.width / 2,
      Math.min(circle.x, rect.x + rect.width / 2)
    );
    const closestY = Math.max(
      rect.y - rect.height / 2,
      Math.min(circle.y, rect.y + rect.height / 2)
    );
    const distanceX = circle.x - closestX;
    const distanceY = circle.y - closestY;
    const distanceSquared = distanceX * distanceX + distanceY * distanceY;
    return distanceSquared < circle.radius * circle.radius;
  }

  function triggerShake(magnitude, duration) {
    shake.magnitude = magnitude;
    shake.duration = duration;
    shake.startTime = Date.now();
    shake.active = true;
  }

  function createParticles(x, y, spriteKey) {
    const element = spriteKey.split("_")[1];
    const colorKey = element
      ? element.charAt(0).toUpperCase() + element.slice(1)
      : null;
    const color = ELEMENT_COLORS[colorKey] || "#ffffff";

    const particleCount = 15;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * 4,
        vy: (Math.random() - 0.5) * 4 - 2,
        size: Math.random() * 2 + 1,
        color: color,
        lifespan: Math.random() * 60 + 30,
        initialLifespan: 90,
      });
    }
  }

  function gameLoop(currentTime) {
    if (!lastTime) {
      lastTime = currentTime;
    }
    const deltaTime = (currentTime - lastTime) / 1000;
    lastTime = currentTime;

    update(deltaTime);
    draw();

    processSoundQueue();

    if (gameState !== "gameOver") {
      brickBreakerAnimationId = requestAnimationFrame(gameLoop);
    }
  }

  async function startGame() {
    try {
      await loadAssets();
    } catch (e) {
      console.error("Error loading minigame assets:", e);
      gameState = "error";
      return;
    }

    if (
      state.minigame &&
      state.minigame.isActiveGame &&
      state.minigame.blocks.length > 0
    ) {
      console.log("Resuming existing minigame from saved state.");
      loadStateIntoGame();
      gameState = "aiming";
    } else {
      console.log("No active game found. Starting a new minigame.");
      initNewGame();
      gameState = "aiming";
    }

    if (brickBreakerAnimationId) {
      cancelAnimationFrame(brickBreakerAnimationId);
    }
    brickBreakerAnimationId = requestAnimationFrame(gameLoop);
  }

  // =================================================================
  // 3. IMPERATIVER CODE
  // =================================================================

  canvas.restartGame = () => {
    if (brickBreakerAnimationId) {
      cancelAnimationFrame(brickBreakerAnimationId);
      brickBreakerAnimationId = null;
    }
    cleanupEventListeners();
    initNewGame();
    saveState();
    initBrickBreakerGame();
  };

  canvas.addEventListener("mousemove", handlePointerMove);
  canvas.addEventListener("mouseup", handlePointerUp);
  canvas.addEventListener("touchmove", handlePointerMove, { passive: false });
  canvas.addEventListener("touchend", handlePointerUp);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  startGame();
}

/**
 * Führt eine komplette Wunsch-Simulation für einen bestimmten Banner durch.
 * Nutzt die bestehende `getAvailableItemsForBanner`-Logik für präzise Item-Pools.
 *
 * @param {string} bannerId - Die ID des Banners, der simuliert werden soll (z.B. 'character1', 'weapon').
 * @param {number} wishCount - Die Anzahl der zu simulierenden Wünsche.
 * @returns {object} Ein Objekt, das die Ergebnisse und den finalen Zustand enthält.
 */
function runWishSimulation(bannerId, wishCount) {
  // 1. SETUP
  const isCharacterBanner =
    bannerId === "character1" || bannerId === "character2";
  const initialState = isCharacterBanner
    ? state.characterShared
    : state[bannerId];

  let simState = {
    pity: initialState.pity,
    pity4star: initialState.pity4star,
    isGuaranteed: initialState.isGuaranteed,
    is4StarGuaranteed: initialState.is4StarGuaranteed,
    cState: isCharacterBanner ? initialState.cState : 0,
    fourStarCarryOverGuaranteed: false,
  };

  const results = {
    pulls: [],
    initialState: JSON.parse(JSON.stringify(initialState)),
    finalState: {},
  };

  const getRandomFromPool = (pool) =>
    pool[Math.floor(Math.random() * pool.length)];

  // Item-Pools vorbereiten
  let fiveStarRateUpNames = [];
  let fourStarRateUpNames = [];

  if (isCharacterBanner) {
    fiveStarRateUpNames = [currentBannerData[bannerId]?.fiveStar].filter(
      Boolean
    );
    fourStarRateUpNames = currentBannerData[bannerId]?.fourStars || [];
  } else if (bannerId === "weapon") {
    fiveStarRateUpNames = currentBannerData.weapon?.fiveStars || [];
    fourStarRateUpNames = currentBannerData.weapon?.fourStars || [];
  } else if (bannerId === "chronicled") {
    const fiveStars = currentBannerData.chronicled?.fiveStars;
    const fourStars = currentBannerData.chronicled?.fourStars;
    if (fiveStars)
      fiveStarRateUpNames = [
        ...(fiveStars.characters || []),
        ...(fiveStars.weapons || []),
      ];
    if (fourStars)
      fourStarRateUpNames = [
        ...(fourStars.characters || []),
        ...(fourStars.weapons || []),
      ];
  }

  const threeStarPool = itemsDB.filter(
    (item) => item.rarity === 3 && item.pool === "standard"
  );

  // 2. SIMULATIONS-SCHLEIFE
  for (let i = 0; i < wishCount; i++) {
    simState.pity++;
    simState.pity4star++;
    let pulledItem = null;

    // --- Check 1: 5-Sterne-Item? ---
    if (Math.random() < getProbability(simState.pity - 1, bannerId) / 100) {
      // ★★★ KORREKTUR: Übergebe den simState an die Hilfsfunktion ★★★
      const fiveStarPool = getAvailableItemsForBanner(bannerId, 5, {
        isGuaranteed: simState.isGuaranteed,
      });

      if (simState.isGuaranteed) {
        // Wenn Garantie aktiv ist, MUSS es ein Rate-Up sein.
        const rateUpPool = fiveStarPool.filter((item) =>
          fiveStarRateUpNames.includes(item.name)
        );
        pulledItem = getRandomFromPool(
          rateUpPool.length > 0 ? rateUpPool : fiveStarPool
        ); // Fallback
      } else {
        const rateUpChance = bannerId === "weapon" ? 0.75 : 0.5;
        if (Math.random() < rateUpChance) {
          const rateUpPool = fiveStarPool.filter((item) =>
            fiveStarRateUpNames.includes(item.name)
          );
          pulledItem = getRandomFromPool(
            rateUpPool.length > 0 ? rateUpPool : fiveStarPool
          );
        } else {
          const standardPool = fiveStarPool.filter(
            (item) => !fiveStarRateUpNames.includes(item.name)
          );
          pulledItem = getRandomFromPool(
            standardPool.length > 0 ? standardPool : fiveStarPool
          );
        }
      }

      // Update state based on the pull
      const isRateUp = fiveStarRateUpNames.includes(pulledItem.name);
      if (simState.isGuaranteed) {
        simState.isGuaranteed = false;
        if (isCharacterBanner)
          simState.cState = Math.max(0, simState.cState - 1);
      } else {
        simState.isGuaranteed = !isRateUp;
        if (isCharacterBanner) {
          if (isRateUp) simState.cState = Math.max(0, simState.cState - 1);
          else simState.cState = Math.min(3, simState.cState + 1);
        }
      }

      if (simState.pity4star >= 10) {
        simState.fourStarCarryOverGuaranteed = true;
      }
      simState.pity = 0;
      if (bannerId === "weapon") simState.pity4star = 0;
    }
    // --- Check 2: 4-Sterne-Item? ---
    else if (
      simState.fourStarCarryOverGuaranteed ||
      Math.random() <
      getProbability4Star(simState.pity4star - 1, bannerId) / 100
    ) {
      // ★★★ KORREKTUR: Übergebe den simState an die Hilfsfunktion ★★★
      const full4StarPool = getAvailableItemsForBanner(bannerId, 4, {
        is4StarGuaranteed: simState.is4StarGuaranteed,
      });

      if (bannerId === "chronicled") {
        pulledItem = getRandomFromPool(full4StarPool);
      } else {
        const rateUp4StarPool = full4StarPool.filter((item) =>
          fourStarRateUpNames.includes(item.name)
        );
        const standard4StarPool = full4StarPool.filter(
          (item) => !fourStarRateUpNames.includes(item.name)
        );
        const rateUpChance = bannerId === "weapon" ? 0.75 : 0.5;

        if (simState.is4StarGuaranteed) {
          pulledItem = getRandomFromPool(rateUp4StarPool);
          simState.is4StarGuaranteed = false;
        } else if (Math.random() < rateUpChance) {
          pulledItem = getRandomFromPool(rateUp4StarPool);
          simState.is4StarGuaranteed = false;
        } else {
          pulledItem = getRandomFromPool(standard4StarPool);
          if (isCharacterBanner || bannerId === "weapon") {
            simState.is4StarGuaranteed = true;
          }
        }
      }

      simState.pity4star = 0;
      simState.fourStarCarryOverGuaranteed = false;
    }
    // --- Check 3: Ansonsten ist es ein 3-Sterne-Item ---
    else {
      pulledItem = getRandomFromPool(threeStarPool);
    }

    if (!pulledItem) {
      console.warn(
        "Simulator failed to determine an item, assigning a default 3-star as a fallback."
      );
      pulledItem = getRandomFromPool(threeStarPool);
    }

    results.pulls.push(pulledItem);
  }

  // 3. ERGEBNISSE FINALISIEREN
  results.finalState = simState;
  return results;
}

/**
 * Zeigt das Wish Simulator Modal an und initialisiert das Setup.
 */
function showWishSimulator() {
  const bannerSelect = document.getElementById("simulator-banner-select");
  const wishCountInput = document.getElementById("simulator-wish-count");

  // Setup-Ansicht anzeigen, Ergebnis-Ansicht verstecken
  document.getElementById("simulator-setup-view").classList.remove("hidden");
  document.getElementById("simulator-results-view").classList.add("hidden");

  const isSpecialPhase =
    currentBannerData.phaseType === "lull" ||
    currentBannerData.phaseType === "server_down";
  const isChronicledActive =
    !isSpecialPhase &&
    currentBannerData.chronicled &&
    currentBannerData.chronicled.fiveStars?.characters?.[0] !== "";

  // Banner-Dropdown füllen
  bannerSelect.innerHTML = `
    <option value="character1">${translations[state.language].characterBanner
    } 1</option>
    <option value="character2">${translations[state.language].characterBanner
    } 2</option>
    <option value="weapon">${translations[state.language].weaponBanner}</option>
    ${isChronicledActive
      ? `<option value="chronicled">${translations[state.language].chronicledBanner}</option>`
      : ""
    }
  `;

  // Listener, um die Start-Status-Anzeige zu aktualisieren, wenn der Banner gewechselt wird
  bannerSelect.addEventListener("change", updateSimulatorStartState);

  // Initialen Start-Status anzeigen
  updateSimulatorStartState();

  showModal("wish-simulator-modal");
}

/**
 * Aktualisiert die Anzeige des Start-Zustands (Pity, Garantie) im Simulator-Setup.
 */
function updateSimulatorStartState() {
  const bannerId = document.getElementById("simulator-banner-select").value;
  const startStateContainer = document.getElementById("simulator-start-state");
  const isCharacterBanner =
    bannerId === "character1" || bannerId === "character2";
  const bannerState = isCharacterBanner
    ? state.characterShared
    : state[bannerId];

  let guaranteeText = "";
  if (bannerState.isGuaranteed) {
    guaranteeText = `<span class="text-green-400">Guaranteed</span>`;
  } else if (bannerId !== "standard") {
    guaranteeText = `<span class="text-yellow-400">50/50</span>`;
  }

  let fourStarText = "";
  if (bannerState.is4StarGuaranteed) {
    fourStarText = ` | 4★: <span class="text-green-400">Guaranteed Rate-Up</span>`;
  }

  startStateContainer.innerHTML = `
    Starting with Pity: <strong>${bannerState.pity}</strong> (${guaranteeText})${fourStarText}
  `;
}

/**
 * Berechnet den Pity-Zustand nach einer bestimmten Sequenz von Zügen.
 * @param {object} initialState - Der Pity-Zustand VOR den Zügen.
 * @param {Array<object>} pulledItems - Eine Liste der gezogenen Item-Objekte.
 * @param {string} bannerId - Die ID des Banners.
 * @returns {object} Der finale Pity-Zustand NACH den Zügen.
 */
function calculateStateAfterPulls(initialState, pulledItems, bannerId) {
  // Erstelle eine Kopie, um den Originalzustand nicht zu verändern
  let currentState = JSON.parse(JSON.stringify(initialState));

  const isCharacterBanner =
    bannerId === "character1" || bannerId === "character2";

  // Hole die Rate-Up-Listen einmal am Anfang
  let fiveStarRateUpNames = [];
  let fourStarRateUpNames = [];

  if (isCharacterBanner) {
    fiveStarRateUpNames = [currentBannerData[bannerId]?.fiveStar].filter(
      Boolean
    );
    fourStarRateUpNames = currentBannerData[bannerId]?.fourStars || [];
  } else if (bannerId === "weapon") {
    fiveStarRateUpNames = currentBannerData.weapon?.fiveStars || [];
    fourStarRateUpNames = currentBannerData.weapon?.fourStars || [];
  } else if (bannerId === "chronicled") {
    fiveStarRateUpNames = (
      currentBannerData[bannerId]?.fiveStars?.characters || []
    ).concat(currentBannerData[bannerId]?.fiveStars?.weapons || []);
    fourStarRateUpNames = (
      currentBannerData[bannerId]?.fourStars?.characters || []
    ).concat(currentBannerData[bannerId]?.fourStars?.weapons || []);
  }

  // Gehe jeden einzelnen Zug in der Sequenz durch
  for (const item of pulledItems) {
    currentState.pity++;
    currentState.pity4star++;

    if (item.rarity === 5) {
      const isRateUp = fiveStarRateUpNames.includes(item.name);

      if (isCharacterBanner) {
        if (currentState.isGuaranteed) {
          currentState.isGuaranteed = false;
        } else {
          currentState.isGuaranteed = !isRateUp; // Wenn es kein Rate-Up ist, wird die nächste garantiert
        }
      } else if (bannerId === "weapon" || bannerId === "chronicled") {
        const courseItemName = state[bannerId].courseSetOn;
        const isCourseItem = courseItemName && item.name === courseItemName;
        if (currentState.isGuaranteed) {
          currentState.isGuaranteed = false;
        } else {
          currentState.isGuaranteed = !isCourseItem;
        }
      }

      currentState.pity = 0;
      if (bannerId === "weapon") currentState.pity4star = 0;
    } else if (item.rarity === 4) {
      const isRateUp = fourStarRateUpNames.includes(item.name);

      if (isCharacterBanner || bannerId === "weapon") {
        if (currentState.is4StarGuaranteed) {
          currentState.is4StarGuaranteed = false;
        } else {
          currentState.is4StarGuaranteed = !isRateUp;
        }
      }
      currentState.pity4star = 0;
    }
  }

  return currentState;
}

/**
 * Startet die Simulation, führt die Logik aus und startet die Enthüllungs-Animation für die erste Seite.
 */
function startSimulation() {
  const bannerId = document.getElementById("simulator-banner-select").value;
  const wishCount = parseInt(
    document.getElementById("simulator-wish-count").value,
    10
  );

  if (isNaN(wishCount) || wishCount < 1 || wishCount > 1000) {
    showToast("Please enter a number of wishes between 1 and 1000.", "error");
    return;
  }

  // UI umschalten
  document.getElementById("simulator-setup-view").classList.add("hidden");
  document.getElementById("simulator-results-view").classList.remove("hidden");
  document.getElementById("simulator-reveal-grid").innerHTML = "";
  document.getElementById("simulator-final-state").innerHTML = "";
  document.getElementById("simulator-next-btn").classList.add("hidden");
  document.getElementById("simulator-again-btn").classList.add("hidden");

  // Simulation für ALLE Wünsche auf einmal durchführen
  currentSimulationResults = runWishSimulation(bannerId, wishCount);
  simulationCurrentPage = 0;

  // Animation für die erste Seite starten
  animateNextSimulationPage();
}

/**
 * Animiert die nächste "Seite" (10 Pulls) der Simulationsergebnisse.
 */
async function animateNextSimulationPage() {
  const grid = document.getElementById("simulator-reveal-grid");
  const nextBtn = document.getElementById("simulator-next-btn");
  const againBtn = document.getElementById("simulator-again-btn");

  // Steuerelemente verstecken, während die Animation läuft
  nextBtn.classList.add("hidden");
  againBtn.classList.add("hidden");

  // Den richtigen Ausschnitt der Pulls für die aktuelle Seite holen
  const startIndex = simulationCurrentPage * 10;
  const endIndex = startIndex + 10;
  const pullsForPage = currentSimulationResults.pulls.slice(
    startIndex,
    endIndex
  );

  if (pullsForPage.length === 0) {
    againBtn.classList.remove("hidden");
    return;
  }

  // Animation für die aktuelle Seite ausführen
  await runPullRevealAnimation(pullsForPage);

  // Ergebnisse und Status aktualisieren
  renderSimulationResults(endIndex);

  // Nächste Seite vorbereiten
  simulationCurrentPage++;

  // Prüfen, ob es weitere Seiten gibt
  if (simulationCurrentPage * 10 < currentSimulationResults.pulls.length) {
    nextBtn.classList.remove("hidden"); // "Weiter"-Button anzeigen
  } else {
    againBtn.classList.remove("hidden"); // "Erneut simulieren"-Button anzeigen
  }
}

/**
 * Führt die visuelle Animation zum Enthüllen der gezogenen Items aus.
 * @param {Array} pulls - Das Array der gezogenen Item-Objekte für die aktuelle Seite.
 */
async function runPullRevealAnimation(pulls) {
  const grid = document.getElementById("simulator-reveal-grid");
  grid.innerHTML = "";

  const cardElements = pulls.map((item) => {
    const cardEl = document.createElement("div");
    cardEl.className = `sim-pull-card rarity-${item.rarity}`;
    const imageUrl = getFandomImageUrl(item.type, item.name);
    cardEl.innerHTML = `
      <div class="card-face card-back"></div>
      <div class="card-face card-front">
        <img src="${imageUrl}" class="item-icon" alt="${item.name}" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
        <span class="item-name-fallback">${item.name}</span>
      </div>
    `;
    return cardEl;
  });

  cardElements.forEach((el) => grid.appendChild(el));

  // 1. Kurze Pause, damit die Karten im DOM sind, bevor die Animation beginnt
  await new Promise((resolve) => setTimeout(resolve, 50));

  // 2. Karten-Deal (Reveal) Animation
  cardElements.forEach((cardEl, index) => {
    setTimeout(() => {
      cardEl.classList.add("revealed");
    }, index * 100);
  });

  // 3. Wartezeit, bis alle Karten ausgeteilt wurden + eine längere Pause
  await new Promise((resolve) => setTimeout(resolve, pulls.length * 100 + 700));

  // 4. Flip-Animation
  for (const cardEl of cardElements) {
    const index = cardElements.indexOf(cardEl);
    const pull = pulls[index];

    // 5. Stagger zwischen den einzelnen Flips (langsamer)
    await new Promise((resolve) => setTimeout(resolve, 250));

    // 6. Glow-Effekt (falls 4* oder 5*)
    if (pull.rarity === 5) {
      cardEl.classList.add("glow-gold");
      playSound("fiveStar");
    } else if (pull.rarity === 4) {
      cardEl.classList.add("glow-purple");
      playSound("fourStar");
    }

    // 7. Wartezeit, WÄHREND der Glow-Effekt sichtbar ist (etwas länger)
    await new Promise((resolve) => setTimeout(resolve, 300));

    // 8. Führe den Flip durch
    cardEl.classList.add("is-flipping");
  }
}

/**
 * Rendert die Zusammenfassung und den finalen Status nach der Simulation.
 * @param {number} pullsSoFar - Die Gesamtzahl der bisher enthüllten Pulls.
 */
function renderSimulationResults(pullsSoFar) {
  const { pulls, initialState } = currentSimulationResults;
  const finalStateContainer = document.getElementById("simulator-final-state");
  const bannerId = document.getElementById("simulator-banner-select").value;

  const revealedPulls = pulls.slice(0, pullsSoFar);

  // 1. Berechne den aktuellen Zustand basierend auf den bisher gezeigten Zügen
  const currentState = calculateStateAfterPulls(
    initialState,
    revealedPulls,
    bannerId
  );

  // 2. Erstelle die Zusammenfassung
  const summary = revealedPulls.reduce((acc, item) => {
    acc[item.rarity] = (acc[item.rarity] || 0) + 1;
    return acc;
  }, {});

  const summaryText = `Obtained (${pullsSoFar}/${pulls.length}): 
    ${summary[5] || 0} x 5★, 
    ${summary[4] || 0} x 4★, 
    ${summary[3] || 0} x 3★`;

  // 3. Erstelle den Status-Text basierend auf dem aktuellen Zustand
  let guaranteeText = "";
  if (currentState.isGuaranteed) {
    guaranteeText = `<span class="text-green-400">Guaranteed</span>`;
  } else {
    if (bannerId === "weapon") {
      guaranteeText = `<span class="text-yellow-400">75/25</span>`;
    } else if (bannerId !== "standard") {
      guaranteeText = `<span class="text-yellow-400">50/50</span>`;
    }
  }

  const statusLabel = pullsSoFar >= pulls.length ? "Final" : "Current";
  const statusText = `<p>${statusLabel} Pity: <strong>${currentState.pity} (${guaranteeText})</strong> | 4★ Pity: <strong>${currentState.pity4star}</strong></p>`;

  // 4. Rendere alles
  finalStateContainer.innerHTML = `
    <p class="font-semibold mb-2">${summaryText}</p>
    ${statusText}
  `;
}

async function initializeApp() {
  // 1. Lade zuerst die Schriften
  await loadFonts();

  // 2. Warte, bis die Datenbank vollständig geladen ist
  await loadItemsDatabase();

  // 3. LADE DEN STATE, BEVOR DU IRGENDETWAS ANDERES TUST.
  loadState();
  runCStateMigration();

  //4. Weise jetzt die globalen Google-Funktionen zu und lade die Skripte.
  //Die Skripte werden im Hintergrund geladen und rufen dann gapiLoaded/gisLoaded auf.
  //window.gapiLoaded = gapiLoaded;
  //window.gisLoaded = gisLoaded;

  // 5. Fahre mit dem Rest der Initialisierung fort.
  loadAudioAssets(); // Starte das Laden der Sounds im Hintergrund
  handleFileLaunch();
  runDataMigration();
  runTotalCountNormalization(); // <-- NEW MIGRATION CALL
  applyBackgroundStyle(state.backgroundStyle);
  checkForNewVersion();
  renderChangelogModal();
  renderUpcomingBannerModal();
  startBannerTimer("banner-timer-container");
  startBannerTimer("character2-banner-timer-container");
  startBannerTimer("weapon-banner-timer-container");
  initEventListeners();
  initInteractiveGuide();
  initNavigation(); // <-- Stellt sicher, dass die Listener aktiv sind
  handleRouting(); // <-- Führe das Routing beim ersten Laden aus
  initSettingsTabs();
  initAnimatedBackground();
  renderCommunityHub();
  initPWA();
  showFirstTimeModal();
  loadGoogleScripts();
  checkAchievements();
  saveState();
}

function initPWA() {
  if ("serviceWorker" in navigator) {
    let newWorker;

    // Funktion zum Anzeigen des Update-Popups
    function showUpdateBar() {
      // Wir nutzen deine bestehende Toast-Funktion für ein konsistentes UI!
      // Erstelle einen Toast mit einem Button drin.
      const toast = document.createElement("div");
      toast.className = `toast-notification success`; // 'success' für gute Sichtbarkeit
      toast.style.cursor = "pointer";
      toast.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
          <span>A new version is available!</span>
          <button id="pwa-update-button" style="margin-left: 1em; padding: 0.3em 0.8em; border: 1px solid; border-radius: 5px; background: transparent; color: inherit; cursor: pointer;">
            REFRESH
          </button>
        </div>
      `;
      document.body.appendChild(toast);

      // Zeige den Toast an
      setTimeout(() => {
        toast.classList.add("show");
      }, 100);

      // Event Listener für den Button
      document
        .getElementById("pwa-update-button")
        .addEventListener("click", (e) => {
          e.stopPropagation(); // Verhindert, dass der Klick den ganzen Toast schließt
          if (newWorker) {
            newWorker.postMessage({ action: "skipWaiting" });
          }
        });

      // Klick auf den Toast selbst schließt ihn (falls der Nutzer das Update nicht will)
      toast.addEventListener("click", () => {
        toast.classList.remove("show");
        toast.addEventListener("transitionend", () => toast.remove());
      });
    }

    // Registriere den Service Worker
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Lausche auf das 'updatefound' Event
        reg.addEventListener("updatefound", () => {
          newWorker = reg.installing;
          newWorker.addEventListener("statechange", () => {
            // Wenn der neue Worker installiert ist und ein alter Worker noch die Seite kontrolliert...
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // ...zeige das Update-Popup an.
              showUpdateBar();
            }
          });
        });
      })
      .catch((err) =>
        console.error("Service Worker registration failed:", err)
      );

    // Wenn der Controller wechselt (nach skipWaiting), lade die Seite neu.
    let refreshing;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (refreshing) return;
      window.location.reload();
      refreshing = true;
    });
  }
}



// --- 110. Move Pull Feature ---
/**
 * Verschiebt den letzten Pull (oder Batch) von einem Banner zum anderen.
 * Relevant für Character Banner 1 <-> Character Banner 2.
 */
function moveLastBatch(sourceBannerId, targetBannerId) {
  try {
    const sourceState = state[sourceBannerId];
    const targetState = state[targetBannerId];

    if (!sourceState.history || sourceState.history.length === 0) {
      showToast("No history to move.", "error");
      return;
    }

    // Identifiziere den letzten "Batch".
    const lastItem = sourceState.history[0];
    const lastDate = lastItem.date;

    // Finde alle Items, die zu diesem Batch gehören (gleicher Timestamp)
    let batchSize = 0;
    for (let i = 0; i < sourceState.history.length; i++) {
      if (sourceState.history[i].date === lastDate) {
        batchSize++;
      } else {
        break;
      }
    }

    const itemsToMove = sourceState.history.slice(0, batchSize);

    // 1. Entferne aus Source
    sourceState.history.splice(0, batchSize);
    sourceState.totalPulls -= batchSize;

    // 2. Füge in Target ein
    // UPDATED: Wir müssen die 'banner'-Eigenschaft der verschobenen Items aktualisieren,
    // damit sie im Log als zum neuen Banner gehörend angezeigt werden.

    // Ermittle den korrekten, lokalisierten Bannernamen für das ZIEL-Banner
    const lang = state.language;
    let newBannerName = targetBannerId; // Fallback auf ID

    if (targetBannerId === "character1" || targetBannerId === "character2") {
      // Zugriff auf die globalen Banner-Daten
      if (typeof currentBannerData !== 'undefined' && currentBannerData[targetBannerId]) {
        const bannerData = currentBannerData[targetBannerId];
        if (bannerData) {
          const key = bannerData.bannerNameKey;
          if (key && translations[lang] && translations[lang][key]) {
            newBannerName = translations[lang][key];
          } else if (bannerData.fiveStar) {
            newBannerName = `Banner ${bannerData.fiveStar}`;
          }
        }
      }
    }

    itemsToMove.forEach(item => {
      if (item.banner) {
        item.banner = newBannerName;
      }
    });

    // Da itemsToMove bereits die "neuesten" sind (Index 0 = allerneuestes),
    // fügen wir sie einfach vorne an targetState an.
    targetState.history.unshift(...itemsToMove);
    targetState.totalPulls += batchSize;

    // Check Types
    const isSourceChar = sourceBannerId.startsWith("character");
    const isTargetChar = targetBannerId.startsWith("character");

    if (isSourceChar !== isTargetChar) {
      showToast("Can only move between Character Banners.", "error");
      return;
    }

    saveAndRefresh();
    const sourceName = sourceBannerId === 'character1' ? 'Char 1' : 'Char 2';
    const targetName = targetBannerId === 'character1' ? 'Char 1' : 'Char 2';
    showToast(`Moved ${batchSize} pull(s) from ${sourceName} to ${targetName}.`, "success");
  } catch (err) {
    console.error("Error in moveLastBatch:", err);
    showToast(`Move failed: ${err.message}`, "error");
  }
}

window.moveLastBatch = moveLastBatch;

// Starte den gesamten Initialisierungsprozess, sobald die Seite geladen ist.
window.onload = initializeApp;
/**
 * Normalizes character data to ensure Total Counts are tracked and
 * Pending Constellations do not exceed the functional cap of C6.
 * Run once on startup.
 */
function runTotalCountNormalization() {
  if (!state.inventory || !state.inventory.characters) return;

  let changesMade = false;

  for (const charName in state.inventory.characters) {
    const char = state.inventory.characters[charName];

    // 1. Initialize Total Count if missing
    // Logic: If totalCount is undefined, we assume the user has exactly what we see:
    // Base Copy (1) + Active Constellations (C) + Pending (P)
    if (typeof char.totalCount === "undefined") {
      char.totalCount = (char.constellation || 0) + (char.pendingConstellations || 0) + 1;
      changesMade = true;
    }

    // 2. Fix Overcapped Pending Constellations
    // A character is effectively C6 if constellation + pending >= 6.
    // If they have MORE than 6 total copies (excluding base), the excess should be in totalCount but NOT in pending.

    // Max allowable constellations (active + pending) is 6.
    const currentActive = char.constellation || 0;
    const currentPending = char.pendingConstellations || 0;

    const totalConstellations = currentActive + currentPending;

    if (totalConstellations > 6) {
      // Example: C6 (active) + 11 (pending) = 17. Desired: Active 6, Pending 0.
      // Example: C4 (active) + 5 (pending) = 9. Desired: Active 4, Pending 2 (reach 6).

      const spaceToC6 = 6 - currentActive;
      const newPending = Math.max(0, spaceToC6);

      if (newPending !== currentPending) {
        console.log(`Normalizing ${charName}: Pending ${currentPending} -> ${newPending}. (Total Count preserved at ${char.totalCount})`);
        char.pendingConstellations = newPending;
        changesMade = true;
      }
    }
  }

  if (changesMade) {
    saveState();
    console.log("Legacy data normalization completed.");
  }
}
